/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  SHIFTIQ_API,
  getAuthHeaders,
  getAuthHeadersMultipart,
  isAbortError,
} from "../../api/client";
import { BlockingActionOverlay, Icon, LoadingPulse } from "../../ui";
import { requestProcureOrdersBadgeRefresh } from "../../hooks/useProcureOrdersBadge";
import {
  procureiqApiError,
  procureiqColorFromName,
  procureiqDisplayProductName,
  procureiqDisplaySupplierName,
  procureiqSupplierInitial,
  formatProcureEuro,
} from "./helpers";
import { InvoicePerspectiveCrop } from "./InvoicePerspectiveCrop";
import { pickInvoiceUploadFile } from "./invoiceFilePick";
import { isInvoiceImageFile, isInvoicePdfFile } from "./invoicePerspective";
import { supyOrderUrl } from "./orderIssueUx";
import {
  opsProcureOrderHref,
  replaceBrowserPath,
  searchForProcureOrders,
} from "../../routing/paths";

function isoDateLocal(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function daysAgoLocal(n) {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() - n);
  return isoDateLocal(d);
}

/** Align with API MATCH_WINDOW_FLOOR — scan-match ignores older orders. */
const MATCH_WINDOW_FLOOR = "2026-10-15";

function matchWindowFromDate() {
  const rolling = daysAgoLocal(45);
  return rolling < MATCH_WINDOW_FLOOR ? MATCH_WINDOW_FLOOR : rolling;
}

const DATE_PRESETS = [
  { id: "7", days: 7, en: "7d", gr: "7η" },
  { id: "30", days: 30, en: "30d", gr: "30η" },
  { id: "45", days: 45, en: "45d", gr: "45η" },
  { id: "90", days: 90, en: "90d", gr: "90η" },
];

/** Browser console timings for invoice upload / OCR / order match. Filter: procureiq-invoice */
const INVOICE_DEBUG = "[procureiq-invoice]";

function invoiceDebugStart(step, detail = {}) {
  const t0 =
    typeof performance !== "undefined" && performance.now
      ? performance.now()
      : Date.now();
  let done = false;
  return {
    end(extra = {}) {
      if (done) return 0;
      done = true;
      const t1 =
        typeof performance !== "undefined" && performance.now
          ? performance.now()
          : Date.now();
      const ms = Math.round(t1 - t0);
      // eslint-disable-next-line no-console
      console.info(INVOICE_DEBUG, step, `${ms}ms`, { ...detail, ...extra });
      return ms;
    },
  };
}

function reviewBucketLabel(bucket, t) {
  switch (bucket) {
    case "open_due":
      return t("Open due", "Εκκρεμής");
    case "open":
      return t("Open", "Ανοιχτή");
    case "received_late":
      return t("Received late", "Καθυστερημένη");
    case "received":
      return t("Received", "Παραλήφθηκε");
    case "received_verified":
      return t("Received · verified", "Παραλήφθηκε · επιβεβ.");
    case "verified":
      return t("Verified", "Επιβεβαιωμένη");
    case "dismissed":
      return t("Not yet", "Όχι ακόμα");
    default:
      return bucket || "—";
  }
}

function canVerifyReceipt(detail) {
  const bucket = String(detail?.review_bucket || "");
  if (["verified", "received", "received_verified", "dismissed"].includes(bucket)) {
    return false;
  }
  const status = String(detail?.status || "").toLowerCase();
  if (["received", "shipped", "closed", "complete", "completed"].includes(status) && bucket !== "received_late") {
    return false;
  }
  return true;
}

/** Align with API order_is_already_received — Supy received / late / verified-received. */
function matchLooksAlreadyReceived(match) {
  if (match?.flags?.already_received) return true;
  const bucket = String(match?.review_bucket || "").toLowerCase();
  if (["received", "received_late", "received_verified"].includes(bucket)) return true;
  const status = String(match?.status || "")
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, "-");
  return [
    "shipped",
    "received",
    "partial-received",
    "partially-received",
    "closed",
    "complete",
    "completed",
  ].includes(status);
}

function orderLooksAlreadyReceived(detail) {
  if (!detail) return false;
  return matchLooksAlreadyReceived(detail);
}

function alreadyReceivedBannerCopy(t, { orderLabel } = {}) {
  const label = String(orderLabel || "").trim();
  if (label) {
    return t(
      `This looks like an already received Supy order (${label}). Scan / verify is usually not needed.`,
      `Φαίνεται ήδη παραληφθείσα παραγγελία στη Supy (${label}). Συνήθως δεν χρειάζεται σάρωση / επιβεβαίωση.`
    );
  }
  return t(
    "This looks like an already received Supy order. Scan / verify is usually not needed.",
    "Φαίνεται ήδη παραληφθείσα παραγγελία στη Supy. Συνήθως δεν χρειάζεται σάρωση / επιβεβαίωση."
  );
}

/** Order number → bare Ops order page (new tab). Supy via Show on Supy only. */
function OpsOrderLink({
  orderKey,
  orderId,
  orderNumber,
  locationId,
  brandId,
  style,
}) {
  const code = String(orderNumber || "").trim();
  const id = String(orderId || "").trim();
  const label = code || (id ? id.slice(0, 8) : "");
  const href = opsProcureOrderHref({
    locationId,
    brandId,
    orderKey: orderKey || null,
  });
  if (!label) return null;
  if (!href) {
    return (
      <span style={style} title="Order">
        {label}
      </span>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      style={{
        color: "var(--green)",
        fontWeight: 700,
        textDecoration: "underline",
        textUnderlineOffset: 2,
        ...style,
      }}
      title="Open order"
    >
      {label}
    </a>
  );
}

function findOrderInReview(data, orderKey) {
  const key = String(orderKey || "").trim();
  if (!data || !key) return null;
  for (const rows of [data.orders, data.open_due, data.received_late]) {
    if (!Array.isArray(rows)) continue;
    const hit = rows.find((o) => String(o.order_key) === key);
    if (hit) return hit;
  }
  return null;
}

function defaultMiddayLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 16);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function numOrNull(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Keep draft lines in invoice page order (extract_index), not Supy order order. */
function draftLinesInInvoiceOrder(lines) {
  if (!Array.isArray(lines) || !lines.length) return [];
  return lines
    .map((l, i) => ({
      ...l,
      extract_index:
        l.extract_index != null && Number.isFinite(Number(l.extract_index))
          ? Number(l.extract_index)
          : i,
    }))
    .sort((a, b) => a.extract_index - b.extract_index);
}

function linesFromOrder(order) {
  const fallbackTax = order?.tax_rate_pct != null ? Number(order.tax_rate_pct) : null;
  const ops = order?.ops_lines;
  if (Array.isArray(ops) && ops.length) {
    return ops.map((l) => {
      const orderedQty = numOrNull(l.ordered_qty);
      const orderedPrice = numOrNull(l.ordered_unit_price);
      return {
        item_id: l.item_id || "",
        product_id: l.product_id ?? null,
        product_name: procureiqDisplayProductName(l.product_name),
        qty: Number(l.qty) || 0,
        unit_price: l.unit_price != null ? Number(l.unit_price) : "",
        ordered_qty: orderedQty,
        ordered_unit_price: orderedPrice,
        tax_rate_pct:
          l.tax_rate_pct != null && Number.isFinite(Number(l.tax_rate_pct))
            ? Number(l.tax_rate_pct)
            : fallbackTax,
      };
    });
  }
  return (order?.lines || []).map((l) => {
    const orderedQty = numOrNull(l.ordered_qty ?? l.qty);
    const orderedPrice = numOrNull(l.ordered_unit_price ?? l.unit_price);
    return {
      item_id: l.item_id || "",
      product_id: l.product_id ?? null,
      product_name: procureiqDisplayProductName(l.product_name),
      qty: Number(l.qty ?? l.ordered_qty) || 0,
      unit_price:
        l.unit_price != null
          ? Number(l.unit_price)
          : l.ordered_unit_price != null
            ? Number(l.ordered_unit_price)
            : "",
      ordered_qty: orderedQty,
      ordered_unit_price: orderedPrice,
      tax_rate_pct:
        l.tax_rate_pct != null && Number.isFinite(Number(l.tax_rate_pct))
          ? Number(l.tax_rate_pct)
          : fallbackTax,
    };
  });
}

function formatOrderedHint(line, t) {
  const oq = numOrNull(line.ordered_qty);
  const op = numOrNull(line.ordered_unit_price);
  if (oq == null && op == null) return null;
  const qtyPart = oq != null ? String(oq) : "—";
  const pricePart = op != null ? formatProcureEuro(op) : "—";
  return `${t("Ordered", "Παραγγελία")} ${qtyPart} · ${pricePart}`;
}

function valuesDifferFromOrdered(line) {
  const oq = numOrNull(line.ordered_qty);
  const op = numOrNull(line.ordered_unit_price);
  const qty = numOrNull(line.qty);
  const price = numOrNull(line.unit_price);
  const qtyDiff = oq != null && qty != null && Math.abs(qty - oq) >= 0.005;
  const priceDiff = op != null && price != null && Math.abs(price - op) >= 0.005;
  return qtyDiff || priceDiff;
}

/** Detect weak invoice↔order match before overwriting qty/price. */
function assessInvoiceDraftMatch(draft, orderLineCount) {
  const lines = Array.isArray(draft?.draft_lines) ? draft.draft_lines : [];
  const matchedLines = lines.filter((l) => l.matched || l.item_id);
  const matchedN =
    draft?.matched_count != null ? Number(draft.matched_count) : matchedLines.length;
  const extractN = lines.length;
  const orderN = Number(orderLineCount) || 0;
  const supplier = draft?.supplier_check || null;
  const avgScore = matchedLines.length
    ? matchedLines.reduce((s, l) => s + (Number(l.match_score) || 0), 0) /
      matchedLines.length
    : 0;
  const reasons = [];
  if (supplier && supplier.ok === false) reasons.push("supplier");
  if (extractN === 0) reasons.push("empty");
  else if (matchedN === 0) reasons.push("no_match");
  else {
    if (orderN > 0 && matchedN / orderN < 0.5) reasons.push("few_vs_order");
    if (matchedN / extractN < 0.5) reasons.push("few_vs_extract");
    if (avgScore > 0 && avgScore < 0.5) reasons.push("weak_names");
  }
  return {
    weak: reasons.length > 0,
    reasons,
    matchedN,
    extractN,
    orderN,
    avgScore,
    supplier,
  };
}

function draftMatchWarningLines(quality, t) {
  const lines = [];
  if (quality.reasons.includes("supplier")) {
    lines.push(
      t(
        `Supplier may not match (invoice: ${quality.supplier?.extracted_supplier_name || "—"} · order: ${quality.supplier?.expected_supplier_name || "—"})`,
        `Ο προμηθευτής μπορεί να μην ταιριάζει (τιμολόγιο: ${quality.supplier?.extracted_supplier_name || "—"} · παραγγελία: ${quality.supplier?.expected_supplier_name || "—"})`
      )
    );
  }
  if (quality.reasons.includes("no_match") || quality.reasons.includes("empty")) {
    lines.push(
      t(
        "No invoice lines matched this order’s products.",
        "Καμία γραμμή τιμολογίου δεν ταίριαξε με τα προϊόντα της παραγγελίας."
      )
    );
  }
  if (quality.reasons.includes("few_vs_order") || quality.reasons.includes("few_vs_extract")) {
    lines.push(
      t(
        `Only ${quality.matchedN} of ${quality.extractN} invoice line(s) matched (order has ${quality.orderN}).`,
        `Μόνο ${quality.matchedN} από ${quality.extractN} γραμμές τιμολογίου ταίριαξαν (η παραγγελία έχει ${quality.orderN}).`
      )
    );
  }
  if (quality.reasons.includes("weak_names")) {
    lines.push(
      t(
        "Product names look only loosely related — quantities may be wrong.",
        "Τα ονόματα προϊόντων μοιάζουν λίγο — οι ποσότητες μπορεί να είναι λάθος."
      )
    );
  }
  return lines;
}

/** Half-up to cents (avoid float 32.304999 → 32.30). */
function roundMoney(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return NaN;
  const scaled = x * 100;
  const rounded = scaled >= 0 ? Math.floor(scaled + 0.5) : Math.ceil(scaled - 0.5);
  return rounded / 100;
}

function moneyToCents(n) {
  return Math.round(roundMoney(n) * 100);
}

/**
 * Received footer: Net = sum of line nets; VAT = round(Net × rate%) when a
 * single rate applies (matches “Gross uses 13% VAT” copy). Mixed per-line
 * rates still sum per-line VAT so 13% + 24% lines stay correct.
 */
function computeAcceptedTotals(lines, detail, preferredVatRatePct = null) {
  const fallbackPct =
    preferredVatRatePct != null && Number.isFinite(Number(preferredVatRatePct))
      ? Number(preferredVatRatePct)
      : detail?.tax_rate_pct != null && Number.isFinite(Number(detail.tax_rate_pct))
        ? Number(detail.tax_rate_pct)
        : null;
  let subtotalCents = 0;
  let perLineTaxCents = 0;
  let any = false;
  const ratesUsed = new Set();
  for (const line of lines || []) {
    const qty = Number(line.qty);
    const price = Number(line.unit_price);
    if (!Number.isFinite(qty) || !Number.isFinite(price)) continue;
    any = true;
    const netCents = moneyToCents(qty * price);
    subtotalCents += netCents;
    const pct =
      line.tax_rate_pct != null && Number.isFinite(Number(line.tax_rate_pct))
        ? Number(line.tax_rate_pct)
        : fallbackPct;
    if (pct != null) {
      ratesUsed.add(Number(pct));
      perLineTaxCents += Math.round((netCents * pct) / 100);
    }
  }
  if (!any) return null;
  const subtotal = subtotalCents / 100;
  if (!ratesUsed.size && fallbackPct == null) {
    return { subtotal, tax: null, total: subtotal };
  }
  let taxCents;
  if (ratesUsed.size <= 1) {
    const pct = ratesUsed.size === 1 ? [...ratesUsed][0] : fallbackPct;
    // 29798¢ × 13 / 100 = 3873.74 → 3874¢ = €38.74 (not sum-of-line ¢)
    taxCents = Math.round((subtotalCents * pct) / 100);
  } else {
    taxCents = perLineTaxCents;
  }
  const tax = taxCents / 100;
  // Always Net + VAT = Gross in cents (no separate rounding path).
  const total = (subtotalCents + taxCents) / 100;
  return { subtotal, tax, total };
}

function formatVatRateLabel(rate, t) {
  if (rate == null || !Number.isFinite(Number(rate))) {
    return t("VAT", "ΦΠΑ");
  }
  const n = Number(rate);
  const label = Number.isInteger(n) ? String(n) : n.toFixed(1);
  return t(`VAT ${label}%`, `ΦΠΑ ${label}%`);
}

function moneyClose(a, b, tol = 0.05) {
  if (a == null || b == null) return null;
  const x = Number(a);
  const y = Number(b);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return Math.abs(x - y) <= Math.max(tol, Math.abs(y) * 0.01);
}

function vatInfoFromDraft(draft) {
  if (!draft || typeof draft !== "object") return null;
  const rate =
    draft.vat_rate_pct != null && Number.isFinite(Number(draft.vat_rate_pct))
      ? Number(draft.vat_rate_pct)
      : null;
  const net =
    draft.total_net != null && Number.isFinite(Number(draft.total_net))
      ? Number(draft.total_net)
      : null;
  const vat =
    draft.total_vat != null && Number.isFinite(Number(draft.total_vat))
      ? Number(draft.total_vat)
      : null;
  const gross =
    draft.total_gross != null && Number.isFinite(Number(draft.total_gross))
      ? Number(draft.total_gross)
      : null;
  if (rate == null && net == null && vat == null && gross == null) return null;
  return {
    vat_rate_pct: rate,
    total_net: net,
    total_vat: vat,
    total_gross: gross,
    prices_include_vat: Boolean(draft.prices_include_vat),
    converted_from_gross: Boolean(draft.converted_from_gross),
  };
}

/**
 * Net / VAT / Gross strip. Supy + received lines are always net-based;
 * gross = net + VAT so users can match the invoice payable total.
 */
function TotalsStrip({
  title,
  subtitle,
  subtotal,
  tax,
  total,
  taxRatePct,
  t,
  accent,
  matchHint,
}) {
  // Invariant: when VAT is shown, Gross is always Net + VAT (not a separate total).
  const gross =
    subtotal != null && tax != null
      ? roundMoney(Number(subtotal) + Number(tax))
      : total != null
        ? total
        : subtotal;
  return (
    <div style={{ marginBottom: 14 }}>
      {title ? (
        <div
          style={{
            fontSize: 11,
            fontWeight: 800,
            color: "var(--text-muted)",
            textTransform: "uppercase",
            letterSpacing: 0.4,
            marginBottom: subtitle ? 2 : 6,
          }}
        >
          {title}
        </div>
      ) : null}
      {subtitle ? (
        <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 6 }}>
          {subtitle}
        </div>
      ) : null}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 8,
          padding: "10px 12px",
          background: accent ? "var(--green-light, #ecfdf5)" : "var(--surface2)",
          borderRadius: 12,
          border: accent ? "1px solid var(--green)" : "1px solid var(--border)",
        }}
      >
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase" }}>
            {t("Net", "Καθαρή αξία")}
          </div>
          <div style={{ fontWeight: 800, fontSize: 14, marginTop: 2 }}>
            {subtotal != null ? formatProcureEuro(subtotal) : "—"}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase" }}>
            {formatVatRateLabel(taxRatePct, t)}
          </div>
          <div style={{ fontWeight: 800, fontSize: 14, marginTop: 2 }}>
            {tax != null ? formatProcureEuro(tax) : "—"}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase" }}>
            {t("Gross", "Πληρωτέο")}
          </div>
          <div
            style={{
              fontWeight: 800,
              fontSize: 14,
              marginTop: 2,
              color: accent ? "var(--green)" : "var(--text-primary)",
            }}
          >
            {gross != null ? formatProcureEuro(gross) : "—"}
          </div>
        </div>
      </div>
      {matchHint ? (
        <div
          style={{
            marginTop: 6,
            fontSize: 11,
            fontWeight: 700,
            color: matchHint.ok ? "var(--green)" : "#B45309",
          }}
        >
          {matchHint.text}
        </div>
      ) : null}
    </div>
  );
}

function supplierGroupKey(order) {
  if (order?.supplier_id != null && order.supplier_id !== "") {
    return `id:${order.supplier_id}`;
  }
  const name = String(order?.supplier_name || "").trim().toLowerCase();
  return name ? `name:${name}` : "unknown";
}

function groupOrdersBySupplier(rows) {
  const map = new Map();
  for (const order of rows || []) {
    const key = supplierGroupKey(order);
    let group = map.get(key);
    if (!group) {
      group = {
        key,
        supplier_id: order.supplier_id ?? null,
        supplier_name: order.supplier_name || "Supplier",
        orders: [],
        total: 0,
      };
      map.set(key, group);
    }
    group.orders.push(order);
    const amt =
      order.ordered_total != null
        ? Number(order.ordered_total)
        : order.total_ordered != null
          ? Number(order.total_ordered)
          : NaN;
    if (!Number.isNaN(amt)) group.total += amt;
  }
  return Array.from(map.values()).sort((a, b) =>
    procureiqDisplaySupplierName(a.supplier_name).localeCompare(
      procureiqDisplaySupplierName(b.supplier_name),
      undefined,
      { sensitivity: "base" }
    )
  );
}

/** Group invoice-match candidates by supplier; best score first. */
function groupScanMatchesBySupplier(matches) {
  const map = new Map();
  for (const m of matches || []) {
    const key = supplierGroupKey(m);
    let group = map.get(key);
    if (!group) {
      group = {
        key,
        supplier_id: m.supplier_id ?? null,
        supplier_name: m.supplier_name || "Supplier",
        matches: [],
        bestScore: 0,
        bestSupplierScore: 0,
      };
      map.set(key, group);
    }
    group.matches.push(m);
    const sc = Number(m.score) || 0;
    if (sc > group.bestScore) group.bestScore = sc;
    const ss = Number(m.supplier_score) || 0;
    if (ss > group.bestSupplierScore) group.bestSupplierScore = ss;
  }
  for (const g of map.values()) {
    g.matches.sort(
      (a, b) => (Number(b.score) || 0) - (Number(a.score) || 0)
    );
  }
  return Array.from(map.values()).sort((a, b) => b.bestScore - a.bestScore);
}

export function ReceiptReviewPanel({
  locationId,
  locationName = "",
  brandId = 15,
  initialOrderKey = null,
  lang,
  t,
  showToast,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedKey, setSelectedKey] = useState(null);
  /** List-row summary shown in the dialog shell while detail loads. */
  const [selectedSummary, setSelectedSummary] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editLines, setEditLines] = useState([]);
  const [receivedAt, setReceivedAt] = useState("");
  const [busy, setBusy] = useState(false);
  const [extractBusy, setExtractBusy] = useState(false);
  /**
   * Progress while OCR / order-line matching runs.
   * { mode: "read"|"match-order", page: 1-based, total, fileName? }
   */
  const [extractProgress, setExtractProgress] = useState(null);
  const [invoiceFileName, setInvoiceFileName] = useState("");
  const [invoicePreviewUrl, setInvoicePreviewUrl] = useState(null);
  const [invoiceDragOver, setInvoiceDragOver] = useState(false);
  const [invoiceCropFile, setInvoiceCropFile] = useState(null);
  const [invoiceExtractNote, setInvoiceExtractNote] = useState(null);
  const [invoiceSupplierCheck, setInvoiceSupplierCheck] = useState(null);
  /** Multi-page invoice files: { key, filename, seq, folder, previewUrl, previewOwned }. */
  const [invoiceAttachments, setInvoiceAttachments] = useState([]);
  const [activeInvoicePageIdx, setActiveInvoicePageIdx] = useState(0);
  /** Full-screen preview of an attached invoice page (view-only after match). */
  const [invoiceLightbox, setInvoiceLightbox] = useState(null);
  const [pendingInvoiceDraft, setPendingInvoiceDraft] = useState(null);
  const [expandedSuppliers, setExpandedSuppliers] = useState({});
  const [activeTab, setActiveTab] = useState("upload");
  const [fromDate, setFromDate] = useState(() => daysAgoLocal(45));
  const [toDate, setToDate] = useState(() => isoDateLocal());
  const [datePreset, setDatePreset] = useState("45");
  const [scanBusy, setScanBusy] = useState(false);
  const [scanPhase, setScanPhase] = useState(null); // "upload" | "extract" | "rank" | null
  /**
   * Blocking overlay progress while scan/extract runs.
   * { kind: "scan"|"apply", step: string, page?: number, pages?: number, index: number, total: number }
   */
  const [invoiceJobProgress, setInvoiceJobProgress] = useState(null);
  const [scanDragOver, setScanDragOver] = useState(false);
  /** Lightweight Upload-tab inbox (store files now, match later). */
  const [inboxItems, setInboxItems] = useState([]);
  const [inboxLoading, setInboxLoading] = useState(false);
  const [inboxBusy, setInboxBusy] = useState(false);
  const [inboxDragOver, setInboxDragOver] = useState(false);
  const [inboxActionId, setInboxActionId] = useState(null);
  /** Inbox item open in preview dialog (review before Match). */
  const [inboxPreviewItem, setInboxPreviewItem] = useState(null);
  const [inboxPreviewPageIdx, setInboxPreviewPageIdx] = useState(0);
  const [inboxPreviewLoading, setInboxPreviewLoading] = useState(false);
  const [scanMatches, setScanMatches] = useState(null);
  /** When matches span >1 supplier: null = pick supplier first; else filter to that group key. */
  const [scanSupplierKey, setScanSupplierKey] = useState(null);
  const [scanExtract, setScanExtract] = useState(null);
  const [scanSupplierProfile, setScanSupplierProfile] = useState(null);
  const [scanInvoiceKey, setScanInvoiceKey] = useState(null);
  const [scanFileName, setScanFileName] = useState("");
  const [scanPreviewUrl, setScanPreviewUrl] = useState(null);
  /** Active page in the Open-tab scan / inbox-match preview strip. */
  const [activeScanPageIdx, setActiveScanPageIdx] = useState(0);
  /**
   * Pages built in the scan/crop session before an order is chosen.
   * { key, filename, seq, folder, previewUrl, previewOwned }
   */
  const [scanBuiltPages, setScanBuiltPages] = useState([]);
  /** VAT footer from last extract (printed invoice totals, net-basis after convert). */
  const [invoiceVatInfo, setInvoiceVatInfo] = useState(null);
  /**
   * Learned supplier memory (`/receipt-review/supplier-profile`): SKU → Supy item
   * pairs remembered from earlier verified invoices. Shown read-only in the dialog
   * so staff can see which codes are known and which will be learned on Verify.
   */
  const [supplierProfile, setSupplierProfile] = useState(null);
  const [supplierProfileOpen, setSupplierProfileOpen] = useState(false);
  /** Edit-line index whose Supy match is being changed by hand. */
  const [rematchIdx, setRematchIdx] = useState(null);
  const [rematchFilter, setRematchFilter] = useState("");
  /** Line left unmatched after a conflict steal — nudge user to rematch. */
  const [needsRematchIdx, setNeedsRematchIdx] = useState(null);
  const openDetailKeyRef = useRef(null);
  const cropPurposeRef = useRef("dialog"); // "dialog" | "scan" | "inbox"
  /** Straightened scan pages queued before match (Add Page on scan flow). */
  const scanPageQueueRef = useRef([]);
  const pendingScanApplyRef = useRef(null);
  const scanBuiltPagesRef = useRef([]);
  scanBuiltPagesRef.current = scanBuiltPages;
  /** Inbox row being matched via Open-tab scan flow (mark matched on verify). */
  const matchingInboxIdRef = useRef(null);
  /** When false, clearing scanPreviewUrl must not revoke (URL handed to dialog). */
  const scanPreviewOwnedRef = useRef(true);
  const invoicePreviewOwnedRef = useRef(true);
  const deepLinkOpenedRef = useRef(null);

  function syncOrderInUrl(orderKey) {
    if (locationId == null) return;
    replaceBrowserPath(
      "/procure-orders",
      searchForProcureOrders({
        locationId,
        brandId,
        orderKey: orderKey || null,
      })
    );
  }

  function applyPreset(presetId) {
    const preset = DATE_PRESETS.find((p) => p.id === presetId);
    if (!preset) {
      setDatePreset("custom");
      return;
    }
    setDatePreset(preset.id);
    setToDate(isoDateLocal());
    setFromDate(daysAgoLocal(preset.days));
  }

  function onFromDateChange(value) {
    setFromDate(value);
    setDatePreset("custom");
  }

  function onToDateChange(value) {
    setToDate(value);
    setDatePreset("custom");
  }

  function clearInvoicePreview() {
    setInvoicePreviewUrl((prev) => {
      if (prev && invoicePreviewOwnedRef.current) URL.revokeObjectURL(prev);
      return null;
    });
    invoicePreviewOwnedRef.current = true;
  }

  /** True when an inbox list thumb still points at this blob URL. */
  function inboxSharesPreviewUrl(url) {
    if (!url) return false;
    return (inboxItems || []).some((item) =>
      (item.pages || []).some((p) => p?.url === url || p?.previewUrl === url)
    );
  }

  function revokeAttachmentPreviews(pages) {
    for (const p of pages || []) {
      // Never revoke blobs the Upload-tab list still shows as thumbs.
      if (p?.previewUrl && p.previewOwned && !inboxSharesPreviewUrl(p.previewUrl)) {
        try {
          URL.revokeObjectURL(p.previewUrl);
        } catch {
          /* ignore */
        }
      }
    }
  }

  function clearInvoicePages() {
    setInvoiceAttachments((prev) => {
      revokeAttachmentPreviews(prev);
      return [];
    });
    setActiveInvoicePageIdx(0);
    setInvoiceFileName("");
    setInvoiceLightbox(null);
    clearInvoicePreview();
  }

  function clearScanPreview({ revoke = true } = {}) {
    setScanPreviewUrl((prev) => {
      if (prev && revoke && scanPreviewOwnedRef.current) URL.revokeObjectURL(prev);
      return null;
    });
    if (revoke) scanPreviewOwnedRef.current = true;
  }

  function createInvoicePreviewUrl(file) {
    if (file && isInvoiceImageFile(file) && !isInvoicePdfFile(file)) {
      return URL.createObjectURL(file);
    }
    return null;
  }

  function looksLikeInvoiceImageKey(key, filename, contentType) {
    const ct = String(contentType || "").toLowerCase();
    if (ct.startsWith("image/")) return true;
    if (ct.includes("pdf")) return false;
    const name = String(filename || key || "").toLowerCase();
    return /\.(jpe?g|png|webp|heic|heif)$/i.test(name);
  }

  function looksLikeInvoicePdfKey(key, filename, contentType) {
    const ct = String(contentType || "").toLowerCase();
    if (ct.includes("pdf")) return true;
    const name = String(filename || key || "").toLowerCase();
    return /\.pdf$/i.test(name);
  }

  /**
   * Open a blob/object URL in a new browser tab (native PDF viewer) so Ops
   * and the invoice can sit side by side. Pop-up blockers may require a gesture.
   */
  function openInvoiceUrlInNewTab(url, filename = "invoice.pdf") {
    if (!url || typeof window === "undefined") return false;
    const w = window.open(url, "_blank", "noopener,noreferrer");
    if (!w) {
      showToast?.(
        t(
          "Allow pop-ups to open the PDF in a new tab",
          "Επιτρέψτε τα αναδυόμενα για άνοιγμα PDF σε νέα καρτέλα"
        )
      );
      return false;
    }
    try {
      w.opener = null;
      if (filename) w.document.title = String(filename);
    } catch {
      /* blob title may be restricted */
    }
    return true;
  }

  /** Prefer an existing preview URL; otherwise fetch with auth and open. */
  async function openInvoicePageInNewTab(page) {
    if (!page) return;
    const name = page.filename || "invoice.pdf";
    if (page.previewUrl || page.url) {
      openInvoiceUrlInNewTab(page.previewUrl || page.url, name);
      return;
    }
    if (!page.key) {
      showToast?.(t("File not available", "Το αρχείο δεν είναι διαθέσιμο"));
      return;
    }
    const got = await fetchInvoiceFileBlob(page.key, {
      allowPdf: true,
      allowImage: true,
    });
    if (!got?.url) {
      showToast?.(t("Could not open file", "Αδυναμία ανοίγματος αρχείου"));
      return;
    }
    // Keep the blob alive for the new tab (do not revoke here).
    openInvoiceUrlInNewTab(got.url, name);
  }

  /**
   * Local invoice storage has no public URL; ``<img>`` / iframe cannot send Bearer.
   * Fetch bytes with auth and build a blob URL (image or PDF).
   */
  async function fetchInvoiceFileBlob(key, { allowPdf = true, allowImage = true } = {}) {
    const k = String(key || "").trim();
    if (!k) return null;
    try {
      const res = await fetch(
        `${SHIFTIQ_API}/api/procureiq/receipt-review/invoice-file?key=${encodeURIComponent(k)}`,
        { headers: getAuthHeaders() }
      );
      if (!res.ok) return null;
      const blob = await res.blob();
      const ct = String(blob.type || res.headers.get("content-type") || "").toLowerCase();
      const isPdf = ct.includes("pdf") || looksLikeInvoicePdfKey(k);
      const isImage =
        ct.startsWith("image/") ||
        (ct.includes("octet-stream") && looksLikeInvoiceImageKey(k));
      if (isPdf && !allowPdf) return null;
      if (isImage && !allowImage) return null;
      if (!isPdf && !isImage) return null;
      return {
        url: URL.createObjectURL(blob),
        contentType: ct || (isPdf ? "application/pdf" : "image/jpeg"),
        isPdf,
        isImage,
      };
    } catch {
      return null;
    }
  }

  async function fetchInvoicePreviewBlobUrl(key) {
    const got = await fetchInvoiceFileBlob(key, { allowPdf: false, allowImage: true });
    return got?.url || null;
  }

  async function hydrateInvoicePagePreviews(pages) {
    const list = Array.isArray(pages) ? pages : [];
    const next = [];
    for (const p of list) {
      if (!p) continue;
      if (p.previewUrl || p.url) {
        const url = p.previewUrl || p.url || null;
        const isPdf =
          p.previewKind === "pdf" ||
          looksLikeInvoicePdfKey(p.key, p.filename, p.content_type);
        next.push({
          ...p,
          previewUrl: url,
          previewKind: isPdf ? "pdf" : p.previewKind || "image",
          previewOwned: Boolean(p.previewOwned && p.previewUrl),
        });
        continue;
      }
      if (!p.key) {
        next.push(p);
        continue;
      }
      const wantPdf = looksLikeInvoicePdfKey(p.key, p.filename, p.content_type);
      const wantImage = looksLikeInvoiceImageKey(p.key, p.filename, p.content_type);
      if (!wantPdf && !wantImage) {
        next.push(p);
        continue;
      }
      const got = await fetchInvoiceFileBlob(p.key, {
        allowPdf: wantPdf,
        allowImage: wantImage || !wantPdf,
      });
      next.push(
        got
          ? {
              ...p,
              previewUrl: got.url,
              previewKind: got.isPdf ? "pdf" : "image",
              content_type: got.contentType || p.content_type,
              previewOwned: true,
            }
          : p
      );
    }
    return next;
  }

  /** Re-fetch blob thumbs for one inbox row (after a revoked object URL). */
  async function repairInboxItemPreviews(item) {
    if (!item?.id || !Array.isArray(item.pages) || !item.pages.length) return;
    try {
      const pages = await hydrateInvoicePagePreviews(
        item.pages.map((p) => ({
          ...p,
          url: null,
          previewUrl: null,
          previewOwned: false,
        }))
      );
      const shared = pages.map((p) => ({
        ...p,
        url: p.url || p.previewUrl || null,
        previewOwned: true,
      }));
      setInboxItems((prev) =>
        prev.map((row) => (row.id === item.id ? { ...row, pages: shared } : row))
      );
    } catch {
      /* thumb repair is best-effort */
    }
  }

  async function openInboxPreview(item, { forceRefresh = false } = {}) {
    if (!item?.id || inboxBusy || scanBusy) return;
    const existing = Array.isArray(item.pages) ? item.pages : [];
    const hasAnyPreview = existing.some((p) => p?.url || p?.previewUrl);
    // Re-fetch when forced (blob may have been revoked after order dialog) or missing.
    const needsHydrate =
      forceRefresh || existing.some((p) => !(p?.url || p?.previewUrl));
    setInboxPreviewPageIdx(0);
    // Open immediately when list already hydrated thumbs — no OCR, no spinner gate.
    if (hasAnyPreview && !forceRefresh) {
      setInboxPreviewItem({
        ...item,
        pages: existing.map((p) => ({
          ...p,
          url: p.url || p.previewUrl || null,
          previewOwned: false,
        })),
      });
    } else if (needsHydrate) {
      setInboxPreviewLoading(true);
      setInboxActionId(item.id);
    }
    if (!needsHydrate) return;
    try {
      // Drop stale blob URLs so hydrate fetches fresh authenticated bytes.
      const toHydrate = forceRefresh
        ? existing.map((p) => ({
            ...p,
            url: null,
            previewUrl: null,
            previewOwned: false,
          }))
        : existing;
      const pages = await hydrateInvoicePagePreviews(toHydrate);
      const shared = pages.map((p) => ({
        ...p,
        url: p.url || p.previewUrl || null,
        // Inbox list owns these blobs for thumbs until dismiss / reload.
        previewOwned: true,
      }));
      setInboxPreviewItem({
        ...item,
        pages: shared.map((p) => ({ ...p, previewOwned: false })),
      });
      setInboxItems((prev) =>
        prev.map((row) => (row.id === item.id ? { ...row, pages: shared } : row))
      );
    } catch (err) {
      showToast?.(err?.message || "Error");
      if (!hasAnyPreview) setInboxPreviewItem(null);
    } finally {
      setInboxPreviewLoading(false);
      setInboxActionId(null);
    }
  }

  function closeInboxPreview() {
    setInboxPreviewItem(null);
    setInboxPreviewPageIdx(0);
  }

  async function matchInboxItemFromPreview() {
    const item = inboxPreviewItem;
    if (!item) return;
    closeInboxPreview();
    await matchInboxItem(item);
  }

  function setInvoiceFromFile(file) {
    setInvoiceFileName(file?.name || "invoice");
    invoicePreviewOwnedRef.current = true;
    setInvoicePreviewUrl((prev) => {
      if (prev && invoicePreviewOwnedRef.current) URL.revokeObjectURL(prev);
      return createInvoicePreviewUrl(file);
    });
  }

  function syncActiveInvoicePreview(pages, idx) {
    const list = Array.isArray(pages) ? pages : [];
    const i = Math.max(0, Math.min(idx, Math.max(0, list.length - 1)));
    const page = list[i] || null;
    setActiveInvoicePageIdx(list.length ? i : 0);
    setInvoiceFileName(page?.filename || "");
    // Active preview is borrowed from the page entry — do not revoke here.
    invoicePreviewOwnedRef.current = false;
    setInvoicePreviewUrl(page?.previewUrl || null);
  }

  function setScanFromFile(file) {
    setScanFileName(file?.name || "invoice");
    scanPreviewOwnedRef.current = true;
    setScanPreviewUrl((prev) => {
      if (prev && scanPreviewOwnedRef.current) URL.revokeObjectURL(prev);
      if (file && isInvoiceImageFile(file) && !isInvoicePdfFile(file)) {
        return URL.createObjectURL(file);
      }
      return null;
    });
  }

  useEffect(() => {
    return () => {
      if (invoicePreviewUrl && invoicePreviewOwnedRef.current) {
        URL.revokeObjectURL(invoicePreviewUrl);
      }
    };
  }, [invoicePreviewUrl]);

  const invoiceAttachmentsRef = useRef(invoiceAttachments);
  invoiceAttachmentsRef.current = invoiceAttachments;
  useEffect(() => {
    return () => {
      revokeAttachmentPreviews(invoiceAttachmentsRef.current);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (scanPreviewUrl && scanPreviewOwnedRef.current) {
        URL.revokeObjectURL(scanPreviewUrl);
      }
    };
  }, [scanPreviewUrl]);

  const load = useCallback(async () => {
    if (!locationId) return;
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams({
        location_id: String(locationId),
        brand_id: String(brandId),
      });
      if (fromDate) qs.set("from_date", fromDate);
      if (toDate) qs.set("to_date", toDate);
      const res = await fetch(`${SHIFTIQ_API}/api/procureiq/receipt-review?${qs}`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(await procureiqApiError(res, "Receipt review failed"));
      const json = await res.json();
      setData(json);
    } catch (err) {
      if (isAbortError(err)) return;
      setError(err?.message || "Error");
    } finally {
      setLoading(false);
    }
  }, [locationId, brandId, fromDate, toDate]);

  useEffect(() => {
    load();
  }, [load]);

  const loadInbox = useCallback(async () => {
    if (!locationId) return;
    setInboxLoading(true);
    try {
      const qs = new URLSearchParams({
        location_id: String(locationId),
        brand_id: String(brandId),
        status: "pending",
      });
      const res = await fetch(
        `${SHIFTIQ_API}/api/procureiq/receipt-review/invoice-inbox?${qs}`,
        { headers: getAuthHeaders() }
      );
      if (!res.ok) throw new Error(await procureiqApiError(res, "Inbox failed"));
      const json = await res.json();
      const raw = Array.isArray(json?.items) ? json.items : [];
      // Hydrate local-disk image thumbs (no S3 presign in local API mode).
      const hydrated = [];
      for (const item of raw) {
        const pages = await hydrateInvoicePagePreviews(item.pages || []);
        hydrated.push({
          ...item,
          pages: pages.map((p) => ({
            ...p,
            url: p.url || p.previewUrl || null,
          })),
        });
      }
      setInboxItems(hydrated);
    } catch (err) {
      if (isAbortError(err)) return;
      showToast?.(err?.message || "Error");
    } finally {
      setInboxLoading(false);
    }
  }, [locationId, brandId, showToast]);

  useEffect(() => {
    loadInbox();
  }, [loadInbox]);

  useEffect(() => {
    deepLinkOpenedRef.current = null;
  }, [locationId]);

  // Deep link: /procure-orders?store=…&order=… opens the Ops order view (not Supy).
  useEffect(() => {
    const key = String(initialOrderKey || "").trim();
    if (!key || !locationId || loading || !data) return;
    if (deepLinkOpenedRef.current === key) return;
    if (openDetailKeyRef.current === key) {
      deepLinkOpenedRef.current = key;
      return;
    }
    deepLinkOpenedRef.current = key;
    setActiveTab("open");
    const found = findOrderInReview(data, key);
    openDetail(found || { order_key: key }, { widenDates: true, skipUrlSync: true });
  }, [initialOrderKey, locationId, loading, data]);

  async function loadSupplierProfile(order) {
    const supplierId =
      order?.supplier_id != null && order.supplier_id !== "" ? order.supplier_id : null;
    const supplierName = order?.supplier_name || "";
    if (supplierId == null && !supplierName) {
      setSupplierProfile(null);
      return;
    }
    const key = order?.order_key;
    try {
      const qs = new URLSearchParams();
      if (supplierId != null) qs.set("supplier_id", String(supplierId));
      if (supplierName) qs.set("supplier_name", supplierName);
      const res = await fetch(
        `${SHIFTIQ_API}/api/procureiq/receipt-review/supplier-profile?${qs}`,
        { headers: getAuthHeaders() }
      );
      if (!res.ok) return;
      const json = await res.json();
      if (key && openDetailKeyRef.current !== key) return;
      setSupplierProfile(json?.profile || null);
    } catch {
      /* memory panel is informational — ignore load errors */
    }
  }

  async function openDetail(order, options = {}) {
    const key = order.order_key;
    const scanApply = options.scanApply || null;
    const widenDates = Boolean(options.widenDates || scanApply);
    openDetailKeyRef.current = key;
    pendingScanApplyRef.current = scanApply;
    setSelectedKey(key);
    setSelectedSummary(order);
    if (!options.skipUrlSync) syncOrderInUrl(key);
    setDetail(null);
    setEditLines([]);
    setRematchIdx(null);
    setRematchFilter("");
    setNeedsRematchIdx(null);
    setSupplierProfile(scanApply?.supplierProfile || null);
    setSupplierProfileOpen(false);
    loadSupplierProfile({ ...order, order_key: key });
    setReceivedAt(
      defaultMiddayLocal(order.expected_received_at_default || order.expected_delivery_date)
    );
    clearInvoicePages();
    setInvoiceExtractNote(null);
    setInvoiceSupplierCheck(null);
    setPendingInvoiceDraft(null);
    setInvoiceVatInfo(scanApply?.vatInfo || null);
    setInvoiceDragOver(false);
    setInvoiceCropFile(null);
    setDetailLoading(true);
    try {
      const qs = new URLSearchParams({
        location_id: String(locationId),
        brand_id: String(brandId),
      });
      if (widenDates) {
        qs.set("from_date", matchWindowFromDate());
        qs.set("to_date", isoDateLocal());
      } else {
        if (fromDate) qs.set("from_date", fromDate);
        if (toDate) qs.set("to_date", toDate);
      }
      const res = await fetch(
        `${SHIFTIQ_API}/api/procureiq/receipt-review/${encodeURIComponent(key)}?${qs}`,
        { headers: getAuthHeaders() }
      );
      if (!res.ok) throw new Error(await procureiqApiError(res, "Load order failed"));
      const json = await res.json();
      if (openDetailKeyRef.current !== key) return null;
      setDetail(json);
      setEditLines(linesFromOrder(json));
      setReceivedAt(
        defaultMiddayLocal(
          json.expected_received_at_default || order.expected_received_at_default
        )
      );
      const pending = pendingScanApplyRef.current;
      if (pending && pending.orderKey === key) {
        pendingScanApplyRef.current = null;
        const pages = Array.isArray(pending.pages) && pending.pages.length
          ? pending.pages.map((p, i) => ({
              key: p.key,
              filename: p.filename || `page-${i + 1}`,
              seq: p.seq != null ? p.seq : i,
              folder: p.folder || "_scan",
              previewUrl: p.previewUrl || null,
              previewOwned: Boolean(p.previewOwned),
              pending: false,
            }))
          : pending.invoiceKey
            ? [
                {
                  key: pending.invoiceKey,
                  filename: pending.filename || "invoice",
                  seq: 0,
                  folder: "_scan",
                  previewUrl: pending.previewUrl || null,
                  previewOwned: Boolean(pending.previewUrl),
                },
              ]
            : [];
        if (pages.length) {
          setInvoiceAttachments(pages);
          syncActiveInvoicePreview(pages, 0);
        }
        // Draft/note applied by pickScanMatch after re-extracting all pages
        // against this order — do not stick the pre-match "pick an order" note.
        if (pending.draft?.draft_lines?.length) {
          const quality = assessInvoiceDraftMatch(
            pending.draft,
            (json.lines || []).length
          );
          if (quality.weak) {
            setPendingInvoiceDraft({
              draft: pending.draft,
              quality,
              invoiceKey: pending.invoiceKey || pages[0]?.key || null,
              filename: pending.filename || pages[0]?.filename || "invoice",
              append: false,
            });
          } else {
            const draft = pending.draft;
            const orderBaseline = new Map();
            for (const ol of json.lines || []) {
              const id = ol.item_id != null ? String(ol.item_id) : "";
              if (!id) continue;
              orderBaseline.set(id, {
                ordered_qty: numOrNull(ol.ordered_qty ?? ol.qty),
                ordered_unit_price: numOrNull(ol.ordered_unit_price ?? ol.unit_price),
                product_name: ol.product_name,
              });
            }
            setEditLines(
              editLinesFromDraft(draft.draft_lines, orderBaseline, json).map((l) => ({
                ...l,
                page_key: l.page_key || pending.invoiceKey || null,
              }))
            );
          }
        }
      }
      return json;
    } catch (err) {
      if (openDetailKeyRef.current !== key) return null;
      pendingScanApplyRef.current = null;
      showToast?.(err?.message || "Error");
      setSelectedKey(null);
      setSelectedSummary(null);
      setDetail(null);
      return null;
    } finally {
      if (openDetailKeyRef.current === key) setDetailLoading(false);
    }
  }

  function updateLine(idx, field, value) {
    setEditLines((prev) =>
      prev.map((row, i) => (i === idx ? { ...row, [field]: value } : row))
    );
  }

  function clearLineMatch(line) {
    const invoiceName =
      line.invoice_product_name ||
      (!line.matched ? line.product_name : null) ||
      line.product_name;
    return {
      ...line,
      item_id: "",
      product_id: null,
      product_name: procureiqDisplayProductName(invoiceName),
      ordered_product_name: null,
      invoice_product_name: invoiceName
        ? procureiqDisplayProductName(invoiceName)
        : line.invoice_product_name || null,
      matched: false,
      match_via: null,
      match_score: null,
      match_reasons: [],
      ordered_qty: null,
      ordered_unit_price: null,
      qty_differs_from_order: false,
      price_differs_from_order: false,
      used_order_values: false,
      uom_mismatch: false,
      uom_converted_to_order: false,
      item_code: null,
      sku_known: false,
      sku_conflict: false,
      sku_mapping: null,
    };
  }

  function bindLineToOrderItem(line, orderLine) {
    const orderedName = procureiqDisplayProductName(orderLine.product_name);
    const orderedQty = numOrNull(orderLine.ordered_qty ?? orderLine.qty);
    const orderedPrice = numOrNull(orderLine.ordered_unit_price ?? orderLine.unit_price);
    const qty = numOrNull(line.qty);
    const price = numOrNull(line.unit_price);
    const invoiceName =
      line.invoice_product_name ||
      (!line.matched && !line.item_id ? line.product_name : null);
    return {
      ...line,
      item_id: orderLine.item_id != null ? String(orderLine.item_id) : "",
      product_id: orderLine.product_id ?? null,
      product_name: orderedName,
      ordered_product_name: orderedName,
      invoice_product_name: invoiceName
        ? procureiqDisplayProductName(invoiceName)
        : line.invoice_product_name || null,
      matched: true,
      match_via: "manual",
      match_score: 1,
      match_reasons: ["manual"],
      ordered_qty: orderedQty,
      ordered_unit_price: orderedPrice,
      item_code: orderLine.item_code ? String(orderLine.item_code) : null,
      qty_differs_from_order:
        orderedQty != null && qty != null && Math.abs(qty - orderedQty) >= 0.005,
      price_differs_from_order:
        orderedPrice != null &&
        price != null &&
        Math.abs(price - orderedPrice) >= 0.005,
      used_order_values: false,
      uom_mismatch: false,
      uom_converted_to_order: false,
      sku_conflict: false,
    };
  }

  function openRematchPicker(idx) {
    setRematchIdx(idx);
    setRematchFilter("");
    if (needsRematchIdx === idx) setNeedsRematchIdx(null);
  }

  function closeRematchPicker() {
    setRematchIdx(null);
    setRematchFilter("");
  }

  function applyRematch(orderLine) {
    if (rematchIdx == null || !orderLine) return;
    const targetIdx = rematchIdx;
    const newId = orderLine.item_id != null ? String(orderLine.item_id) : "";
    if (!newId) return;

    const conflictIdx = editLines.findIndex(
      (l, i) => i !== targetIdx && l.item_id && String(l.item_id) === newId
    );
    if (conflictIdx >= 0) {
      const other = editLines[conflictIdx];
      const otherLabel = procureiqDisplayProductName(
        other.invoice_product_name || other.product_name || "—"
      );
      const ok = window.confirm(
        t(
          `“${procureiqDisplayProductName(orderLine.product_name)}” is already matched to invoice line “${otherLabel}”. Reassign here and leave that line unmatched so you can match it again?`,
          `Το «${procureiqDisplayProductName(orderLine.product_name)}» είναι ήδη συνδεδεμένο με τη γραμμή τιμολογίου «${otherLabel}». Να μεταφερθεί εδώ και η άλλη να μείνει αταίριαστη για νέο matching;`
        )
      );
      if (!ok) return;
      setEditLines((prev) =>
        prev.map((row, i) => {
          if (i === conflictIdx) return clearLineMatch(row);
          if (i === targetIdx) return bindLineToOrderItem(row, orderLine);
          return row;
        })
      );
      closeRematchPicker();
      setNeedsRematchIdx(conflictIdx);
      showToast?.(
        t(
          `Reassigned. Match the unmatched invoice line “${otherLabel}” next.`,
          `Μεταφέρθηκε. Ταιριάξτε τώρα την αταίριαστη γραμμή «${otherLabel}».`
        )
      );
      return;
    }

    setEditLines((prev) =>
      prev.map((row, i) =>
        i === targetIdx ? bindLineToOrderItem(row, orderLine) : row
      )
    );
    closeRematchPicker();
    if (needsRematchIdx === targetIdx) setNeedsRematchIdx(null);
  }

  function clearRematchAtPicker() {
    if (rematchIdx == null) return;
    const targetIdx = rematchIdx;
    setEditLines((prev) =>
      prev.map((row, i) => (i === targetIdx ? clearLineMatch(row) : row))
    );
    closeRematchPicker();
    setNeedsRematchIdx(targetIdx);
    showToast?.(
      t(
        "Unmatched — pick another order item for this invoice line",
        "Αταίριαστο — επιλέξτε άλλο είδος παραγγελίας για αυτή τη γραμμή"
      )
    );
  }

  async function handleVerify() {
    if (!detail?.order_key || busy) return;
    setBusy(true);
    try {
      const usedInvoice =
        invoiceAttachments.length > 0 ||
        Boolean(invoiceVatInfo) ||
        editLines.some((l) => l.invoice_product_name || l.ocr_qty != null);
      const lines = editLines.map((l) => ({
        item_id: l.item_id || null,
        product_id: l.product_id,
        product_name: l.product_name,
        ordered_product_name: l.ordered_product_name || l.product_name || null,
        invoice_product_name: l.invoice_product_name || null,
        // Supplier's own code from the invoice → learned as SKU→item memory on verify.
        supplier_sku: l.supplier_sku || null,
        item_code: l.item_code || null,
        match_via: l.match_via || null,
        qty: Number(l.qty) || 0,
        unit_price: l.unit_price === "" ? null : Number(l.unit_price),
        ordered_qty: l.ordered_qty != null ? Number(l.ordered_qty) : null,
        ordered_unit_price:
          l.ordered_unit_price != null ? Number(l.ordered_unit_price) : null,
        source: usedInvoice ? "invoice_extract" : "manual",
      }));
      const res = await fetch(
        `${SHIFTIQ_API}/api/procureiq/receipt-review/${encodeURIComponent(detail.order_key)}/assume`,
        {
          method: "POST",
          headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({
            unified_location_id: locationId,
            brand_id: brandId,
            received_at: receivedAt ? new Date(receivedAt).toISOString() : null,
            lines,
            line_source: usedInvoice ? "invoice_extract" : "manual",
            invoice_s3_key: invoiceAttachments[0]?.key || null,
            outlet_id: detail.outlet_id,
            submitted_at: detail.submitted_at,
            supplier_id: detail.supplier_id,
            supplier_name: detail.supplier_name,
            ordered_total_eur: detail.total_ordered,
            prices_include_vat: invoiceVatInfo?.converted_from_gross
              ? true
              : invoiceVatInfo?.prices_include_vat ?? null,
            vat_rate_pct:
              invoiceVatInfo?.vat_rate_pct != null
                ? Number(invoiceVatInfo.vat_rate_pct)
                : null,
            learn_invoice_profile: usedInvoice,
          }),
        }
      );
      if (!res.ok) throw new Error(await procureiqApiError(res, "Verify failed"));
      const inboxId = matchingInboxIdRef.current;
      if (inboxId != null && detail?.order_key) {
        try {
          await fetch(
            `${SHIFTIQ_API}/api/procureiq/receipt-review/invoice-inbox/${inboxId}/match`,
            {
              method: "POST",
              headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
              body: JSON.stringify({
                unified_location_id: locationId,
                brand_id: brandId,
                order_key: detail.order_key,
              }),
            }
          );
        } catch {
          /* verify already succeeded — inbox mark is best-effort */
        }
        matchingInboxIdRef.current = null;
        await loadInbox();
      }
      showToast?.(t("Verified — calcs will use this receipt date", "Επιβεβαιώθηκε — οι υπολογισμοί θα χρησιμοποιήσουν αυτή την ημερομηνία"));
      openDetailKeyRef.current = null;
      setSelectedKey(null);
      setSelectedSummary(null);
      setDetail(null);
      setDetailLoading(false);
      setInvoiceCropFile(null);
      clearInvoicePages();
      setInvoiceExtractNote(null);
      setInvoiceSupplierCheck(null);
      setPendingInvoiceDraft(null);
      setInvoiceVatInfo(null);
      setSupplierProfile(null);
      syncOrderInUrl(null);
      // Reset Open-tab scan dropzone — order was matched/verified.
      clearScanMatchResults();
      await load();
      requestProcureOrdersBadgeRefresh();
    } catch (err) {
      showToast?.(err?.message || "Error");
    } finally {
      setBusy(false);
    }
  }

  function releaseProcureUiLocks() {
    if (typeof document === "undefined") return;
    const unlock = () => {
      if (
        document.querySelector(
          ".modal-overlay, .blocking-action-overlay, .photo-lightbox"
        )
      ) {
        return;
      }
      document.body.style.overflow = "";
      delete document.body.dataset.scrollLocked;
      const root = document.getElementById("root");
      if (root) root.inert = false;
    };
    // After React removes the overlay node.
    requestAnimationFrame(() => requestAnimationFrame(unlock));
  }

  function closeOrderDialog() {
    if (busy || extractBusy) return;
    // Drop confirm draft instead of blocking close (avoids stuck dim overlay).
    if (pendingInvoiceDraft) setPendingInvoiceDraft(null);
    openDetailKeyRef.current = null;
    setSelectedKey(null);
    setSelectedSummary(null);
    setDetail(null);
    setDetailLoading(false);
    setInvoiceCropFile(null);
    clearInvoicePages();
    setInvoiceExtractNote(null);
    setInvoiceSupplierCheck(null);
    setInvoiceVatInfo(null);
    setSupplierProfile(null);
    setRematchIdx(null);
    setRematchFilter("");
    setNeedsRematchIdx(null);
    setInvoiceLightbox(null);
    syncOrderInUrl(null);
    releaseProcureUiLocks();
  }

  function mapDraftLineToEdit(l, orderBaseline, orderDetail) {
    const prior = l.item_id ? orderBaseline.get(String(l.item_id)) : null;
    const orderedQty = numOrNull(l.ordered_qty) ?? numOrNull(prior?.ordered_qty);
    const orderedPrice =
      numOrNull(l.ordered_unit_price) ?? numOrNull(prior?.ordered_unit_price);
    const orderedName =
      l.ordered_product_name ||
      prior?.product_name ||
      (l.matched || l.item_id ? l.product_name : null);
    const invoiceName =
      l.invoice_product_name ||
      (!(l.matched || l.item_id) ? l.product_name : null);
    return {
      item_id: l.item_id || "",
      product_id: l.product_id ?? null,
      product_name: procureiqDisplayProductName(orderedName || l.product_name),
      ordered_product_name: orderedName
        ? procureiqDisplayProductName(orderedName)
        : null,
      invoice_product_name: invoiceName
        ? procureiqDisplayProductName(invoiceName)
        : null,
      qty: Number(l.qty) || 0,
      unit_price: l.unit_price != null ? Number(l.unit_price) : "",
      ordered_qty: orderedQty,
      ordered_unit_price: orderedPrice,
      ocr_qty: numOrNull(l.ocr_qty),
      ocr_unit_price: numOrNull(l.ocr_unit_price),
      tax_rate_pct:
        l.tax_rate_pct != null && Number.isFinite(Number(l.tax_rate_pct))
          ? Number(l.tax_rate_pct)
          : orderDetail?.tax_rate_pct != null
            ? Number(orderDetail.tax_rate_pct)
            : null,
      matched: Boolean(l.item_id || l.matched),
      used_order_values: Boolean(l.used_order_values),
      qty_differs_from_order: Boolean(l.qty_differs_from_order),
      price_differs_from_order: Boolean(l.price_differs_from_order),
      uom_mismatch: Boolean(l.uom_mismatch),
      uom_converted_to_order: Boolean(l.uom_converted_to_order),
      // Supplier SKU memory (see invoice_line_match.py): how the pair was decided.
      supplier_sku: l.supplier_sku ? String(l.supplier_sku) : null,
      item_code: l.item_code ? String(l.item_code) : null,
      match_via: l.match_via || null,
      match_score: numOrNull(l.match_score),
      match_reasons: Array.isArray(l.match_reasons) ? l.match_reasons : [],
      sku_known: Boolean(l.sku_known),
      sku_conflict: Boolean(l.sku_conflict),
      sku_mapping: l.sku_mapping || null,
      extract_index: numOrNull(l.extract_index),
    };
  }

  function editLinesFromDraft(draftLines, orderBaseline, orderDetail) {
    return draftLinesInInvoiceOrder(draftLines).map((l) =>
      mapDraftLineToEdit(l, orderBaseline, orderDetail)
    );
  }

  /** Short label for how an invoice line was paired with an order line. */
  function matchViaLabel(via) {
    switch (via) {
      case "sku":
        return t("by supplier code", "από κωδικό προμηθευτή");
      case "alias":
        return t("by learned alias", "από γνωστή ονομασία");
      case "numeric":
        return t("by qty/price", "από ποσ./τιμή");
      case "name":
        return t("by name", "από όνομα");
      default:
        return null;
    }
  }

  function applyInvoiceDraft(draft, { silent, pageKey = null, append = false } = {}) {
    if (!Array.isArray(draft?.draft_lines) || !draft.draft_lines.length) return;
    const vatInfo = vatInfoFromDraft(draft);
    if (vatInfo) setInvoiceVatInfo(vatInfo);
    const orderBaseline = new Map();
    for (const ol of detail?.lines || []) {
      const id = ol.item_id != null ? String(ol.item_id) : "";
      if (!id) continue;
      orderBaseline.set(id, {
        ordered_qty: numOrNull(ol.ordered_qty ?? ol.qty),
        ordered_unit_price: numOrNull(ol.ordered_unit_price ?? ol.unit_price),
        product_name: ol.product_name,
      });
    }
    const mapped = editLinesFromDraft(draft.draft_lines, orderBaseline, detail).map(
      (l) => ({
        ...l,
        page_key: pageKey || l.page_key || null,
      })
    );
    if (append) {
      setEditLines((prev) => {
        const base =
          prev.reduce(
            (m, l) =>
              Math.max(m, Number.isFinite(Number(l.extract_index)) ? Number(l.extract_index) : -1),
            -1
          ) + 1;
        const stamped = mapped.map((l, i) => ({
          ...l,
          extract_index:
            l.extract_index != null && Number.isFinite(Number(l.extract_index))
              ? base + Number(l.extract_index)
              : base + i,
        }));
        return [...prev, ...stamped];
      });
    } else {
      setEditLines(mapped);
    }
    if (silent) return;
    const matchedN = (draft.draft_lines || []).filter((l) => l.item_id).length;
    const pageN = append ? invoiceAttachments.length + 1 : 1;
    showToast?.(
      append
        ? t(
            `Page ${pageN}: +${draft.draft_lines.length} line(s), ${matchedN} matched — review then Verify`,
            `Σελίδα ${pageN}: +${draft.draft_lines.length} γραμμές, ${matchedN} ταιριαστές — έλεγχος και Verify`
          )
        : t(
            `Read ${draft.draft_lines.length} line(s), matched ${matchedN} to order — review then Verify`,
            `Διαβάστηκαν ${draft.draft_lines.length} γραμμές, ταιριάστηκαν ${matchedN} με την παραγγελία — έλεγχος και Verify`
          )
    );
  }

  function confirmPendingInvoiceDraft() {
    if (!pendingInvoiceDraft?.draft) return;
    const q = pendingInvoiceDraft.quality;
    const draft = pendingInvoiceDraft.draft;
    const pageKey = pendingInvoiceDraft.invoiceKey || null;
    const append = Boolean(pendingInvoiceDraft.append);
    applyInvoiceDraft(draft, { silent: true, pageKey, append });
    setPendingInvoiceDraft(null);
    if (q && q.orderN > 0 && q.matchedN < q.orderN && !append) {
      showToast?.(
        t(
          `Partial receive — ${q.matchedN} of ${q.orderN} order lines from invoice. Review then Verify.`,
          `Μερική παραλαβή — ${q.matchedN} από ${q.orderN} γραμμές παραγγελίας. Έλεγχος και Verify.`
        )
      );
    } else {
      const matchedN = (draft.draft_lines || []).filter((l) => l.item_id).length;
      showToast?.(
        append
          ? t(
              `Added ${draft.draft_lines.length} line(s) from another page (${matchedN} matched)`,
              `Προστέθηκαν ${draft.draft_lines.length} γραμμές από άλλη σελίδα (${matchedN} ταιριαστές)`
            )
          : t(
              `Read ${draft.draft_lines.length} line(s), matched ${matchedN} to order — review then Verify`,
              `Διαβάστηκαν ${draft.draft_lines.length} γραμμές, ταιριάστηκαν ${matchedN} με την παραγγελία — έλεγχος και Verify`
            )
      );
    }
  }

  /** Abort this order — invoice lines are required when a file is attached; do not keep PO lines.
   *  Extra-page weak match: only drop that page and stay in the dialog. */
  async function cancelPendingInvoiceDraft() {
    const pending = pendingInvoiceDraft;
    setPendingInvoiceDraft(null);
    if (pending?.append && pending.invoiceKey) {
      const idx = invoiceAttachments.findIndex((p) => p.key === pending.invoiceKey);
      if (idx >= 0) {
        const page = invoiceAttachments[idx];
        setExtractBusy(true);
        try {
          if (page.key && detail?.order_key) {
            await fetch(
              `${SHIFTIQ_API}/api/procureiq/receipt-review/${encodeURIComponent(detail.order_key)}/invoice-discard`,
              {
                method: "POST",
                headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
                body: JSON.stringify({ invoice_key: page.key }),
              }
            );
          }
          if (page.previewUrl && page.previewOwned) {
            try {
              URL.revokeObjectURL(page.previewUrl);
            } catch {
              /* ignore */
            }
          }
          const next = invoiceAttachments.filter((_, i) => i !== idx);
          setInvoiceAttachments(next);
          if (next.length) syncActiveInvoicePreview(next, Math.min(idx, next.length - 1));
        } finally {
          setExtractBusy(false);
        }
      }
      showToast?.(
        t(
          "Extra page discarded — previous lines kept",
          "Η επιπλέον σελίδα απορρίφθηκε — κρατήθηκαν οι προηγούμενες γραμμές"
        )
      );
      return;
    }
    openDetailKeyRef.current = null;
    setSelectedKey(null);
    setSelectedSummary(null);
    setDetail(null);
    setDetailLoading(false);
    setEditLines([]);
    setInvoiceCropFile(null);
    clearInvoicePages();
    setInvoiceExtractNote(null);
    setInvoiceSupplierCheck(null);
    setInvoiceVatInfo(null);
    setSupplierProfile(null);
    setRematchIdx(null);
    setRematchFilter("");
    setNeedsRematchIdx(null);
    syncOrderInUrl(null);
    showToast?.(
      t(
        "Cancelled — pick another order if this wasn’t the right one",
        "Ακυρώθηκε — επιλέξτε άλλη παραγγελία αν δεν ήταν η σωστή"
      )
    );
  }

  function handleNotReceivedYet() {
    if (busy || extractBusy) return;
    closeOrderDialog();
    showToast?.(
      t(
        "Still open — we'll alert again while it's due",
        "Παραμένει ανοιχτή — θα ειδοποιήσουμε ξανά όσο εκκρεμεί"
      )
    );
  }

  function acceptInvoiceFile(file) {
    if (!file || extractBusy || invoiceCropFile || scanBusy) return;
    const okPdf = isInvoicePdfFile(file);
    const okImage = isInvoiceImageFile(file);
    if (!okPdf && !okImage) {
      showToast?.(t("Use a PDF or photo (JPEG/PNG/WebP)", "Χρησιμοποιήστε PDF ή φωτο (JPEG/PNG/WebP)"));
      return;
    }
    if (okImage && !okPdf) {
      cropPurposeRef.current = "dialog";
      setInvoiceCropFile(file);
      return;
    }
    setInvoiceFromFile(file);
    handleInvoiceFile(file);
  }

  async function uploadScanInvoiceFile(file, meta = {}, pageLabel = "page") {
    const timer = invoiceDebugStart("upload", {
      page: pageLabel,
      file: file?.name || null,
      bytes: file?.size ?? null,
    });
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("unified_location_id", String(locationId));
      if (locationName) form.append("location_name", String(locationName));
      appendInvoiceTrainingSidecars(form, { ...meta, primaryFile: file });
      const up = await fetch(
        `${SHIFTIQ_API}/api/procureiq/receipt-review/invoice-scan-upload`,
        { method: "POST", headers: getAuthHeadersMultipart(), body: form }
      );
      if (!up.ok) throw new Error(await procureiqApiError(up, "Upload failed"));
      const json = await up.json();
      timer.end({ ok: true, key: json?.key || null });
      return json;
    } catch (err) {
      timer.end({ ok: false, error: err?.message || String(err) });
      throw err;
    }
  }

  async function runInvoiceExtractOnly(invoiceKey, pageLabel = "page") {
    const timer = invoiceDebugStart("invoice-extract (client round-trip)", {
      page: pageLabel,
      key: invoiceKey,
    });
    try {
      const matchRes = await fetch(
        `${SHIFTIQ_API}/api/procureiq/receipt-review/invoice-match`,
        {
          method: "POST",
          headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({
            unified_location_id: locationId,
            brand_id: brandId,
            invoice_s3_key: invoiceKey,
            extract_only: true,
          }),
        }
      );
      if (!matchRes.ok) {
        throw new Error(await procureiqApiError(matchRes, "Extract failed"));
      }
      const json = await matchRes.json();
      const server = json?.timings || null;
      timer.end({
        ok: true,
        lines: Array.isArray(json?.extract?.draft_lines)
          ? json.extract.draft_lines.length
          : 0,
        server_ms: server,
      });
      if (server?.extract_ms != null) {
        // eslint-disable-next-line no-console
        console.info(INVOICE_DEBUG, "lines analysis (server extract)", `${server.extract_ms}ms`, {
          page: pageLabel,
        });
      }
      return json;
    } catch (err) {
      timer.end({ ok: false, error: err?.message || String(err) });
      throw err;
    }
  }

  async function runInvoiceRank(mergedExtract, invoiceKey, pageLabel = "rank") {
    const timer = invoiceDebugStart("invoice-rank (client round-trip)", {
      page: pageLabel,
      lines: mergedExtract?.draft_lines?.length || 0,
    });
    try {
      const rankRes = await fetch(
        `${SHIFTIQ_API}/api/procureiq/receipt-review/invoice-rank`,
        {
          method: "POST",
          headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({
            unified_location_id: locationId,
            brand_id: brandId,
            invoice_s3_key: invoiceKey || null,
            supplier_name: mergedExtract?.supplier_name || null,
            invoice_date: mergedExtract?.invoice_date || null,
            draft_lines: mergedExtract?.draft_lines || [],
            ui_from_date: fromDate || null,
            ui_to_date: toDate || null,
          }),
        }
      );
      if (!rankRes.ok) {
        throw new Error(await procureiqApiError(rankRes, "Match failed"));
      }
      const json = await rankRes.json();
      const server = json?.timings || null;
      timer.end({
        ok: true,
        matches: Array.isArray(json?.matches) ? json.matches.length : 0,
        server_ms: server,
      });
      if (server) {
        // eslint-disable-next-line no-console
        console.info(INVOICE_DEBUG, "order matching (server rank)", `${server.rank_ms}ms`, {
          orders_load_ms: server.orders_load_ms,
          match_window: json?.match_window || null,
        });
      }
      return json;
    } catch (err) {
      timer.end({ ok: false, error: err?.message || String(err) });
      throw err;
    }
  }

  /**
   * Upload + OCR every page, then rank orders once on the merged lines.
   * Do not rank per page (that reloaded the whole order catalog each time).
   */
  async function flushScanQueueAndMatch(queue) {
    if (!queue.length || !locationId) return;
    const pageCount = queue.length;
    // upload+extract per page, then one rank step
    const totalSteps = pageCount * 2 + 1;
    const setJob = (partial) =>
      setInvoiceJobProgress({ kind: "scan", pages: pageCount, total: totalSteps, ...partial });
    setScanBusy(true);
    setScanPhase("upload");
    setJob({ step: "upload", page: 1, index: 0 });
    setScanMatches(null);
    setScanSupplierKey(null);
    setScanExtract(null);
    setScanInvoiceKey(null);
    clearScanBuiltPages({ revoke: true });
    const session = invoiceDebugStart("build invoice session", {
      pages: pageCount,
    });
    try {
      const built = [];
      const rawChunks = [];
      let supplierProfile = null;
      let serverExtractTotalMs = 0;
      for (let i = 0; i < pageCount; i++) {
        const { file, meta } = queue[i];
        const pageLabel = `page ${i + 1}/${pageCount}`;
        if (i === 0) setScanFromFile(file);
        else setScanFileName(file?.name || `page-${i + 1}`);
        setScanPhase("upload");
        setJob({ step: "upload", page: i + 1, index: i * 2 });
        const uploaded = await uploadScanInvoiceFile(file, meta || {}, pageLabel);
        const previewUrl = createInvoicePreviewUrl(file);
        built.push({
          key: uploaded.key,
          filename: uploaded.filename || file?.name || `page-${i + 1}`,
          seq: i,
          folder: "_scan",
          previewUrl: previewUrl || null,
          previewOwned: Boolean(previewUrl),
        });
        setScanBuiltPages([...built]);
        setActiveScanPageIdx(0);
        setScanInvoiceKey(built[0].key);
        setScanPhase("extract");
        setJob({ step: "extract", page: i + 1, index: i * 2 + 1 });
        const extracted = await runInvoiceExtractOnly(uploaded.key, pageLabel);
        if (extracted?.timings?.extract_ms != null) {
          serverExtractTotalMs += Number(extracted.timings.extract_ms) || 0;
        }
        rawChunks.push({
          draft: extracted.extract || null,
          pageKey: uploaded.key,
        });
      }
      const mergedExtract = mergePageDrafts(rawChunks);
      setScanPhase("rank");
      setJob({ step: "rank", page: pageCount, index: totalSteps - 1 });
      const ranked = await runInvoiceRank(
        mergedExtract,
        built[0]?.key || null,
        `merged ${built.length} page(s)`
      );
      if (ranked.supplier_profile) supplierProfile = ranked.supplier_profile;
      const nextMatches = Array.isArray(ranked?.matches) ? ranked.matches : [];
      setScanExtract(mergedExtract);
      setScanSupplierProfile(supplierProfile || null);
      setScanMatches(nextMatches);
      const groups = groupScanMatchesBySupplier(nextMatches);
      setScanSupplierKey(groups.length === 1 ? groups[0].key : null);
      session.end({
        ok: true,
        server_extract_total_ms: Math.round(serverExtractTotalMs),
        server_rank_total_ms: Math.round(Number(ranked?.timings?.rank_ms) || 0),
        lines: mergedExtract?.draft_lines?.length || 0,
        matches: nextMatches.length,
        match_window: ranked?.match_window || null,
      });
      const topMatch = [...nextMatches].sort(
        (a, b) => (Number(b.score) || 0) - (Number(a.score) || 0)
      )[0];
      if (!nextMatches.length) {
        showToast?.(
          t(
            "No matching orders since 15 Oct — pick an order from the list",
            "Καμία ταιριαστή παραγγελία από 15 Οκτ — επιλέξτε από τη λίστα"
          )
        );
      } else if (topMatch && matchLooksAlreadyReceived(topMatch)) {
        showToast?.(
          alreadyReceivedBannerCopy(t, {
            orderLabel: topMatch.supy_order_number || topMatch.supy_order_id,
          })
        );
      } else if (built.length > 1) {
        showToast?.(
          t(
            `Invoice ready · ${built.length} pages · ${mergedExtract?.draft_lines?.length || 0} lines — pick the order`,
            `Τιμολόγιο έτοιμο · ${built.length} σελίδες · ${mergedExtract?.draft_lines?.length || 0} γραμμές — επιλέξτε παραγγελία`
          )
        );
      }
    } catch (err) {
      session.end({ ok: false, error: err?.message || String(err) });
      showToast?.(err?.message || "Error");
      setScanMatches(null);
      setScanSupplierKey(null);
      setScanExtract(null);
    } finally {
      setScanBusy(false);
      setScanPhase(null);
      setInvoiceJobProgress(null);
    }
  }

  /**
   * Crop dialog commits an ordered list of pages when the user finishes.
   * Scan: build full invoice then match. Inbox: upload only (match later).
   * Order dialog: upload/extract all, then apply every line once.
   */
  async function finishCroppedInvoice(pages) {
    const purpose = cropPurposeRef.current;
    setInvoiceCropFile(null);
    scanPageQueueRef.current = [];
    const list = (Array.isArray(pages) ? pages : [])
      .map((p) => (p?.file ? { file: p.file, meta: p.meta || {} } : null))
      .filter(Boolean);
    if (!list.length) return;
    if (purpose === "scan") {
      await flushScanQueueAndMatch(list);
      return;
    }
    if (purpose === "inbox") {
      await flushInboxUpload(list);
      return;
    }
    await applyInvoicePagesToOpenOrder(list);
  }

  async function uploadInboxInvoiceFile(file, meta = {}, pageLabel = "page") {
    const timer = invoiceDebugStart("inbox-upload", {
      page: pageLabel,
      file: file?.name || null,
      bytes: file?.size ?? null,
    });
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("unified_location_id", String(locationId));
      if (locationName) form.append("location_name", String(locationName));
      appendInvoiceTrainingSidecars(form, { ...meta, primaryFile: file });
      const up = await fetch(
        `${SHIFTIQ_API}/api/procureiq/receipt-review/invoice-inbox-upload`,
        { method: "POST", headers: getAuthHeadersMultipart(), body: form }
      );
      if (!up.ok) throw new Error(await procureiqApiError(up, "Upload failed"));
      const json = await up.json();
      timer.end({ ok: true, key: json?.key || null });
      return json;
    } catch (err) {
      timer.end({ ok: false, error: err?.message || String(err) });
      throw err;
    }
  }

  /** Upload-only path: straighten pages → store in inbox (no OCR / match). */
  async function flushInboxUpload(queue) {
    if (!queue.length || !locationId) return;
    const pageCount = queue.length;
    const setJob = (partial) =>
      setInvoiceJobProgress({
        kind: "scan",
        pages: pageCount,
        total: pageCount,
        ...partial,
      });
    setInboxBusy(true);
    setJob({ step: "upload", page: 1, index: 0 });
    try {
      const pages = [];
      for (let i = 0; i < pageCount; i++) {
        const { file, meta } = queue[i];
        const pageLabel = `page ${i + 1}/${pageCount}`;
        setJob({ step: "upload", page: i + 1, index: i });
        const uploaded = await uploadInboxInvoiceFile(file, meta || {}, pageLabel);
        pages.push({
          key: uploaded.key,
          filename: uploaded.filename || file?.name || `page-${i + 1}`,
          content_type: uploaded.content_type || file?.type || null,
          seq: i,
        });
      }
      const res = await fetch(
        `${SHIFTIQ_API}/api/procureiq/receipt-review/invoice-inbox`,
        {
          method: "POST",
          headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({
            unified_location_id: locationId,
            brand_id: brandId,
            pages,
          }),
        }
      );
      if (!res.ok) throw new Error(await procureiqApiError(res, "Save failed"));
      showToast?.(
        t(
          `Uploaded ${pageCount} page(s) — we’ll match them to an order later`,
          `Ανέβηκαν ${pageCount} σελίδ(ες) — θα τις αντιστοιχίσουμε αργότερα`
        )
      );
      await loadInbox();
    } catch (err) {
      showToast?.(err?.message || "Error");
    } finally {
      setInboxBusy(false);
      setInvoiceJobProgress(null);
    }
  }

  function acceptInboxFile(file) {
    if (!file || inboxBusy || extractBusy || invoiceCropFile || scanBusy) return;
    const okPdf = isInvoicePdfFile(file);
    const okImage = isInvoiceImageFile(file);
    if (!okPdf && !okImage) {
      showToast?.(t("Use a PDF or photo (JPEG/PNG/WebP)", "Χρησιμοποιήστε PDF ή φωτο (JPEG/PNG/WebP)"));
      return;
    }
    if (okImage && !okPdf) {
      cropPurposeRef.current = "inbox";
      setInvoiceCropFile(file);
      return;
    }
    flushInboxUpload([{ file, meta: {} }]);
  }

  /** Run Open-tab OCR + order rank from an already-uploaded inbox set. */
  async function matchInboxItem(item) {
    const pages = Array.isArray(item?.pages) ? item.pages.filter((p) => p?.key) : [];
    if (!pages.length || !locationId) return;
    matchingInboxIdRef.current = item.id;
    setActiveTab("open");
    setInboxActionId(item.id);
    const pageCount = pages.length;
    // extract per page + one rank (no upload — pages already in inbox)
    const totalSteps = pageCount + 1;
    const knownCached = Boolean(
      item.lines_cached || pages.every((p) => p.extract_cached)
    );
    const setJob = (partial) =>
      setInvoiceJobProgress({
        kind: "inbox-match",
        pages: pageCount,
        total: totalSteps,
        ...partial,
      });
    setScanBusy(true);
    setScanPhase("extract");
    const initialReused = knownCached
      ? Object.fromEntries(pages.map((_, i) => [i + 1, true]))
      : {};
    setJob({
      step: "extract",
      page: 1,
      index: 0,
      pageReused: knownCached,
      reusedPages: { ...initialReused },
    });
    setScanMatches(null);
    setScanSupplierKey(null);
    setScanExtract(null);
    setScanInvoiceKey(pages[0].key);
    clearScanBuiltPages({ revoke: true });
    try {
      const built = await hydrateInvoicePagePreviews(
        pages.map((p, i) => ({
          key: p.key,
          filename: p.filename || `page-${i + 1}`,
          content_type: p.content_type || null,
          seq: i,
          folder: "_inbox",
          previewUrl: p.url || p.previewUrl || null,
          previewOwned: false,
        }))
      );
      setScanBuiltPages(built);
      setActiveScanPageIdx(0);
      setScanFileName(built[0]?.filename || "invoice");
      if (built[0]?.previewUrl) {
        scanPreviewOwnedRef.current = Boolean(built[0].previewOwned);
        setScanPreviewUrl(built[0].previewUrl);
      } else if (built[0]?.key) {
        // Ensure the matcher sees page 1 even if list hydrate missed the blob URL.
        void selectScanPreviewPage(0);
      }
      const rawChunks = [];
      let supplierProfile = null;
      let reusedAll = true;
      const reusedPages = { ...initialReused };
      for (let i = 0; i < pageCount; i++) {
        const pageLabel = `page ${i + 1}/${pageCount}`;
        const expectReuse = Boolean(pages[i].extract_cached || knownCached);
        setScanPhase("extract");
        setJob({
          step: "extract",
          page: i + 1,
          index: i,
          pageReused: expectReuse,
          reusedPages: { ...reusedPages },
        });
        const extracted = await runInvoiceExtractOnly(pages[i].key, pageLabel);
        const reused = Boolean(
          extracted?.reused_scan_extract || extracted?.timings?.reused_scan_extract
        );
        if (!reused) reusedAll = false;
        else reusedPages[i + 1] = true;
        setJob({
          step: "extract",
          page: i + 1,
          index: i,
          pageReused: reused,
          reusedPages: { ...reusedPages },
        });
        rawChunks.push({
          draft: extracted.extract || null,
          pageKey: pages[i].key,
        });
        if (extracted.supplier_profile) supplierProfile = extracted.supplier_profile;
      }
      // Sidecar is written on every successful page extract — next Match reuses it.
      setInboxItems((prev) =>
        prev.map((row) => {
          if (row.id !== item.id) return row;
          const nextPages = (row.pages || []).map((p) =>
            pages.some((x) => x.key === p.key)
              ? { ...p, extract_cached: true }
              : p
          );
          return {
            ...row,
            pages: nextPages,
            lines_cached:
              nextPages.length > 0 && nextPages.every((p) => p.extract_cached),
          };
        })
      );
      const mergedExtract = mergePageDrafts(rawChunks);
      setScanPhase("rank");
      setJob({ step: "rank", page: pageCount, index: totalSteps - 1 });
      const ranked = await runInvoiceRank(
        mergedExtract,
        built[0]?.key || null,
        `inbox ${built.length} page(s)`
      );
      if (ranked.supplier_profile) supplierProfile = ranked.supplier_profile;
      const nextMatches = Array.isArray(ranked?.matches) ? ranked.matches : [];
      setScanExtract(mergedExtract);
      setScanSupplierProfile(supplierProfile || null);
      setScanMatches(nextMatches);
      const groups = groupScanMatchesBySupplier(nextMatches);
      setScanSupplierKey(groups.length === 1 ? groups[0].key : null);
      const topMatch = [...nextMatches].sort(
        (a, b) => (Number(b.score) || 0) - (Number(a.score) || 0)
      )[0];
      if (!nextMatches.length) {
        showToast?.(
          t(
            "No matching orders since 15 Oct — pick an order from the list",
            "Καμία ταιριαστή παραγγελία από 15 Οκτ — επιλέξτε από τη λίστα"
          )
        );
      } else if (topMatch && matchLooksAlreadyReceived(topMatch)) {
        showToast?.(
          alreadyReceivedBannerCopy(t, {
            orderLabel: topMatch.supy_order_number || topMatch.supy_order_id,
          })
        );
      } else {
        showToast?.(
          reusedAll
            ? t(
                "Using saved lines — pick the order below",
                "Χρήση αποθηκευμένων γραμμών — επιλέξτε παραγγελία κάτω"
              )
            : t(
                "Inbox invoice ready — pick the order below",
                "Τιμολόγιο από inbox έτοιμο — επιλέξτε παραγγελία κάτω"
              )
        );
      }
    } catch (err) {
      matchingInboxIdRef.current = null;
      showToast?.(err?.message || "Error");
      setScanMatches(null);
      setScanSupplierKey(null);
      setScanExtract(null);
    } finally {
      setScanBusy(false);
      setScanPhase(null);
      setInvoiceJobProgress(null);
      setInboxActionId(null);
    }
  }

  async function dismissInboxItem(item) {
    if (!item?.id || !locationId) return;
    const pageN = item.page_count || item.pages?.length || 0;
    const ok = window.confirm(
      t(
        `Dismiss this upload (${pageN} page(s))? This cannot be undone.`,
        `Απόρριψη αυτής της μεταφόρτωσης (${pageN} σελίδ(ες)); Δεν αναιρείται.`
      )
    );
    if (!ok) return;
    setInboxActionId(item.id);
    try {
      const qs = new URLSearchParams({
        location_id: String(locationId),
        brand_id: String(brandId),
      });
      const res = await fetch(
        `${SHIFTIQ_API}/api/procureiq/receipt-review/invoice-inbox/${item.id}/dismiss?${qs}`,
        { method: "POST", headers: getAuthHeaders() }
      );
      if (!res.ok) throw new Error(await procureiqApiError(res, "Dismiss failed"));
      showToast?.(t("Upload dismissed", "Η μεταφόρτωση απορρίφθηκε"));
      await loadInbox();
    } catch (err) {
      showToast?.(err?.message || "Error");
    } finally {
      setInboxActionId(null);
    }
  }

  /** Upload + extract every page, then apply one merged draft to the open order. */
  async function applyInvoicePagesToOpenOrder(list) {
    if (!detail?.order_key || !list.length || extractBusy) return;
    const priorPages = invoiceAttachments;
    const pageCount = list.length;
    const totalSteps = pageCount * 2;
    setExtractBusy(true);
    setExtractProgress({
      mode: "read",
      page: 1,
      total: pageCount,
      fileName: list[0]?.file?.name || null,
    });
    setInvoiceJobProgress({
      kind: "read",
      step: "upload",
      page: 1,
      pages: pageCount,
      index: 0,
      total: totalSteps,
    });
    setInvoiceExtractNote(null);
    setInvoiceSupplierCheck(null);
    const session = invoiceDebugStart("apply pages to open order", {
      order: detail.order_key,
      pages: pageCount,
    });
    try {
      const built = [...priorPages];
      const pageDrafts = [];
      for (let i = 0; i < pageCount; i++) {
        const { file, meta } = list[i];
        const pageLabel = `page ${i + 1}/${pageCount}`;
        setExtractProgress({
          mode: "read",
          page: i + 1,
          total: pageCount,
          fileName: file?.name || null,
        });
        setInvoiceJobProgress({
          kind: "read",
          step: "upload",
          page: i + 1,
          pages: pageCount,
          index: i * 2,
          total: totalSteps,
        });
        const previewUrl = createInvoicePreviewUrl(file);
        const pendingKey = `pending-${Date.now()}-${built.length}`;
        const pendingPage = {
          key: pendingKey,
          filename: file?.name || `page-${built.length + 1}`,
          seq: built.length,
          folder: null,
          previewUrl: previewUrl || null,
          previewOwned: Boolean(previewUrl),
          pending: true,
        };
        built.push(pendingPage);
        setInvoiceAttachments([...built]);
        syncActiveInvoicePreview(built, built.length - 1);

        const form = new FormData();
        form.append("file", file);
        form.append("unified_location_id", String(locationId));
        if (locationName) form.append("location_name", String(locationName));
        if (detail.supplier_name) form.append("supplier_name", String(detail.supplier_name));
        if (detail.supy_order_number) {
          form.append("supy_order_number", String(detail.supy_order_number));
        }
        appendInvoiceTrainingSidecars(form, { ...meta, primaryFile: file });
        const upTimer = invoiceDebugStart("upload", {
          page: pageLabel,
          file: file?.name || null,
          bytes: file?.size ?? null,
          order: detail.order_key,
        });
        const up = await fetch(
          `${SHIFTIQ_API}/api/procureiq/receipt-review/${encodeURIComponent(detail.order_key)}/invoice-upload`,
          { method: "POST", headers: getAuthHeadersMultipart(), body: form }
        );
        if (!up.ok) throw new Error(await procureiqApiError(up, "Upload failed"));
        const uploaded = await up.json();
        upTimer.end({ ok: true, key: uploaded?.key || null });
        const page = {
          key: uploaded.key,
          filename: uploaded.filename || file.name,
          seq: uploaded.seq,
          folder: uploaded.folder,
          previewUrl: previewUrl || null,
          previewOwned: Boolean(previewUrl),
          pending: false,
        };
        built[built.length - 1] = page;
        setInvoiceAttachments([...built]);
        syncActiveInvoicePreview(built, built.length - 1);

        setInvoiceJobProgress({
          kind: "read",
          step: "extract",
          page: i + 1,
          pages: pageCount,
          index: i * 2 + 1,
          total: totalSteps,
        });
        const extractTimer = invoiceDebugStart("lines analysis (order extract)", {
          page: pageLabel,
          key: uploaded.key,
          order: detail.order_key,
        });
        const ex = await fetch(
          `${SHIFTIQ_API}/api/procureiq/receipt-review/${encodeURIComponent(detail.order_key)}/invoice-extract`,
          {
            method: "POST",
            headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
            body: JSON.stringify({
              unified_location_id: locationId,
              brand_id: brandId,
              invoice_s3_key: uploaded.key,
              order_lines: detail.lines || [],
              expected_supplier_name: detail.supplier_name || "",
              supplier_id: detail.supplier_id ?? null,
            }),
          }
        );
        if (!ex.ok) throw new Error(await procureiqApiError(ex, "Extract failed"));
        const draft = await ex.json();
        extractTimer.end({
          ok: true,
          lines: Array.isArray(draft?.draft_lines) ? draft.draft_lines.length : 0,
          server_ms: draft?.timings || null,
        });
        if (draft.supplier_profile) setSupplierProfile(draft.supplier_profile);
        pageDrafts.push({ draft, pageKey: uploaded.key });
      }

      const merged = mergePageDrafts(pageDrafts);
      session.end({
        ok: true,
        lines: merged?.draft_lines?.length || 0,
      });
      if (!merged?.draft_lines?.length) {
        showToast?.(t("No lines extracted", "Δεν εξήχθησαν γραμμές"));
        return;
      }
      setInvoiceSupplierCheck(null);
      const vatInfo = vatInfoFromDraft(merged);
      if (vatInfo) setInvoiceVatInfo(vatInfo);
      setInvoiceExtractNote(
        t(
          `Read ${merged.draft_lines.length} line(s) from ${pageDrafts.length} page(s)`,
          `Διαβάστηκαν ${merged.draft_lines.length} γραμμές από ${pageDrafts.length} σελίδες`
        )
      );
      const quality = assessInvoiceDraftMatch(merged, (detail.lines || []).length);
      const replace = priorPages.length === 0;
      if (quality.weak) {
        setPendingInvoiceDraft({
          draft: merged,
          quality,
          invoiceKey: built[0]?.key || null,
          filename: built[0]?.filename || "invoice",
          append: !replace,
        });
        showToast?.(
          t(
            "Invoice may not match this order — confirm before applying lines",
            "Το τιμολόγιο μπορεί να μην ταιριάζει — επιβεβαιώστε πριν εφαρμοστούν οι γραμμές"
          )
        );
      } else if (replace) {
        applyInvoiceDraft(merged, { append: false, pageKey: null });
      } else {
        applyInvoiceDraft(merged, { append: true, pageKey: null });
      }
    } catch (err) {
      session.end({ ok: false, error: err?.message || String(err) });
      setInvoiceAttachments(priorPages);
      if (priorPages.length) {
        syncActiveInvoicePreview(priorPages, priorPages.length - 1);
      }
      showToast?.(err?.message || "Error");
      setInvoiceExtractNote(err?.message || "Extract failed");
    } finally {
      setExtractBusy(false);
      setExtractProgress(null);
      setInvoiceJobProgress(null);
    }
  }

  function appendInvoiceTrainingSidecars(form, meta = {}) {
    const original = meta.originalFile;
    // Only attach when straighten produced a different primary file.
    if (original && original !== meta.primaryFile && !meta.skipped) {
      form.append("original_file", original);
    }
    if (
      !meta.skipped &&
      Array.isArray(meta.corners) &&
      meta.corners.length === 4
    ) {
      form.append(
        "corners_json",
        JSON.stringify({
          page_detect_version: meta.pageDetectVersion || null,
          corners: meta.corners,
          natural_width: meta.naturalWidth || null,
          natural_height: meta.naturalHeight || null,
          enhanced: Boolean(meta.enhanced),
          enhance_version: meta.enhanceVersion || null,
          skipped: false,
        })
      );
    }
  }

  function acceptScanFile(file) {
    if (!file || scanBusy || extractBusy || invoiceCropFile) return;
    const okPdf = isInvoicePdfFile(file);
    const okImage = isInvoiceImageFile(file);
    if (!okPdf && !okImage) {
      showToast?.(t("Use a PDF or photo (JPEG/PNG/WebP)", "Χρησιμοποιήστε PDF ή φωτο (JPEG/PNG/WebP)"));
      return;
    }
    if (okImage && !okPdf) {
      cropPurposeRef.current = "scan";
      setInvoiceCropFile(file);
      return;
    }
    setScanFromFile(file);
    handleScanFile(file);
  }

  async function handleScanFile(file, meta = {}) {
    if (!locationId || !file || scanBusy) return;
    await flushScanQueueAndMatch([{ file, meta }]);
  }

  function clearScanBuiltPages({ revoke = true } = {}) {
    if (revoke) {
      for (const p of scanBuiltPagesRef.current) {
        if (
          p?.previewUrl &&
          p.previewOwned &&
          !inboxSharesPreviewUrl(p.previewUrl)
        ) {
          try {
            URL.revokeObjectURL(p.previewUrl);
          } catch {
            /* ignore */
          }
        }
      }
    }
    setScanBuiltPages([]);
    setActiveScanPageIdx(0);
  }

  async function selectScanPreviewPage(idx, { openLightbox = false } = {}) {
    const list = scanBuiltPagesRef.current || [];
    if (!list.length) return;
    const i = Math.max(0, Math.min(Number(idx) || 0, list.length - 1));
    setActiveScanPageIdx(i);
    let page = list[i];
    let url = page?.previewUrl || null;
    if (!url && page?.key && looksLikeInvoiceImageKey(page.key, page.filename, page.content_type)) {
      url = await fetchInvoicePreviewBlobUrl(page.key);
      if (url) {
        const next = list.map((p, j) =>
          j === i ? { ...p, previewUrl: url, previewOwned: true } : p
        );
        setScanBuiltPages(next);
        page = next[i];
      }
    }
    setScanFileName(page?.filename || `page-${i + 1}`);
    if (url) {
      // Preview is owned by the page entry (or was already); don't revoke on switch.
      scanPreviewOwnedRef.current = false;
      setScanPreviewUrl(url);
    }
    if (openLightbox && url) {
      setInvoiceLightbox({
        url,
        label:
          page?.filename ||
          t(`Page ${i + 1}`, `Σελίδα ${i + 1}`),
      });
    }
  }

  function clearScanMatchResults() {
    matchingInboxIdRef.current = null;
    setScanMatches(null);
    setScanSupplierKey(null);
    setScanExtract(null);
    setScanSupplierProfile(null);
    setScanInvoiceKey(null);
    setScanFileName("");
    clearScanBuiltPages({ revoke: true });
    clearScanPreview();
  }

  /** Concatenate per-page draft_lines with continuing extract_index + page_key. */
  function mergePageDrafts(pageDrafts) {
    const lines = [];
    let shell = null;
    for (const { draft, pageKey } of pageDrafts) {
      if (!draft) continue;
      if (!shell) shell = { ...draft };
      if (draft.total_net != null || draft.total_gross != null) {
        shell = {
          ...shell,
          vat_rate_pct: draft.vat_rate_pct ?? shell.vat_rate_pct,
          total_net: draft.total_net ?? shell.total_net,
          total_vat: draft.total_vat ?? shell.total_vat,
          total_gross: draft.total_gross ?? shell.total_gross,
          prices_include_vat: draft.prices_include_vat ?? shell.prices_include_vat,
          converted_from_gross: draft.converted_from_gross ?? shell.converted_from_gross,
        };
      }
      if (draft.supplier_name && !shell.supplier_name) {
        shell.supplier_name = draft.supplier_name;
      }
      if (draft.invoice_date && !shell.invoice_date) {
        shell.invoice_date = draft.invoice_date;
      }
      const base = lines.length;
      (draft.draft_lines || []).forEach((l, i) => {
        lines.push({
          ...l,
          page_key: pageKey || l.page_key || null,
          extract_index:
            l.extract_index != null && Number.isFinite(Number(l.extract_index))
              ? base + Number(l.extract_index)
              : base + i,
        });
      });
    }
    if (!shell) return null;
    return {
      ...shell,
      draft_lines: lines,
      extract_count: lines.length,
      // Never keep the pre-match "pick an order" note on the order dialog.
      note: null,
    };
  }

  async function pickScanMatch(match) {
    if (!match?.order_key || scanBusy || extractBusy) return;
    const already = matchLooksAlreadyReceived(match);
    if (already) {
      const ok = window.confirm(
        t(
          "This order looks already received in Supy — open for review anyway?",
          "Αυτή η παραγγελία φαίνεται ήδη παραληφθείσα στη Supy — άνοιγμα για έλεγχο;"
        )
      );
      if (!ok) return;
    }
    const rawPages = (scanBuiltPages.length
      ? scanBuiltPages
      : scanInvoiceKey
        ? [
            {
              key: scanInvoiceKey || match.invoice_s3_key,
              filename: scanFileName || "invoice",
              seq: 0,
              folder: "_scan",
              previewUrl: scanPreviewUrl,
              previewOwned: Boolean(scanPreviewUrl),
            },
          ]
        : []
    ).filter((p) => p?.key);
    // Local-disk keys need an authenticated blob preview (no public URL).
    const pages = await hydrateInvoicePagePreviews(rawPages);

    // Hand preview ownership to the order dialog — but not blobs the Upload
    // tab still shows (revoking those breaks inbox thumbs after close).
    scanPreviewOwnedRef.current = false;
    const fromInbox = matchingInboxIdRef.current != null;
    for (const p of pages) {
      if (!p.previewUrl) continue;
      const shared =
        fromInbox ||
        p.folder === "_inbox" ||
        inboxSharesPreviewUrl(p.previewUrl);
      p.previewOwned = !shared;
    }
    clearScanPreview({ revoke: false });
    clearScanBuiltPages({ revoke: false });

    const order =
      (data?.orders || []).find((o) => o.order_key === match.order_key) ||
      (data?.open_due || []).find((o) => o.order_key === match.order_key) ||
      (data?.received_late || []).find((o) => o.order_key === match.order_key) ||
      {
        order_key: match.order_key,
        supplier_name: match.supplier_name,
        submitted_at: match.submitted_at,
        expected_delivery_date: match.expected_delivery_date,
        status: match.status,
        review_bucket: match.review_bucket,
        supy_order_id: match.supy_order_id,
        supy_order_number: match.supy_order_number,
        total_ordered: match.total_ordered,
        ordered_total: match.ordered_total,
      };

    // Reuse scan OCR — only link lines to this order locally (no second Bedrock).
    const priorExtract = scanExtract;
    const priorProfile = scanSupplierProfile;
    setExtractBusy(true);
    setExtractProgress({
      mode: "match-order",
      page: 0,
      total: 1,
      fileName: pages[0]?.filename || scanFileName || null,
    });
    setInvoiceJobProgress({
      kind: "apply",
      step: "open",
      index: 0,
      total: 2,
      pages: pages.length,
    });

    let json;
    try {
      json = await openDetail(order, {
        widenDates: true,
        scanApply: {
          orderKey: match.order_key,
          pages,
          invoiceKey: pages[0]?.key || null,
          filename: pages[0]?.filename || scanFileName || "invoice",
          previewUrl: pages[0]?.previewUrl || null,
          draft: null,
          note: null,
          vatInfo: null,
          supplierProfile: priorProfile || null,
        },
      });
    } catch (err) {
      setExtractBusy(false);
      setExtractProgress(null);
      setInvoiceJobProgress(null);
      showToast?.(err?.message || "Error");
      return;
    }
    if (!json || !pages.length) {
      setExtractBusy(false);
      setExtractProgress(null);
      setInvoiceJobProgress(null);
      return;
    }

    const applyTimer = invoiceDebugStart("apply to order (reuse scan extract)", {
      order: match.order_key,
      pages: pages.length,
      scan_lines: priorExtract?.draft_lines?.length || 0,
    });
    try {
      setExtractProgress({
        mode: "match-order",
        page: 1,
        total: 1,
        fileName: pages[0]?.filename || null,
      });
      setInvoiceJobProgress({
        kind: "apply",
        step: "link",
        index: 1,
        total: 2,
        pages: pages.length,
      });
      let merged = null;
      const reuseLines = Array.isArray(priorExtract?.draft_lines)
        ? priorExtract.draft_lines
        : [];
      if (reuseLines.length) {
        const matchTimer = invoiceDebugStart("order matching (reuse extract)", {
          order: match.order_key,
          lines: reuseLines.length,
        });
        const ex = await fetch(
          `${SHIFTIQ_API}/api/procureiq/receipt-review/${encodeURIComponent(match.order_key)}/invoice-extract`,
          {
            method: "POST",
            headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
            body: JSON.stringify({
              unified_location_id: locationId,
              brand_id: brandId,
              invoice_s3_key: pages[0].key,
              order_lines: json.lines || [],
              expected_supplier_name: json.supplier_name || match.supplier_name || "",
              supplier_id: json.supplier_id ?? null,
              draft_lines: reuseLines,
              supplier_name: priorExtract?.supplier_name || null,
              invoice_date: priorExtract?.invoice_date || null,
              vat_rate_pct: priorExtract?.vat_rate_pct ?? null,
              total_net: priorExtract?.total_net ?? null,
              total_vat: priorExtract?.total_vat ?? null,
              total_gross: priorExtract?.total_gross ?? null,
              prices_include_vat: priorExtract?.prices_include_vat ?? null,
              converted_from_gross: priorExtract?.converted_from_gross ?? null,
            }),
          }
        );
        if (!ex.ok) throw new Error(await procureiqApiError(ex, "Match failed"));
        const draft = await ex.json();
        matchTimer.end({
          ok: true,
          lines: Array.isArray(draft?.draft_lines) ? draft.draft_lines.length : 0,
          reused: true,
          server_ms: draft?.timings || null,
        });
        if (draft.supplier_profile) setSupplierProfile(draft.supplier_profile);
        merged = {
          ...draft,
          draft_lines: (draft.draft_lines || []).map((l, i) => ({
            ...l,
            page_key: l.page_key || reuseLines[i]?.page_key || pages[0]?.key || null,
          })),
        };
      } else {
        const pageDrafts = [];
        for (let i = 0; i < pages.length; i++) {
          const page = pages[i];
          setExtractProgress({
            mode: "match-order",
            page: i + 1,
            total: pages.length,
            fileName: page.filename || null,
          });
          const pageTimer = invoiceDebugStart("lines analysis (order extract)", {
            page: `page ${i + 1}/${pages.length}`,
            key: page.key,
            order: match.order_key,
          });
          const ex = await fetch(
            `${SHIFTIQ_API}/api/procureiq/receipt-review/${encodeURIComponent(match.order_key)}/invoice-extract`,
            {
              method: "POST",
              headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
              body: JSON.stringify({
                unified_location_id: locationId,
                brand_id: brandId,
                invoice_s3_key: page.key,
                order_lines: json.lines || [],
                expected_supplier_name: json.supplier_name || match.supplier_name || "",
                supplier_id: json.supplier_id ?? null,
              }),
            }
          );
          if (!ex.ok) throw new Error(await procureiqApiError(ex, "Extract failed"));
          const draft = await ex.json();
          pageTimer.end({
            ok: true,
            lines: Array.isArray(draft?.draft_lines) ? draft.draft_lines.length : 0,
            server_ms: draft?.timings || null,
          });
          if (draft.supplier_profile) setSupplierProfile(draft.supplier_profile);
          pageDrafts.push({ draft, pageKey: page.key });
        }
        merged = mergePageDrafts(pageDrafts);
      }
      applyTimer.end({
        ok: true,
        lines: merged?.draft_lines?.length || 0,
        reused_scan_extract: reuseLines.length > 0,
      });
      if (!merged?.draft_lines?.length) {
        showToast?.(t("No lines extracted", "Δεν εξήχθησαν γραμμές"));
        return;
      }
      const vatInfo = vatInfoFromDraft(merged);
      if (vatInfo) setInvoiceVatInfo(vatInfo);
      setInvoiceExtractNote(
        t(
          `Read ${merged.draft_lines.length} line(s) from ${pages.length} page(s)`,
          `Διαβάστηκαν ${merged.draft_lines.length} γραμμές από ${pages.length} σελίδες`
        )
      );
      const quality = assessInvoiceDraftMatch(merged, (json.lines || []).length);
      if (quality.weak) {
        setPendingInvoiceDraft({
          draft: merged,
          quality,
          invoiceKey: pages[0]?.key || null,
          filename: pages[0]?.filename || "invoice",
          append: false,
        });
        showToast?.(
          t(
            "Invoice may not match this order — confirm before replacing lines",
            "Το τιμολόγιο μπορεί να μην ταιριάζει — επιβεβαιώστε πριν αντικατασταθούν οι γραμμές"
          )
        );
      } else {
        const orderBaseline = new Map();
        for (const ol of json.lines || []) {
          const id = ol.item_id != null ? String(ol.item_id) : "";
          if (!id) continue;
          orderBaseline.set(id, {
            ordered_qty: numOrNull(ol.ordered_qty ?? ol.qty),
            ordered_unit_price: numOrNull(ol.ordered_unit_price ?? ol.unit_price),
            product_name: ol.product_name,
          });
        }
        setEditLines(
          editLinesFromDraft(merged.draft_lines, orderBaseline, json).map((l) => ({
            ...l,
            page_key: l.page_key || null,
          }))
        );
        showToast?.(
          t(
            `Read ${merged.draft_lines.length} line(s) from ${pages.length} page(s) — review then Verify`,
            `Διαβάστηκαν ${merged.draft_lines.length} γραμμές από ${pages.length} σελίδες — έλεγχος και Verify`
          )
        );
      }
      setScanMatches(null);
      setScanSupplierKey(null);
      setScanExtract(null);
      setScanInvoiceKey(null);
      setScanFileName("");
      setScanSupplierProfile(null);
    } catch (err) {
      applyTimer.end({ ok: false, error: err?.message || String(err) });
      showToast?.(err?.message || "Error");
    } finally {
      setExtractBusy(false);
      setExtractProgress(null);
      setInvoiceJobProgress(null);
    }
  }

  async function handleInvoiceFile(file, meta = {}) {
    if (!detail?.order_key || !file || extractBusy) return;
    const priorPages = invoiceAttachments;
    const append = priorPages.length > 0;
    const previewUrl =
      invoicePreviewUrl && invoicePreviewOwnedRef.current
        ? invoicePreviewUrl
        : createInvoicePreviewUrl(file);
    // Ownership moves to the page entry (pending → final).
    if (previewUrl && previewUrl === invoicePreviewUrl) {
      invoicePreviewOwnedRef.current = false;
    }
    const pendingKey = `pending-${Date.now()}-${priorPages.length}`;
    const pendingPage = {
      key: pendingKey,
      filename: file?.name || "invoice",
      seq: priorPages.length,
      folder: null,
      previewUrl: previewUrl || null,
      previewOwned: Boolean(previewUrl),
      pending: true,
    };
    // Show sidebar + Add page immediately (before upload/extract finishes).
    const optimistic = [...priorPages, pendingPage];
    setInvoiceAttachments(optimistic);
    syncActiveInvoicePreview(optimistic, optimistic.length - 1);
    setExtractBusy(true);
    setExtractProgress({
      mode: "read",
      page: 1,
      total: 1,
      fileName: file?.name || null,
    });
    setInvoiceJobProgress({
      kind: "read",
      step: "upload",
      page: 1,
      pages: 1,
      index: 0,
      total: 2,
    });
    if (!append) {
      setInvoiceExtractNote(null);
      setInvoiceSupplierCheck(null);
    }
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("unified_location_id", String(locationId));
      if (locationName) form.append("location_name", String(locationName));
      if (detail.supplier_name) form.append("supplier_name", String(detail.supplier_name));
      if (detail.supy_order_number) {
        form.append("supy_order_number", String(detail.supy_order_number));
      }
      appendInvoiceTrainingSidecars(form, { ...meta, primaryFile: file });
      const upTimer = invoiceDebugStart("upload", {
        file: file?.name || null,
        bytes: file?.size ?? null,
        order: detail.order_key,
      });
      let uploaded;
      try {
        const up = await fetch(
          `${SHIFTIQ_API}/api/procureiq/receipt-review/${encodeURIComponent(detail.order_key)}/invoice-upload`,
          { method: "POST", headers: getAuthHeadersMultipart(), body: form }
        );
        if (!up.ok) throw new Error(await procureiqApiError(up, "Upload failed"));
        uploaded = await up.json();
        upTimer.end({ ok: true, key: uploaded?.key || null });
      } catch (err) {
        upTimer.end({ ok: false, error: err?.message || String(err) });
        throw err;
      }
      const page = {
        key: uploaded.key,
        filename: uploaded.filename || file.name,
        seq: uploaded.seq,
        folder: uploaded.folder,
        previewUrl: previewUrl || null,
        previewOwned: Boolean(previewUrl),
        pending: false,
      };
      const nextPages = [...priorPages, page];
      setInvoiceAttachments(nextPages);
      syncActiveInvoicePreview(nextPages, nextPages.length - 1);
      setInvoiceJobProgress({
        kind: "read",
        step: "extract",
        page: 1,
        pages: 1,
        index: 1,
        total: 2,
      });
      const extractTimer = invoiceDebugStart("lines analysis (order extract)", {
        key: uploaded.key,
        order: detail.order_key,
      });
      let draft;
      try {
        const ex = await fetch(
          `${SHIFTIQ_API}/api/procureiq/receipt-review/${encodeURIComponent(detail.order_key)}/invoice-extract`,
          {
            method: "POST",
            headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
            body: JSON.stringify({
              unified_location_id: locationId,
              brand_id: brandId,
              invoice_s3_key: uploaded.key,
              order_lines: detail.lines || [],
              expected_supplier_name: detail.supplier_name || "",
              supplier_id: detail.supplier_id ?? null,
            }),
          }
        );
        if (!ex.ok) throw new Error(await procureiqApiError(ex, "Extract failed"));
        draft = await ex.json();
        extractTimer.end({
          ok: true,
          lines: Array.isArray(draft?.draft_lines) ? draft.draft_lines.length : 0,
          server_ms: draft?.timings || null,
        });
      } catch (err) {
        extractTimer.end({ ok: false, error: err?.message || String(err) });
        throw err;
      }
      setInvoiceExtractNote(draft.note || null);
      setInvoiceSupplierCheck(draft.supplier_check || null);
      if (draft.supplier_profile) setSupplierProfile(draft.supplier_profile);
      const vatInfo = vatInfoFromDraft(draft);
      if (vatInfo) setInvoiceVatInfo(vatInfo);
      if (Array.isArray(draft.draft_lines) && draft.draft_lines.length) {
        const quality = assessInvoiceDraftMatch(draft, (detail.lines || []).length);
        // Extra pages: still warn on weak match, but confirm appends lines.
        if (quality.weak) {
          setPendingInvoiceDraft({
            draft,
            quality,
            invoiceKey: uploaded.key,
            filename: uploaded.filename || file.name,
            append,
          });
          showToast?.(
            append
              ? t(
                  "This page may not match — confirm before adding its lines",
                  "Αυτή η σελίδα μπορεί να μην ταιριάζει — επιβεβαιώστε πριν προστεθούν οι γραμμές"
                )
              : t(
                  "Invoice may not match this order — confirm before replacing lines",
                  "Το τιμολόγιο μπορεί να μην ταιριάζει — επιβεβαιώστε πριν αντικατασταθούν οι γραμμές"
                )
          );
        } else {
          applyInvoiceDraft(draft, { pageKey: uploaded.key, append });
        }
      } else {
        showToast?.(draft.note || t("No lines extracted", "Δεν εξήχθησαν γραμμές"));
      }
    } catch (err) {
      // Drop optimistic page; revoke preview only if we created it for this attempt.
      setInvoiceAttachments(priorPages);
      if (priorPages.length) {
        syncActiveInvoicePreview(priorPages, priorPages.length - 1);
      } else {
        setActiveInvoicePageIdx(0);
        setInvoiceFileName("");
        invoicePreviewOwnedRef.current = false;
        setInvoicePreviewUrl(null);
        if (previewUrl) {
          try {
            URL.revokeObjectURL(previewUrl);
          } catch {
            /* ignore */
          }
        }
      }
      showToast?.(err?.message || "Error");
      setInvoiceExtractNote(err?.message || "Extract failed");
    } finally {
      setExtractBusy(false);
      setExtractProgress(null);
      setInvoiceJobProgress(null);
    }
  }

  function toggleSupplierGroup(sectionKey, groupKey) {
    const id = `${sectionKey}:${groupKey}`;
    setExpandedSuppliers((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  function renderList(sectionKey, title, rows, empty) {
    const groups = groupOrdersBySupplier(rows);
    return (
      <div className="card-box procureiq-send-block" style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 10 }}>{title}</div>
        {!groups.length ? (
          <p style={{ fontSize: 13, color: "var(--text-muted)" }}>{empty}</p>
        ) : (
          groups.map((group) => {
            const name = procureiqDisplaySupplierName(group.supplier_name);
            const expandId = `${sectionKey}:${group.key}`;
            const isOpen = Boolean(expandedSuppliers[expandId]);
            const orderCount = group.orders.length;
            return (
              <div key={expandId} className="procureiq-supplier-card" style={{ marginBottom: 8 }}>
                <button
                  type="button"
                  className="procureiq-supplier-row"
                  style={{
                    width: "100%",
                    textAlign: "left",
                    cursor: "pointer",
                    border: "none",
                    background: "transparent",
                  }}
                  aria-expanded={isOpen}
                  onClick={() => toggleSupplierGroup(sectionKey, group.key)}
                >
                  <div
                    className="procureiq-supplier-avatar"
                    style={{ background: procureiqColorFromName(name) }}
                  >
                    {procureiqSupplierInitial(name)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 800, fontSize: 13 }}>{name}</div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
                      {orderCount}{" "}
                      {orderCount === 1
                        ? t("order", "παραγγελία")
                        : t("orders", "παραγγελίες")}
                      {group.total > 0 ? ` · ${formatProcureEuro(group.total)}` : ""}
                    </div>
                  </div>
                  <span className={`procureiq-chevron ${isOpen ? "is-open" : ""}`}>›</span>
                </button>
                {isOpen && (
                  <div className="procureiq-expanded-lines" style={{ paddingTop: 4, paddingBottom: 6 }}>
                    {group.orders.map((order) => (
                      <button
                        key={order.order_key}
                        type="button"
                        className="procureiq-supplier-row procureiq-supplier-row-readonly"
                        style={{
                          width: "100%",
                          textAlign: "left",
                          cursor: "pointer",
                          border: "none",
                          background: "transparent",
                          paddingLeft: 12,
                          paddingRight: 12,
                        }}
                        onClick={() => openDetail(order)}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: 12, color: "var(--text-primary)" }}>
                            <OpsOrderLink
                              orderKey={order.order_key}
                              orderId={order.supy_order_id}
                              orderNumber={order.supy_order_number}
                              locationId={locationId}
                              brandId={brandId}
                              style={{ marginRight: 6 }}
                            />
                            {t("Expected", "Αναμενόμενο")} {order.expected_delivery_date || "—"}
                          </div>
                          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 3 }}>
                            {t("Ordered", "Παραγγελία")} {String(order.submitted_at || "").slice(0, 10)}
                            {order.review_bucket
                              ? ` · ${reviewBucketLabel(order.review_bucket, t)}`
                              : order.status
                                ? ` · ${order.status}`
                                : ""}
                            {order.total_ordered != null
                              ? ` · ${t("ex VAT", "χωρίς ΦΠΑ")} ${formatProcureEuro(order.total_ordered)}`
                              : ""}
                          </div>
                        </div>
                        <div style={{ textAlign: "right", flexShrink: 0, marginRight: 8 }}>
                          <div style={{ fontWeight: 800, fontSize: 13, color: "var(--text-primary)" }}>
                            {order.ordered_total != null
                              ? formatProcureEuro(order.ordered_total)
                              : order.total_ordered != null
                                ? formatProcureEuro(order.total_ordered)
                                : "—"}
                          </div>
                          <div style={{ fontSize: 10, color: "var(--text-muted)", fontWeight: 600 }}>
                            {sectionKey === "orders" ? t("Open", "Άνοιγμα") : t("Review", "Έλεγχος")} ›
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    );
  }

  function renderInvoiceExtractBusy({ previewMaxH = 140 } = {}) {
    const progress = extractProgress;
    const matching = progress?.mode === "match-order";
    const page = progress?.page || 0;
    const total = progress?.total || 0;
    const title = matching
      ? t("Matching lines to this order…", "Αντιστοίχιση γραμμών με την παραγγελία…")
      : t("Reading invoice…", "Ανάγνωση τιμολογίου…");
    let subtitle;
    if (matching && page === 0) {
      subtitle = t("Opening order…", "Άνοιγμα παραγγελίας…");
    } else if (matching) {
      subtitle = t(
        "Linking scanned lines — almost done",
        "Σύνδεση σαρωμένων γραμμών — σχεδόν έτοιμο"
      );
    } else if (total > 1 && page > 0) {
      subtitle = t(`Page ${page} of ${total}`, `Σελίδα ${page} από ${total}`);
    } else {
      subtitle =
        progress?.fileName ||
        invoiceFileName ||
        t("This can take a few seconds", "Μπορεί να πάρει λίγα δευτερόλεπτα");
    }
    return (
      <div style={{ padding: "24px 8px", textAlign: "center" }}>
        {invoicePreviewUrl ? (
          <img
            src={invoicePreviewUrl}
            alt=""
            style={{
              maxWidth: "100%",
              maxHeight: previewMaxH,
              objectFit: "contain",
              borderRadius: 8,
              marginBottom: 12,
              opacity: 0.85,
            }}
          />
        ) : null}
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}>
          <LoadingPulse size={28} />
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>{title}</div>
        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>{subtitle}</div>
      </div>
    );
  }

  function invoiceJobOverlayCopy(job) {
    if (!job) return null;
    const pages = Math.max(1, Number(job.pages) || 1);
    const page = Math.max(1, Number(job.page) || 1);
    const index = Math.max(0, Number(job.index) || 0);
    const total = Math.max(1, Number(job.total) || 1);
    const progressPct = Math.min(99, Math.round(((index + 0.15) / total) * 100));

    if (job.kind === "inbox-match") {
      const reusedPages = job.reusedPages || {};
      const steps = [];
      for (let p = 1; p <= pages; p += 1) {
        const exIdx = p - 1;
        const saved = Boolean(reusedPages[p]);
        steps.push({
          label: saved
            ? t(`Saved lines · page ${p}`, `Αποθηκευμένες γραμμές · σελ. ${p}`)
            : t(`Read lines · page ${p}`, `Ανάγνωση γραμμών · σελ. ${p}`),
          status: index > exIdx ? "done" : index === exIdx ? "active" : "pending",
        });
      }
      const rankIdx = total - 1;
      steps.push({
        label: t("Match orders", "Αντιστοίχιση παραγγελιών"),
        status: index > rankIdx ? "done" : index === rankIdx ? "active" : "pending",
      });
      let title = t("Preparing invoice…", "Προετοιμασία τιμολογίου…");
      let subtitle = t("Please wait — do not leave this page", "Περιμένετε — μην φύγετε από τη σελίδα");
      if (job.step === "extract") {
        if (job.pageReused) {
          title = t(
            `Using saved lines · page ${page} of ${pages}`,
            `Χρήση αποθηκευμένων γραμμών · σελ. ${page} από ${pages}`
          );
          subtitle = t("Skipped re-read — already extracted", "Παράλειψη επανάληψης — ήδη εξαχθέντα");
        } else {
          title = t(`Reading page ${page} of ${pages}…`, `Ανάγνωση σελίδας ${page} από ${pages}…`);
          subtitle = t(
            "First read ~20–30s per page · then reused on Match",
            "Πρώτη ανάγνωση ~20–30δ/σελ. · μετά επαναχρησιμοποιείται στο Match"
          );
        }
      } else if (job.step === "rank") {
        title = t("Matching orders…", "Αντιστοίχιση παραγγελιών…");
        subtitle = t("Comparing invoice lines to recent orders", "Σύγκριση γραμμών με πρόσφατες παραγγελίες");
      }
      return { title, subtitle, progressPct, steps };
    }

    if (job.kind === "scan") {
      const steps = [];
      for (let p = 1; p <= pages; p += 1) {
        const upIdx = (p - 1) * 2;
        const exIdx = upIdx + 1;
        steps.push({
          label: t(`Upload page ${p}`, `Ανέβασμα σελίδας ${p}`),
          status: index > upIdx ? "done" : index === upIdx ? "active" : "pending",
        });
        steps.push({
          label: t(`Read lines · page ${p}`, `Ανάγνωση γραμμών · σελ. ${p}`),
          status: index > exIdx ? "done" : index === exIdx ? "active" : "pending",
        });
      }
      const rankIdx = total - 1;
      steps.push({
        label: t("Match orders", "Αντιστοίχιση παραγγελιών"),
        status: index > rankIdx ? "done" : index === rankIdx ? "active" : "pending",
      });
      let title = t("Building invoice…", "Δημιουργία τιμολογίου…");
      let subtitle = t("Please wait — do not leave this page", "Περιμένετε — μην φύγετε από τη σελίδα");
      if (job.step === "upload") {
        title = t(`Uploading page ${page} of ${pages}…`, `Ανέβασμα σελίδας ${page} από ${pages}…`);
      } else if (job.step === "extract") {
        title = t(`Reading page ${page} of ${pages}…`, `Ανάγνωση σελίδας ${page} από ${pages}…`);
        subtitle = t(
          "Line analysis can take ~20–30s per page · saved after first read",
          "Η ανάλυση γραμμών μπορεί να πάρει ~20–30δ ανά σελίδα · αποθηκεύεται μετά την πρώτη"
        );
      } else if (job.step === "rank") {
        title = t("Matching orders…", "Αντιστοίχιση παραγγελιών…");
        subtitle = t("Comparing invoice lines to recent orders", "Σύγκριση γραμμών με πρόσφατες παραγγελίες");
      }
      return { title, subtitle, progressPct, steps };
    }

    if (job.kind === "apply") {
      const steps = [
        {
          label: t("Open order", "Άνοιγμα παραγγελίας"),
          status: index > 0 ? "done" : "active",
        },
        {
          label: t("Link scanned lines", "Σύνδεση σαρωμένων γραμμών"),
          status: index >= 1 ? "active" : "pending",
        },
      ];
      return {
        title:
          job.step === "open"
            ? t("Opening order…", "Άνοιγμα παραγγελίας…")
            : t("Matching lines to this order…", "Αντιστοίχιση γραμμών με την παραγγελία…"),
        subtitle: t("Please wait — do not leave this page", "Περιμένετε — μην φύγετε από τη σελίδα"),
        progressPct,
        steps,
      };
    }

    // kind === "read" (upload into open order)
    const steps = [];
    for (let p = 1; p <= pages; p += 1) {
      const upIdx = (p - 1) * 2;
      const exIdx = upIdx + 1;
      steps.push({
        label: t(`Upload page ${p}`, `Ανέβασμα σελίδας ${p}`),
        status: index > upIdx ? "done" : index === upIdx ? "active" : "pending",
      });
      steps.push({
        label: t(`Read lines · page ${p}`, `Ανάγνωση γραμμών · σελ. ${p}`),
        status: index > exIdx ? "done" : index === exIdx ? "active" : "pending",
      });
    }
    return {
      title:
        job.step === "upload"
          ? t(`Uploading page ${page} of ${pages}…`, `Ανέβασμα σελίδας ${page} από ${pages}…`)
          : t(`Reading page ${page} of ${pages}…`, `Ανάγνωση σελίδας ${page} από ${pages}…`),
      subtitle:
        job.step === "extract"
          ? t(
              "Line analysis can take ~20–30s per page",
              "Η ανάλυση γραμμών μπορεί να πάρει ~20–30δ ανά σελίδα"
            )
          : t("Please wait — do not leave this page", "Περιμένετε — μην φύγετε από τη σελίδα"),
      progressPct,
      steps,
    };
  }

  if (loading && !data) return <LoadingPulse label={t("Loading…", "Φόρτωση…")} />;
  if (error) {
    return (
      <div className="card-box">
        <p style={{ color: "var(--danger, #b91c1c)" }}>{error}</p>
        <button type="button" className="btn-base btn-outline btn-small" onClick={load}>
          {t("Retry", "Ξανά")}
        </button>
      </div>
    );
  }

  const showVerifyActions = detail ? canVerifyReceipt(detail) : false;
  const dialogOrder = detail || selectedSummary;
  const showOnSupyHref = dialogOrder?.supy_order_id
    ? supyOrderUrl(dialogOrder.supy_order_id)
    : null;

  const scanSupplierGroups = groupScanMatchesBySupplier(scanMatches || []);
  const scanMultiSupplier = scanSupplierGroups.length > 1;
  const scanSelectedSupplierGroup = scanSupplierKey
    ? scanSupplierGroups.find((g) => g.key === scanSupplierKey) || null
    : null;
  // Multi-supplier → pick supplier first; single supplier (or after pick) → orders.
  const scanShowSupplierStep = Boolean(
    scanMatches?.length && scanMultiSupplier && !scanSelectedSupplierGroup
  );
  const scanOrderMatches = (() => {
    const raw = scanSelectedSupplierGroup
      ? scanSelectedSupplierGroup.matches
      : scanMultiSupplier
        ? []
        : scanMatches || [];
    return [...raw].sort(
      (a, b) => (Number(b.score) || 0) - (Number(a.score) || 0)
    );
  })();
  const scanTopMatchAlreadyReceived =
    scanOrderMatches.length > 0 && matchLooksAlreadyReceived(scanOrderMatches[0]);
  const dialogAlreadyReceived = orderLooksAlreadyReceived(dialogOrder || detail);

  const invoiceOverlay = invoiceJobOverlayCopy(invoiceJobProgress);

  return (
    <div>
      <div className="procureiq-tabs" style={{ marginBottom: 12 }}>
        <button
          type="button"
          className={`procureiq-tab ${activeTab === "upload" ? "procureiq-tab-active" : ""}`}
          onClick={() => setActiveTab("upload")}
        >
          {t(
            `Upload (${inboxItems.length})`,
            `Μεταφόρτωση (${inboxItems.length})`
          )}
        </button>
        <button
          type="button"
          className={`procureiq-tab ${activeTab === "open" ? "procureiq-tab-active" : ""}`}
          onClick={() => setActiveTab("open")}
        >
          {t(`Open (${data?.open_due_count || 0})`, `Εκκρεμείς (${data?.open_due_count || 0})`)}
        </button>
        <button
          type="button"
          className={`procureiq-tab ${activeTab === "orders" ? "procureiq-tab-active" : ""}`}
          onClick={() => setActiveTab("orders")}
        >
          {t(`Orders (${data?.orders_count || 0})`, `Παραγγελίες (${data?.orders_count || 0})`)}
        </button>
      </div>

      {activeTab !== "upload" ? (
        <div
          className="card-box"
          style={{
            marginBottom: 14,
            padding: "12px 14px",
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-secondary)" }}>
            {t("Ordered between", "Παραγγελίες μεταξύ")}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {DATE_PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`filter-chip ${datePreset === p.id ? "chip-active" : ""}`}
                onClick={() => applyPreset(p.id)}
              >
                {lang === "gr" ? p.gr : p.en}
              </button>
            ))}
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
              alignItems: "end",
            }}
          >
            <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, fontWeight: 600, color: "var(--text-muted)" }}>
              {t("From", "Από")}
              <input
                type="date"
                className="form-input"
                value={fromDate}
                max={toDate || undefined}
                onChange={(e) => onFromDateChange(e.target.value)}
                style={{ fontSize: 13, fontWeight: 600 }}
              />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, fontWeight: 600, color: "var(--text-muted)" }}>
              {t("To", "Έως")}
              <input
                type="date"
                className="form-input"
                value={toDate}
                min={fromDate || undefined}
                max={isoDateLocal()}
                onChange={(e) => onToDateChange(e.target.value)}
                style={{ fontSize: 13, fontWeight: 600 }}
              />
            </label>
          </div>
          {loading && data ? (
            <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{t("Updating…", "Ενημέρωση…")}</div>
          ) : null}
        </div>
      ) : null}

      {activeTab === "upload" ? (
        <>
          <div className="card-box" style={{ marginBottom: 12, padding: "14px 14px" }}>
            <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 6 }}>
              {t("Upload invoice photos", "Ανέβασμα φωτο τιμολογίου")}
            </div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12, lineHeight: 1.45 }}>
              {t(
                "Take a clear photo of each page (or drop a PDF). You’ll crop, straighten, and enhance — then we save it. Matching to the order comes later.",
                "Βγάλτε καθαρή φωτο κάθε σελίδας (ή αφήστε PDF). Θα την κόψετε, ισιώσετε και βελτιώσετε — μετά αποθηκεύεται. Η αντιστοίχιση με παραγγελία γίνεται αργότερα."
              )}
            </div>
            <div
              className="photo-upload-zone"
              role="button"
              tabIndex={inboxBusy || extractBusy || scanBusy ? -1 : 0}
              style={{
                borderColor: inboxDragOver ? "var(--green)" : undefined,
                background: inboxDragOver ? "var(--green-light)" : undefined,
                opacity: inboxBusy ? 0.7 : 1,
                pointerEvents: inboxBusy || extractBusy || scanBusy ? "none" : undefined,
                padding: "20px 14px",
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setInboxDragOver(true);
              }}
              onDragLeave={() => setInboxDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setInboxDragOver(false);
                acceptInboxFile(e.dataTransfer.files?.[0]);
              }}
              onClick={async () => {
                if (inboxBusy || extractBusy || scanBusy) return;
                const file = await pickInvoiceUploadFile();
                if (file) acceptInboxFile(file);
              }}
              onKeyDown={async (e) => {
                if (e.key !== "Enter" && e.key !== " ") return;
                e.preventDefault();
                if (inboxBusy || extractBusy || scanBusy) return;
                const file = await pickInvoiceUploadFile();
                if (file) acceptInboxFile(file);
              }}
            >
              {inboxBusy ? (
                <>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>
                    {t("Uploading pages…", "Ανέβασμα σελίδων…")}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
                    {t("Please wait", "Περιμένετε")}
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>
                    {t("Drop PDF or photo — or click to browse", "Αφήστε PDF ή φωτο — ή κλικ για επιλογή")}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
                    {t(
                      "Photos open the crop tool (corners → straighten → enhance → add pages). PDFs upload as-is.",
                      "Οι φωτο ανοίγουν το εργαλείο κοπής (γωνίες → ίσιωμα → βελτίωση → προσθήκη σελίδων). Τα PDF ανεβαίνουν όπως είναι."
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="card-box" style={{ padding: "12px 14px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                marginBottom: 10,
              }}
            >
              <div style={{ fontWeight: 800, fontSize: 13 }}>
                {t("Waiting for match", "Αναμονή αντιστοίχισης")}
              </div>
              <button
                type="button"
                className="btn-base btn-outline btn-small"
                disabled={inboxLoading || inboxBusy}
                onClick={() => loadInbox()}
              >
                {inboxLoading ? t("Refreshing…", "Ανανέωση…") : t("Refresh", "Ανανέωση")}
              </button>
            </div>
            {inboxLoading && !inboxItems.length ? (
              <LoadingPulse label={t("Loading uploads…", "Φόρτωση μεταφορτώσεων…")} />
            ) : null}
            {!inboxLoading && !inboxItems.length ? (
              <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0 }}>
                {t(
                  "No pending uploads for this store yet.",
                  "Δεν υπάρχουν ακόμα εκκρεμείς μεταφορτώσεις για το κατάστημα."
                )}
              </p>
            ) : null}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {inboxItems.map((item) => {
                const first = item.pages?.[0] || null;
                const thumb =
                  first?.previewKind !== "pdf"
                    ? first?.url || first?.previewUrl || null
                    : null;
                const isPdf =
                  first?.previewKind === "pdf" ||
                  looksLikeInvoicePdfKey(first?.key, first?.filename, first?.content_type);
                const pageN = item.page_count || item.pages?.length || 0;
                const busy = inboxActionId === item.id || inboxPreviewLoading;
                const when = item.uploaded_at
                  ? new Date(item.uploaded_at).toLocaleString(
                      lang === "gr" ? "el-GR" : "en-GB",
                      { dateStyle: "short", timeStyle: "short" }
                    )
                  : "";
                return (
                  <div
                    key={item.id}
                    style={{
                      display: "flex",
                      gap: 12,
                      alignItems: "stretch",
                      padding: "10px 0",
                      borderTop: "1px solid var(--border, #e8e8e8)",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => openInboxPreview(item, { forceRefresh: true })}
                      disabled={busy || scanBusy || inboxBusy}
                      title={t("Preview", "Προεπισκόπηση")}
                      style={{
                        width: 72,
                        height: 72,
                        borderRadius: 8,
                        background: "var(--surface, #f5f5f5)",
                        overflow: "hidden",
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "1px solid var(--border, #e5e7eb)",
                        padding: 0,
                        cursor: busy || scanBusy || inboxBusy ? "default" : "zoom-in",
                      }}
                    >
                      {thumb ? (
                        <img
                          src={thumb}
                          alt=""
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          onError={() => {
                            // Blob was revoked (e.g. after order dialog) — refresh thumbs.
                            void repairInboxItemPreviews(item);
                          }}
                        />
                      ) : (
                        <span style={{ fontSize: 11, fontWeight: 800, color: "var(--text-muted)" }}>
                          {isPdf ? "PDF" : "…"}
                        </span>
                      )}
                    </button>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 13 }}>
                        {t(`${pageN} page(s)`, `${pageN} σελίδ(ες)`)}
                        {isPdf ? " · PDF" : ""}
                        {item.lines_cached ||
                        (item.pages || []).every((p) => p.extract_cached) ? (
                          <span
                            style={{
                              marginLeft: 8,
                              fontSize: 10,
                              fontWeight: 700,
                              color: "var(--green, #2d6a4f)",
                              background: "var(--green-light, #e8f5e9)",
                              borderRadius: 999,
                              padding: "2px 8px",
                            }}
                          >
                            {t("Lines saved", "Γραμμές αποθηκευμένες")}
                          </span>
                        ) : null}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                        {item.uploaded_by || "—"}
                        {when ? ` · ${when}` : ""}
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                        <button
                          type="button"
                          className="btn-base btn-green btn-small"
                          disabled={busy || scanBusy || inboxBusy}
                          onClick={() => openInboxPreview(item, { forceRefresh: true })}
                        >
                          {busy && inboxPreviewLoading
                            ? t("Opening…", "Άνοιγμα…")
                            : t("Preview", "Προεπισκόπηση")}
                        </button>
                        {isPdf ? (
                          <button
                            type="button"
                            className="btn-base btn-outline btn-small"
                            disabled={busy || scanBusy || inboxBusy}
                            onClick={() => openInvoicePageInNewTab(first)}
                          >
                            {t("Open PDF", "Άνοιγμα PDF")} ↗
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="btn-base btn-outline btn-small"
                          disabled={busy || scanBusy || inboxBusy}
                          onClick={() => dismissInboxItem(item)}
                        >
                          {t("Dismiss", "Απόρριψη")}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {(inboxPreviewItem || inboxPreviewLoading) && (
            <div
              className="modal-overlay"
              onClick={() => {
                if (!inboxPreviewLoading) closeInboxPreview();
              }}
            >
              <div
                className="card-box"
                style={{
                  maxWidth: 720,
                  width: "96%",
                  maxHeight: "92vh",
                  overflow: "auto",
                  margin: "16px auto",
                  position: "relative",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 12,
                    marginBottom: 10,
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 16 }}>
                      {t("Review upload", "Έλεγχος μεταφόρτωσης")}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>
                      {inboxPreviewItem?.lines_cached ||
                      (inboxPreviewItem?.pages || []).every((p) => p?.extract_cached)
                        ? t(
                            "Lines already saved — Match will reuse them (no re-read).",
                            "Οι γραμμές είναι αποθηκευμένες — το Match τις επαναχρησιμοποιεί."
                          )
                        : t(
                            "Check the pages, then match to an order — first Match reads lines (~20–30s/page); later Matches reuse them.",
                            "Ελέγξτε τις σελίδες και μετά Match — η πρώτη φορά διαβάζει γραμμές (~20–30δ/σελ.), οι επόμενες τις επαναχρησιμοποιούν."
                          )}
                    </div>
                  </div>
                  <button
                    type="button"
                    aria-label={t("Close", "Κλείσιμο")}
                    disabled={inboxPreviewLoading}
                    onClick={closeInboxPreview}
                    style={{
                      border: "none",
                      background: "none",
                      cursor: "pointer",
                      color: "var(--text-muted)",
                      fontSize: 18,
                      lineHeight: 1,
                      padding: "2px 4px",
                      flexShrink: 0,
                    }}
                  >
                    ✕
                  </button>
                </div>

                {inboxPreviewLoading || !inboxPreviewItem ? (
                  <LoadingPulse label={t("Loading preview…", "Φόρτωση προεπισκόπησης…")} />
                ) : (
                  <>
                    {(() => {
                      const pages = inboxPreviewItem.pages || [];
                      const page =
                        pages[
                          Math.max(
                            0,
                            Math.min(inboxPreviewPageIdx, Math.max(0, pages.length - 1))
                          )
                        ] || null;
                      const isPdf = page?.previewKind === "pdf";
                      return (
                        <>
                          {pages.length > 1 ? (
                            <div
                              style={{
                                display: "flex",
                                gap: 8,
                                overflowX: "auto",
                                paddingBottom: 8,
                                marginBottom: 10,
                                WebkitOverflowScrolling: "touch",
                                touchAction: "pan-x pan-y",
                              }}
                            >
                              {pages.map((p, idx) => {
                                const active = idx === inboxPreviewPageIdx;
                                return (
                                  <button
                                    key={p.key || `inbox-prev-${idx}`}
                                    type="button"
                                    onClick={() => setInboxPreviewPageIdx(idx)}
                                    style={{
                                      flex: "0 0 72px",
                                      width: 72,
                                      borderRadius: 8,
                                      border: active
                                        ? "2px solid var(--green)"
                                        : "1px solid var(--border, #e5e7eb)",
                                      background: "var(--surface, #fff)",
                                      padding: 0,
                                      overflow: "hidden",
                                      cursor: "pointer",
                                    }}
                                  >
                                    {p.previewKind !== "pdf" && p.previewUrl ? (
                                      <img
                                        src={p.previewUrl}
                                        alt=""
                                        style={{
                                          width: "100%",
                                          height: 64,
                                          objectFit: "cover",
                                          display: "block",
                                        }}
                                      />
                                    ) : (
                                      <div
                                        style={{
                                          height: 64,
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          fontSize: 11,
                                          fontWeight: 800,
                                          color: "var(--text-muted)",
                                        }}
                                      >
                                        PDF
                                      </div>
                                    )}
                                    <div
                                      style={{
                                        fontSize: 10,
                                        fontWeight: 700,
                                        padding: "3px 4px",
                                        color: active ? "var(--green)" : "var(--text-primary)",
                                      }}
                                    >
                                      {t(`Page ${idx + 1}`, `Σελ. ${idx + 1}`)}
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          ) : null}

                          {isPdf && (page?.previewUrl || page?.url) ? (
                            <>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "flex-end",
                                  marginBottom: 8,
                                }}
                              >
                                <button
                                  type="button"
                                  className="btn-base btn-outline btn-small"
                                  onClick={() => openInvoicePageInNewTab(page)}
                                >
                                  {t("Open PDF in new tab", "Άνοιγμα PDF σε νέα καρτέλα")} ↗
                                </button>
                              </div>
                              <iframe
                                title={page.filename || "invoice.pdf"}
                                src={page.previewUrl || page.url}
                                style={{
                                  width: "100%",
                                  height: "min(70vh, 560px)",
                                  border: "1px solid var(--border, #e5e7eb)",
                                  borderRadius: 12,
                                  background: "#fff",
                                }}
                              />
                            </>
                          ) : page?.previewUrl ? (
                            <button
                              type="button"
                              onClick={() =>
                                setInvoiceLightbox({
                                  url: page.previewUrl,
                                  label:
                                    page.filename ||
                                    t(
                                      `Page ${inboxPreviewPageIdx + 1}`,
                                      `Σελίδα ${inboxPreviewPageIdx + 1}`
                                    ),
                                })
                              }
                              style={{
                                display: "block",
                                width: "100%",
                                border: "none",
                                padding: 0,
                                background: "transparent",
                                cursor: "zoom-in",
                              }}
                            >
                              <img
                                src={page.previewUrl}
                                alt={page.filename || "invoice"}
                                style={{
                                  width: "100%",
                                  maxHeight: "min(70vh, 560px)",
                                  objectFit: "contain",
                                  display: "block",
                                  borderRadius: 12,
                                  border: "1px solid var(--border, #e5e7eb)",
                                  background: "var(--surface, #fff)",
                                }}
                              />
                            </button>
                          ) : (
                            <div
                              style={{
                                padding: "28px 12px",
                                textAlign: "center",
                                color: "var(--text-muted)",
                                fontSize: 13,
                                fontWeight: 600,
                              }}
                            >
                              {t(
                                "Preview unavailable for this file.",
                                "Δεν υπάρχει προεπισκόπηση για αυτό το αρχείο."
                              )}
                            </div>
                          )}

                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 8,
                              marginTop: 14,
                              justifyContent: "flex-end",
                            }}
                          >
                            {(page?.previewUrl || page?.url || page?.key) && (
                              <button
                                type="button"
                                className="btn-base btn-outline"
                                onClick={() => openInvoicePageInNewTab(page)}
                              >
                                {isPdf
                                  ? t("Open PDF in new tab", "Άνοιγμα PDF σε νέα καρτέλα")
                                  : t("Open in new tab", "Άνοιγμα σε νέα καρτέλα")}{" "}
                                ↗
                              </button>
                            )}
                            <button
                              type="button"
                              className="btn-base btn-outline"
                              onClick={closeInboxPreview}
                            >
                              {t("Back", "Πίσω")}
                            </button>
                            <button
                              type="button"
                              className="btn-base btn-green"
                              disabled={scanBusy || inboxBusy}
                              onClick={() => matchInboxItemFromPreview()}
                            >
                              {t("Match to order", "Αντιστοίχιση σε παραγγελία")}
                            </button>
                          </div>
                        </>
                      );
                    })()}
                  </>
                )}
              </div>
            </div>
          )}
        </>
      ) : activeTab === "open" ? (
        <>
          <div
            className="card-box"
            style={{ marginBottom: 12, padding: "12px 14px" }}
          >
            <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 8 }}>
              {t("Scan invoice to find order", "Σάρωση τιμολογίου για εύρεση παραγγελίας")}
            </div>
            {(() => {
              const scanReady =
                !scanBusy &&
                (scanMatches != null ||
                  scanExtract != null ||
                  scanBuiltPages.length > 0);
              if (scanReady) {
                const activePage =
                  scanBuiltPages[
                    Math.max(
                      0,
                      Math.min(activeScanPageIdx, Math.max(0, scanBuiltPages.length - 1))
                    )
                  ] || null;
                const mainUrl = activePage?.previewUrl || scanPreviewUrl || null;
                const mainIsPdf =
                  activePage?.previewKind === "pdf" ||
                  looksLikeInvoicePdfKey(
                    activePage?.key,
                    activePage?.filename,
                    activePage?.content_type
                  );
                return (
                  <div
                    style={{
                      border: "1.5px solid var(--border, #e5e7eb)",
                      borderRadius: 14,
                      background: "var(--surface2, #f8faf8)",
                      padding: "12px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 800,
                        color: "var(--green)",
                        marginBottom: 8,
                      }}
                    >
                      {scanMatches != null
                        ? scanShowSupplierStep
                          ? t(
                              "Invoice ready — choose the supplier first",
                              "Τιμολόγιο έτοιμο — επιλέξτε πρώτα τον προμηθευτή"
                            )
                          : t(
                              "Invoice ready — pick an order below",
                              "Τιμολόγιο έτοιμο — επιλέξτε παραγγελία κάτω"
                            )
                        : t("Invoice attached", "Το τιμολόγιο επισυνάφθηκε")}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "var(--text-muted)",
                        marginBottom: 10,
                      }}
                    >
                      {t(
                        `${scanBuiltPages.length || 1} page(s) · ${scanExtract?.draft_lines?.length || 0} lines · tap a page to enlarge`,
                        `${scanBuiltPages.length || 1} σελίδ(ες) · ${scanExtract?.draft_lines?.length || 0} γραμμές · πατήστε σελίδα για μεγέθυνση`
                      )}
                    </div>

                    {scanBuiltPages.length > 1 ? (
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          overflowX: "auto",
                          paddingBottom: 8,
                          marginBottom: 8,
                          WebkitOverflowScrolling: "touch",
                          touchAction: "pan-x pan-y",
                        }}
                      >
                        {scanBuiltPages.map((page, pIdx) => {
                          const active = pIdx === activeScanPageIdx;
                          return (
                            <button
                              key={page.key || `scan-page-${pIdx}`}
                              type="button"
                              onClick={() => selectScanPreviewPage(pIdx)}
                              onDoubleClick={() =>
                                selectScanPreviewPage(pIdx, { openLightbox: true })
                              }
                              title={
                                page.filename ||
                                t(`Page ${pIdx + 1}`, `Σελίδα ${pIdx + 1}`)
                              }
                              style={{
                                flex: "0 0 72px",
                                width: 72,
                                borderRadius: 8,
                                border: active
                                  ? "2px solid var(--green)"
                                  : "1px solid var(--border, #e5e7eb)",
                                background: "var(--surface, #fff)",
                                padding: 0,
                                overflow: "hidden",
                                cursor: "pointer",
                              }}
                            >
                              {page.previewUrl ? (
                                <img
                                  src={page.previewUrl}
                                  alt=""
                                  style={{
                                    width: "100%",
                                    height: 64,
                                    objectFit: "cover",
                                    display: "block",
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    height: 64,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: 10,
                                    fontWeight: 700,
                                    color: "var(--text-muted)",
                                  }}
                                >
                                  {looksLikeInvoiceImageKey(
                                    page.key,
                                    page.filename,
                                    page.content_type
                                  )
                                    ? "…"
                                    : "PDF"}
                                </div>
                              )}
                              <div
                                style={{
                                  fontSize: 10,
                                  fontWeight: 700,
                                  padding: "3px 4px",
                                  color: active ? "var(--green)" : "var(--text-primary)",
                                }}
                              >
                                {t(`Page ${pIdx + 1}`, `Σελ. ${pIdx + 1}`)}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    ) : null}

                    {mainUrl ? (
                      <button
                        type="button"
                        className="photo-preview-wrap"
                        onClick={() =>
                          selectScanPreviewPage(activeScanPageIdx, {
                            openLightbox: true,
                          })
                        }
                        style={{
                          display: "block",
                          width: "100%",
                          border: "none",
                          padding: 0,
                          background: "transparent",
                          cursor: "zoom-in",
                          marginBottom: 8,
                        }}
                        title={t("Tap to enlarge", "Πατήστε για μεγέθυνση")}
                      >
                        <img
                          src={mainUrl}
                          alt={
                            activePage?.filename ||
                            scanFileName ||
                            t("Invoice page", "Σελίδα τιμολογίου")
                          }
                          style={{
                            width: "100%",
                            maxHeight: 320,
                            objectFit: "contain",
                            display: "block",
                            borderRadius: 12,
                            background: "var(--surface, #fff)",
                            border: "1px solid var(--border, #e5e7eb)",
                          }}
                        />
                      </button>
                    ) : (
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: "var(--text-primary)",
                          marginBottom: 8,
                          wordBreak: "break-all",
                        }}
                      >
                        {activePage?.filename ||
                          scanFileName ||
                          t("Invoice", "Τιμολόγιο")}
                      </div>
                    )}

                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 8,
                        alignItems: "center",
                      }}
                    >
                      {(mainUrl || activePage?.key) && (
                        <button
                          type="button"
                          className="btn-base btn-outline btn-small"
                          onClick={() =>
                            openInvoicePageInNewTab(
                              activePage || {
                                previewUrl: mainUrl,
                                filename: scanFileName || "invoice.pdf",
                              }
                            )
                          }
                        >
                          {mainIsPdf
                            ? t("Open PDF in new tab", "Άνοιγμα PDF σε νέα καρτέλα")
                            : t("Open in new tab", "Άνοιγμα σε νέα καρτέλα")}{" "}
                          ↗
                        </button>
                      )}
                      {mainUrl && !mainIsPdf ? (
                        <button
                          type="button"
                          className="btn-base btn-outline btn-small"
                          onClick={() =>
                            selectScanPreviewPage(activeScanPageIdx, {
                              openLightbox: true,
                            })
                          }
                        >
                          {t("Enlarge", "Μεγέθυνση")}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="btn-base btn-outline btn-small"
                        disabled={scanBusy || extractBusy}
                        onClick={async () => {
                          const file = await pickInvoiceUploadFile();
                          if (file) acceptScanFile(file);
                        }}
                      >
                        {t("Replace invoice", "Αντικατάσταση τιμολογίου")}
                      </button>
                    </div>
                  </div>
                );
              }

              return (
                <div
                  className="photo-upload-zone"
                  role="button"
                  tabIndex={scanBusy || extractBusy ? -1 : 0}
                  style={{
                    borderColor: scanDragOver ? "var(--green)" : undefined,
                    background: scanDragOver ? "var(--green-light)" : undefined,
                    opacity: scanBusy ? 0.7 : 1,
                    pointerEvents: scanBusy || extractBusy ? "none" : undefined,
                    padding: "16px 12px",
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setScanDragOver(true);
                  }}
                  onDragLeave={() => setScanDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setScanDragOver(false);
                    acceptScanFile(e.dataTransfer.files?.[0]);
                  }}
                  onClick={async () => {
                    if (scanBusy || extractBusy) return;
                    const file = await pickInvoiceUploadFile();
                    if (file) acceptScanFile(file);
                  }}
                  onKeyDown={async (e) => {
                    if (e.key !== "Enter" && e.key !== " ") return;
                    e.preventDefault();
                    if (scanBusy || extractBusy) return;
                    const file = await pickInvoiceUploadFile();
                    if (file) acceptScanFile(file);
                  }}
                >
                  {scanBusy ? (
                    <>
                      {scanPreviewUrl ? (
                        <img
                          src={scanPreviewUrl}
                          alt=""
                          style={{
                            maxWidth: "100%",
                            maxHeight: 140,
                            objectFit: "contain",
                            borderRadius: 8,
                            marginBottom: 8,
                            opacity: 0.85,
                          }}
                        />
                      ) : null}
                      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>
                        {scanPhase === "match" || scanPhase === "rank"
                          ? t("Building invoice & matching…", "Δημιουργία τιμολογίου & αντιστοίχιση…")
                          : scanPhase === "extract"
                            ? t("Reading invoice…", "Ανάγνωση τιμολογίου…")
                            : t("Uploading pages…", "Ανέβασμα σελίδων…")}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
                        {scanBuiltPages.length
                          ? t(
                              `Page ${scanBuiltPages.length}…`,
                              `Σελίδα ${scanBuiltPages.length}…`
                            )
                          : scanFileName || t("Please wait", "Περιμένετε")}
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>
                        {t("Drop PDF or photo — or click to browse", "Αφήστε PDF ή φωτο — ή κλικ για επιλογή")}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
                        {t(
                          "All files are listed (no image filter). Open Downloads/Desktop if today’s photos aren’t in Recents.",
                          "Εμφανίζονται όλα τα αρχεία (χωρίς φίλτρο εικόνας). Ανοίξτε Λήψεις/Επιφάνεια αν δεν φαίνονται οι σημερινές φωτο στα Πρόσφατα."
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })()}
            {scanExtract || scanMatches != null ? (
              <div style={{ marginTop: 12 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 8,
                    marginBottom: 8,
                  }}
                >
                  <div style={{ fontSize: 12, color: "var(--text-muted)", minWidth: 0 }}>
                    {scanExtract?.supplier_name ? (
                      <div style={{ fontWeight: 700, color: "var(--text-primary)" }}>
                        {procureiqDisplaySupplierName(scanExtract.supplier_name)}
                        {scanSupplierProfile?.sku_mapping_count ? (
                          <span
                            style={{
                              marginLeft: 6,
                              fontSize: 10,
                              fontWeight: 700,
                              color: "var(--green)",
                            }}
                            title={t(
                              "Supplier codes already learned from earlier verified invoices",
                              "Κωδικοί προμηθευτή που έχουν ήδη μαθευτεί από προηγούμενα τιμολόγια"
                            )}
                          >
                            {scanSupplierProfile.sku_mapping_count}{" "}
                            {t("known codes", "γνωστοί κωδικοί")}
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                    <div style={{ fontWeight: 700, color: "var(--text-primary)", marginTop: 2 }}>
                      {t("Invoice date", "Ημ/νία τιμολογίου")}{" "}
                      {scanExtract?.invoice_date ||
                        t("not detected", "δεν εντοπίστηκε")}
                    </div>
                    <div>
                      {scanExtract?.note ||
                        t(
                          `${scanExtract?.extract_count || 0} line(s) read`,
                          `${scanExtract?.extract_count || 0} γραμμές`
                        )}
                    </div>
                    {scanExtract?.extract_audit_key ? (
                      <div style={{ marginTop: 2, fontSize: 10 }}>
                        {t("OCR audit saved", "Αποθηκεύτηκε OCR audit")}
                      </div>
                    ) : null}
                    {(scanExtract?.vat_rate_pct != null ||
                      scanExtract?.total_net != null ||
                      scanExtract?.total_gross != null) && (
                      <div style={{ marginTop: 4, fontSize: 11 }}>
                        {scanExtract.total_net != null
                          ? `${t("Net", "Καθαρή")} €${Number(scanExtract.total_net).toFixed(2)}`
                          : null}
                        {scanExtract.vat_rate_pct != null
                          ? ` · ΦΠΑ ${Number(scanExtract.vat_rate_pct).toFixed(0)}%`
                          : null}
                        {scanExtract.total_gross != null
                          ? ` · ${t("Gross", "Πληρωτέο")} €${Number(scanExtract.total_gross).toFixed(2)}`
                          : null}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn-base btn-outline btn-small"
                    onClick={clearScanMatchResults}
                    disabled={scanBusy || extractBusy}
                  >
                    {t("Clear", "Καθαρισμός")}
                  </button>
                </div>
                {Array.isArray(scanExtract?.draft_lines) &&
                scanExtract.draft_lines.length > 0 ? (
                  <div
                    style={{
                      marginBottom: 10,
                      border: "1px solid var(--border, #e5e7eb)",
                      borderRadius: 10,
                      overflow: "hidden",
                      background: "var(--surface, #fff)",
                    }}
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 56px 64px",
                        gap: 6,
                        padding: "6px 10px",
                        fontSize: 10,
                        fontWeight: 700,
                        color: "var(--text-muted)",
                        background: "var(--bg-soft, #f9fafb)",
                        borderBottom: "1px solid var(--border, #e5e7eb)",
                      }}
                    >
                      <span>{t("Invoice line", "Γραμμή τιμολογίου")}</span>
                      <span style={{ textAlign: "right" }}>{t("Qty", "Ποσ.")}</span>
                      <span style={{ textAlign: "right" }}>{t("Price", "Τιμή")}</span>
                    </div>
                    {draftLinesInInvoiceOrder(scanExtract.draft_lines)
                      .slice(0, 8)
                      .map((ln, idx) => {
                      const name =
                        ln.product_name ||
                        ln.invoice_product_name ||
                        ln.name ||
                        "—";
                      const qty =
                        ln.qty != null && Number.isFinite(Number(ln.qty))
                          ? Number(ln.qty)
                          : null;
                      const price =
                        ln.unit_price != null && Number.isFinite(Number(ln.unit_price))
                          ? Number(ln.unit_price)
                          : null;
                      return (
                        <div
                          key={`scan-line-${idx}`}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 56px 64px",
                            gap: 6,
                            padding: "7px 10px",
                            fontSize: 11,
                            borderTop:
                              idx === 0 ? "none" : "1px solid var(--border, #f3f4f6)",
                            color: "var(--text-primary)",
                          }}
                        >
                          <span
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              minWidth: 0,
                            }}
                            title={name}
                          >
                            {ln.supplier_sku ? (
                              <span
                                style={{
                                  fontFamily:
                                    "ui-monospace, SFMono-Regular, Menlo, monospace",
                                  fontSize: 10,
                                  fontWeight: 700,
                                  color: "var(--text-muted)",
                                  marginRight: 6,
                                }}
                              >
                                {ln.supplier_sku}
                              </span>
                            ) : null}
                            {procureiqDisplayProductName(name)}
                          </span>
                          <span style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                            {qty != null ? qty.toLocaleString(undefined, { maximumFractionDigits: 3 }) : "—"}
                          </span>
                          <span style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                            {price != null ? `€${price.toFixed(2)}` : "—"}
                          </span>
                        </div>
                      );
                    })}
                    {scanExtract.draft_lines.length > 8 ? (
                      <div
                        style={{
                          padding: "6px 10px",
                          fontSize: 10,
                          color: "var(--text-muted)",
                          borderTop: "1px solid var(--border, #f3f4f6)",
                        }}
                      >
                        {t(
                          `+${scanExtract.draft_lines.length - 8} more line(s)`,
                          `+${scanExtract.draft_lines.length - 8} ακόμη`
                        )}
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {scanMatches && scanMatches.length === 0 ? (
                  <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0 }}>
                    {t(
                      "No candidates above the score threshold — open an order from the lists below.",
                      "Κανένας υποψήφιος πάνω από το όριο — ανοίξτε παραγγελία από τις λίστες κάτω."
                    )}
                  </p>
                ) : null}
                {scanMatches && scanMatches.length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {scanTopMatchAlreadyReceived && !scanShowSupplierStep ? (
                      <div
                        role="status"
                        style={{
                          padding: "10px 12px",
                          borderRadius: 10,
                          border: "1.5px solid #F59E0B",
                          background: "#FFFBEB",
                          color: "#92400E",
                          fontSize: 12,
                          fontWeight: 700,
                          lineHeight: 1.4,
                        }}
                      >
                        {alreadyReceivedBannerCopy(t, {
                          orderLabel:
                            scanOrderMatches[0]?.supy_order_number ||
                            scanOrderMatches[0]?.supy_order_id,
                        })}
                      </div>
                    ) : null}
                    {scanShowSupplierStep ? (
                      <>
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: "var(--text-muted)",
                            marginBottom: 2,
                          }}
                        >
                          {t(
                            `1 · Choose supplier (${scanSupplierGroups.length})`,
                            `1 · Επιλογή προμηθευτή (${scanSupplierGroups.length})`
                          )}
                        </div>
                        {scanSupplierGroups.map((g, gIdx) => {
                          const isTop = gIdx === 0;
                          return (
                          <button
                            key={g.key}
                            type="button"
                            className="procureiq-supplier-row"
                            style={{
                              width: "100%",
                              textAlign: "left",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              border: isTop
                                ? "2px solid var(--green)"
                                : "1px solid var(--border, #e5e7eb)",
                              borderRadius: 10,
                              background: isTop
                                ? "var(--green-light, #ecfdf5)"
                                : "var(--surface, #fff)",
                              padding: isTop ? "9px 11px" : "10px 12px",
                              boxShadow: isTop
                                ? "0 0 0 1px rgba(22, 163, 74, 0.15)"
                                : undefined,
                            }}
                            onClick={() => setScanSupplierKey(g.key)}
                            disabled={scanBusy || extractBusy}
                          >
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  alignItems: "center",
                                  gap: 6,
                                }}
                              >
                                <span
                                  style={{
                                    fontWeight: 700,
                                    fontSize: 12,
                                    color: "var(--text-primary)",
                                  }}
                                >
                                  {procureiqDisplaySupplierName(g.supplier_name)}
                                </span>
                                {isTop ? (
                                  <span
                                    style={{
                                      fontSize: 10,
                                      fontWeight: 800,
                                      padding: "2px 7px",
                                      borderRadius: 999,
                                      background: "var(--green)",
                                      color: "#fff",
                                      letterSpacing: "0.02em",
                                    }}
                                  >
                                    {t("Best match", "Καλύτερο")}
                                  </span>
                                ) : null}
                              </div>
                              <div
                                style={{
                                  fontSize: 11,
                                  color: "var(--text-muted)",
                                  marginTop: 3,
                                  fontVariantNumeric: "tabular-nums",
                                }}
                              >
                                {t(
                                  `${g.matches.length} order(s)`,
                                  `${g.matches.length} παραγγελί${g.matches.length === 1 ? "α" : "ες"}`
                                )}
                                {` · ${t("best match", "καλύτερο")} ${Math.round(g.bestScore * 100)}%`}
                                {` · ${t("supplier", "προμηθ.")} ${Math.round(g.bestSupplierScore * 100)}%`}
                              </div>
                            </div>
                            <div
                              style={{
                                textAlign: "right",
                                flexShrink: 0,
                                marginLeft: 8,
                                fontSize: 11,
                                fontWeight: 700,
                                color: "var(--green)",
                              }}
                            >
                              {t("Next", "Επόμενο")} ›
                            </div>
                          </button>
                          );
                        })}
                      </>
                    ) : (
                      <>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 8,
                            marginBottom: 2,
                          }}
                        >
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: "var(--text-muted)",
                              minWidth: 0,
                            }}
                          >
                            {scanMultiSupplier
                              ? t(
                                  `2 · Choose order · ${procureiqDisplaySupplierName(scanSelectedSupplierGroup?.supplier_name)}`,
                                  `2 · Επιλογή παραγγελίας · ${procureiqDisplaySupplierName(scanSelectedSupplierGroup?.supplier_name)}`
                                )
                              : t("Choose order", "Επιλογή παραγγελίας")}
                          </div>
                          {scanMultiSupplier ? (
                            <button
                              type="button"
                              className="btn-base btn-outline btn-small"
                              onClick={() => setScanSupplierKey(null)}
                              disabled={scanBusy || extractBusy}
                              style={{ flexShrink: 0 }}
                            >
                              {t("← Suppliers", "← Προμηθευτές")}
                            </button>
                          ) : null}
                        </div>
                        {scanOrderMatches.map((m, mIdx) => {
                          const outside = Boolean(m.flags?.outside_ui_range);
                          const already = matchLooksAlreadyReceived(m);
                          const isTop = mIdx === 0;
                          return (
                            <button
                              key={m.order_key}
                              type="button"
                              className="procureiq-supplier-row"
                              style={{
                                width: "100%",
                                textAlign: "left",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                border: already
                                  ? "2px solid #F59E0B"
                                  : isTop
                                    ? "2px solid var(--green)"
                                    : "1px solid var(--border, #e5e7eb)",
                                borderRadius: 10,
                                background: already
                                  ? "#FFFBEB"
                                  : isTop
                                    ? "var(--green-light, #ecfdf5)"
                                    : "var(--surface, #fff)",
                                padding: isTop || already ? "9px 11px" : "10px 12px",
                                boxShadow: already
                                  ? "0 0 0 1px rgba(245, 158, 11, 0.2)"
                                  : isTop
                                    ? "0 0 0 1px rgba(22, 163, 74, 0.15)"
                                    : undefined,
                              }}
                              onClick={() => pickScanMatch(m)}
                              disabled={scanBusy || extractBusy}
                            >
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div
                                  style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    alignItems: "center",
                                    gap: 6,
                                    fontWeight: 700,
                                    fontSize: 12,
                                    color: "var(--text-primary)",
                                  }}
                                >
                                  <span style={{ minWidth: 0 }}>
                                  {scanMultiSupplier
                                    ? null
                                    : (
                                        <>
                                          {procureiqDisplaySupplierName(m.supplier_name)}
                                          {(m.supy_order_number || m.supy_order_id) ? " · " : null}
                                        </>
                                      )}
                                  {m.supy_order_number || m.supy_order_id ? (
                                    <OpsOrderLink
                                      orderKey={m.order_key}
                                      orderId={m.supy_order_id}
                                      orderNumber={m.supy_order_number}
                                      locationId={locationId}
                                      brandId={brandId}
                                    />
                                  ) : (
                                    m.order_key
                                  )}
                                  </span>
                                  {isTop && !already ? (
                                    <span
                                      style={{
                                        fontSize: 10,
                                        fontWeight: 800,
                                        padding: "2px 7px",
                                        borderRadius: 999,
                                        background: "var(--green)",
                                        color: "#fff",
                                        letterSpacing: "0.02em",
                                        flexShrink: 0,
                                      }}
                                    >
                                      {t("Best match", "Καλύτερο")}
                                    </span>
                                  ) : null}
                                  {already ? (
                                    <span
                                      style={{
                                        fontSize: 10,
                                        fontWeight: 800,
                                        padding: "2px 7px",
                                        borderRadius: 999,
                                        background: "#D97706",
                                        color: "#fff",
                                        letterSpacing: "0.02em",
                                        flexShrink: 0,
                                      }}
                                    >
                                      {isTop
                                        ? t("Best · already received", "Καλύτερο · ήδη παραληφθείσα")
                                        : t("Already received", "Ήδη παραληφθείσα")}
                                    </span>
                                  ) : null}
                                </div>
                                <div
                                  style={{
                                    fontSize: 11,
                                    color: "var(--text-muted)",
                                    marginTop: 3,
                                  }}
                                >
                                  {t("Ordered", "Παραγγελία")}{" "}
                                  {String(m.submitted_at || "").slice(0, 10) || "—"}
                                  {m.expected_delivery_date
                                    ? ` · ${t("Due", "Παράδοση")} ${String(m.expected_delivery_date).slice(0, 10)}`
                                    : ""}
                                  {m.review_bucket
                                    ? ` · ${reviewBucketLabel(m.review_bucket, t)}`
                                    : ""}
                                  {m.score != null
                                    ? ` · ${Math.round(Number(m.score) * 100)}%`
                                    : ""}
                                </div>
                                <div
                                  style={{
                                    fontSize: 10,
                                    color: "var(--text-muted)",
                                    marginTop: 2,
                                    fontVariantNumeric: "tabular-nums",
                                  }}
                                >
                                  {t("Match", "Ταίριασμα")}
                                  {m.supplier_score != null
                                    ? ` · ${t("supplier", "προμηθ.")} ${Math.round(Number(m.supplier_score) * 100)}%`
                                    : ""}
                                  {m.date_score != null
                                    ? ` · ${t("date", "ημ/νία")} ${Math.round(Number(m.date_score) * 100)}%`
                                    : ""}
                                  {m.lines_score != null
                                    ? ` · ${t("lines", "γραμμές")} ${Math.round(Number(m.lines_score) * 100)}%`
                                    : ""}
                                </div>
                                {(outside || already) && (
                                  <div
                                    style={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 4,
                                      marginTop: 6,
                                    }}
                                  >
                                    {outside ? (
                                      <span
                                        style={{
                                          fontSize: 10,
                                          fontWeight: 700,
                                          padding: "2px 6px",
                                          borderRadius: 6,
                                          background: "#FEF3C7",
                                          color: "#92400E",
                                        }}
                                      >
                                        {t("Outside selected dates", "Εκτός επιλεγμένων ημερομηνιών")}
                                      </span>
                                    ) : null}
                                    {already ? (
                                      <span
                                        style={{
                                          fontSize: 10,
                                          fontWeight: 700,
                                          padding: "2px 6px",
                                          borderRadius: 6,
                                          background: "#FDE68A",
                                          color: "#92400E",
                                        }}
                                      >
                                        {t(
                                          "Already received in Supy — scan usually not needed",
                                          "Ήδη παραληφθείσα στη Supy — συνήθως δεν χρειάζεται σάρωση"
                                        )}
                                      </span>
                                    ) : null}
                                  </div>
                                )}
                              </div>
                              <div
                                style={{
                                  textAlign: "right",
                                  flexShrink: 0,
                                  marginLeft: 8,
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: "var(--green)",
                                }}
                              >
                                {t("Open", "Άνοιγμα")} ›
                              </div>
                            </button>
                          );
                        })}
                      </>
                    )}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
          <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 12 }}>
            {t(
              "Verify only after checking the delivery. Until then, food-cost keeps Supy’s dates.",
              "Επιβεβαιώστε μόνο αφού ελέγξετε την παράδοση. Μέχρι τότε το food-cost χρησιμοποιεί τις ημερομηνίες της Supy."
            )}
          </p>
          {renderList(
            "open_due",
            t(`Open due (${data?.open_due_count || 0})`, `Εκκρεμείς (${data?.open_due_count || 0})`),
            data?.open_due,
            t("No open orders due in this range", "Καμία εκκρεμής παραγγελία σε αυτό το διάστημα")
          )}
          {renderList(
            "received_late",
            t(
              `Received late in Supy (${data?.received_late_count || 0})`,
              `Καθυστερημένη παραλαβή στη Supy (${data?.received_late_count || 0})`
            ),
            data?.received_late,
            t("No late-marked receipts in this range", "Καμία καθυστερημένη παραλαβή σε αυτό το διάστημα")
          )}
        </>
      ) : (
        <>
          <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 8 }}>
            {t(
              "All Supy orders for this store in the date range, including received.",
              "Όλες οι παραγγελίες Supy για το κατάστημα στο διάστημα, συμπεριλαμβανομένων παραληφθεισών."
            )}
          </p>
          <p style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12, opacity: 0.85 }}>
            {t(
              "Invoice scan matching is on the Open tab.",
              "Η αντιστοίχιση με σάρωση τιμολογίου είναι στην καρτέλα Εκκρεμείς."
            )}
          </p>
          {renderList(
            "orders",
            t(`All orders (${data?.orders_count || 0})`, `Όλες (${data?.orders_count || 0})`),
            data?.orders,
            t("No orders in this range", "Καμία παραγγελία σε αυτό το διάστημα")
          )}
        </>
      )}

      {selectedKey &&
        typeof document !== "undefined" &&
        createPortal(
        <div
          className="modal-overlay"
          onClick={(e) => {
            if (e.target !== e.currentTarget) return;
            if (busy || extractBusy) return;
            closeOrderDialog();
          }}
        >
          <div
            className="card-box"
            style={{
              maxWidth: 960,
              width: "96%",
              maxHeight: "92vh",
              overflow: "auto",
              margin: "16px auto",
              position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 12,
                marginBottom: 8,
              }}
            >
              <div style={{ fontWeight: 800, fontSize: 16, paddingRight: 8 }}>
                {procureiqDisplaySupplierName(dialogOrder?.supplier_name || "Order")}
              </div>
              <button
                type="button"
                aria-label={t("Close", "Κλείσιμο")}
                disabled={busy || extractBusy}
                onClick={closeOrderDialog}
                style={{
                  border: "none",
                  background: "none",
                  cursor: busy || extractBusy ? "default" : "pointer",
                  color: "var(--text-muted)",
                  fontSize: 18,
                  lineHeight: 1,
                  padding: "2px 4px",
                  flexShrink: 0,
                }}
              >
                ✕
              </button>
            </div>
            <div
              style={{
                fontSize: 12,
                color: "var(--text-muted)",
                marginBottom: 10,
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span>
                {(dialogOrder?.supy_order_number || dialogOrder?.supy_order_id) && (
                  <>
                    {t("Supy", "Supy")}{" "}
                    <span style={{ fontWeight: 700, color: "var(--text-primary)" }}>
                      {dialogOrder.supy_order_number ||
                        String(dialogOrder.supy_order_id).slice(0, 8)}
                    </span>
                    {" · "}
                  </>
                )}
                {t("Status", "Κατάσταση")} {dialogOrder?.status || "—"} ·{" "}
                {t("Expected", "Αναμενόμενο")}{" "}
                {dialogOrder?.expected_delivery_date || "—"}
              </span>
              {showOnSupyHref ? (
                <a
                  href={showOnSupyHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-base btn-outline btn-small"
                  style={{ flexShrink: 0 }}
                >
                  {t("Show on Supy", "Εμφάνιση στο Supy")} ↗
                </a>
              ) : null}
            </div>
            {dialogAlreadyReceived && !detailLoading ? (
              <div
                role="status"
                style={{
                  marginBottom: 12,
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: "1.5px solid #F59E0B",
                  background: "#FFFBEB",
                  color: "#92400E",
                  fontSize: 12,
                  fontWeight: 700,
                  lineHeight: 1.4,
                }}
              >
                {alreadyReceivedBannerCopy(t, {
                  orderLabel:
                    dialogOrder?.supy_order_number || dialogOrder?.supy_order_id,
                })}
              </div>
            ) : null}
            {detailLoading || !detail ? (
              <div style={{ padding: "36px 12px", textAlign: "center" }}>
                <LoadingPulse label={t("Loading order…", "Φόρτωση παραγγελίας…")} />
              </div>
            ) : (
              <>
            <TotalsStrip
              title={t("Supy order (net)", "Παραγγελία Supy (καθαρή)")}
              subtitle={t(
                "Supy stores net unit prices — compare Net to the invoice καθαρή αξία.",
                "Η Supy έχει καθαρές τιμές — συγκρίνετε την Καθαρή αξία με το τιμολόγιο."
              )}
              subtotal={detail.total_ordered}
              tax={detail.applied_tax}
              total={
                detail.ordered_total != null
                  ? detail.ordered_total
                  : detail.total_ordered != null && detail.applied_tax != null
                    ? roundMoney(Number(detail.total_ordered) + Number(detail.applied_tax))
                    : detail.total_ordered != null
                      ? detail.total_ordered
                      : null
              }
              taxRatePct={detail.tax_rate_pct}
              t={t}
            />
            <label className="form-label">{t("Received at", "Παραλήφθηκε στις")}</label>
            <input
              type="datetime-local"
              className="form-input"
              value={receivedAt}
              onChange={(e) => setReceivedAt(e.target.value)}
              style={{ marginBottom: 12 }}
            />
            {(() => {
              // Supplier memory: known supplier codes → Supy items, plus what this
              // invoice will add on Verify. Read-only; learning happens server-side.
              const known = Array.isArray(supplierProfile?.sku_mappings)
                ? supplierProfile.sku_mappings
                : [];
              const knownSet = new Set(
                known.map((m) => String(m.supplier_sku || "").trim().toUpperCase())
              );
              const pendingNew = editLines.filter(
                (l) =>
                  l.supplier_sku &&
                  l.item_id &&
                  !knownSet.has(String(l.supplier_sku).trim().toUpperCase())
              );
              const conflicts = editLines.filter((l) => l.sku_conflict);
              if (!known.length && !pendingNew.length && !conflicts.length) return null;
              const count = known.length;
              return (
                <div
                  style={{
                    marginBottom: 12,
                    border: "1px solid var(--border, #e5e7eb)",
                    borderRadius: 10,
                    background: "var(--bg-soft, #f9fafb)",
                    fontSize: 11,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setSupplierProfileOpen((v) => !v)}
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 8,
                      padding: "8px 10px",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      fontSize: 12,
                      fontWeight: 700,
                      color: "var(--text-primary)",
                      textAlign: "left",
                    }}
                  >
                    <span>
                      {t("Supplier codes memory", "Μνήμη κωδικών προμηθευτή")} ({count})
                      {pendingNew.length ? (
                        <span
                          style={{
                            marginLeft: 8,
                            fontSize: 10,
                            fontWeight: 700,
                            color: "var(--green)",
                          }}
                        >
                          +{pendingNew.length} {t("on Verify", "στο Verify")}
                        </span>
                      ) : null}
                      {conflicts.length ? (
                        <span
                          style={{
                            marginLeft: 8,
                            fontSize: 10,
                            fontWeight: 700,
                            color: "#B45309",
                          }}
                        >
                          ⚠ {conflicts.length} {t("conflict", "σύγκρουση")}
                        </span>
                      ) : null}
                    </span>
                    <span style={{ color: "var(--text-muted)" }}>
                      {supplierProfileOpen ? "▴" : "▾"}
                    </span>
                  </button>
                  {supplierProfileOpen ? (
                    <div style={{ padding: "0 10px 8px" }}>
                      <div style={{ color: "var(--text-muted)", marginBottom: 6, lineHeight: 1.4 }}>
                        {t(
                          "Supplier SKU (first column on the invoice) → Supy item. Learned from verified invoices; a known code matches the line even when the name changes.",
                          "Κωδικός προμηθευτή (πρώτη στήλη τιμολογίου) → είδος Supy. Μαθαίνεται από επιβεβαιωμένα τιμολόγια· γνωστός κωδικός ταιριάζει τη γραμμή ακόμη κι αν αλλάξει το όνομα."
                        )}
                        {supplierProfile?.sample_count ? (
                          <>
                            {" · "}
                            {supplierProfile.sample_count}{" "}
                            {t("invoice(s) seen", "τιμολόγια")}
                          </>
                        ) : null}
                      </div>
                      {pendingNew.length ? (
                        <div style={{ marginBottom: 6 }}>
                          <div style={{ fontWeight: 700, color: "var(--green)", marginBottom: 3 }}>
                            {t("Will be remembered on Verify", "Θα απομνημονευθούν στο Verify")}
                          </div>
                          {pendingNew.map((l, i) => (
                            <div
                              key={`pending-sku-${i}`}
                              style={{
                                display: "grid",
                                gridTemplateColumns: "minmax(64px, auto) 1fr",
                                gap: 8,
                                padding: "2px 0",
                              }}
                            >
                              <span
                                style={{
                                  fontFamily:
                                    "ui-monospace, SFMono-Regular, Menlo, monospace",
                                  fontWeight: 700,
                                }}
                              >
                                {l.supplier_sku}
                              </span>
                              <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
                                {procureiqDisplayProductName(
                                  l.ordered_product_name || l.product_name
                                )}
                                {l.item_code ? (
                                  <span style={{ color: "var(--text-muted)" }}>
                                    {" "}
                                    · {l.item_code}
                                  </span>
                                ) : null}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : null}
                      {known.length ? (
                        <div
                          style={{
                            maxHeight: 180,
                            overflowY: "auto",
                            borderTop: pendingNew.length
                              ? "1px solid var(--border, #e5e7eb)"
                              : "none",
                            paddingTop: pendingNew.length ? 6 : 0,
                          }}
                        >
                          {known.map((m, i) => (
                            <div
                              key={`known-sku-${m.supplier_sku}-${i}`}
                              style={{
                                display: "grid",
                                gridTemplateColumns: "minmax(64px, auto) 1fr auto",
                                gap: 8,
                                padding: "2px 0",
                                color: "var(--text-primary)",
                              }}
                              title={m.invoice_name || undefined}
                            >
                              <span
                                style={{
                                  fontFamily:
                                    "ui-monospace, SFMono-Regular, Menlo, monospace",
                                  fontWeight: 700,
                                }}
                              >
                                {m.supplier_sku}
                              </span>
                              <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
                                {procureiqDisplayProductName(m.catalog_name || "—")}
                                {m.catalog_code ? (
                                  <span style={{ color: "var(--text-muted)" }}>
                                    {" "}
                                    · {m.catalog_code}
                                  </span>
                                ) : null}
                              </span>
                              <span style={{ color: "var(--text-muted)", whiteSpace: "nowrap" }}>
                                ×{m.seen_count || 1}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })()}
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>
              {t("Received qty / price", "Ποσότητα / τιμή παραλαβής")}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "2fr 0.8fr 0.9fr 0.9fr",
                gap: 8,
                marginBottom: 6,
                fontSize: 10,
                fontWeight: 700,
                color: "var(--text-muted)",
                textTransform: "uppercase",
              }}
            >
              <div>{t("Item", "Είδος")}</div>
              <div>{t("Received qty", "Ποσ. παραλαβής")}</div>
              <div>{t("Net unit €", "Καθ. τιμή €")}</div>
              <div style={{ textAlign: "right" }}>{t("Net line", "Καθ. γραμμή")}</div>
            </div>
            {editLines.map((line, idx) => {
              const qty = Number(line.qty);
              const price = Number(line.unit_price);
              const lineTotal =
                Number.isFinite(qty) && Number.isFinite(price) ? qty * price : null;
              const orderedHint = formatOrderedHint(line, t);
              const differs = valuesDifferFromOrdered(line);
              const qtyDiffers =
                line.qty_differs_from_order ||
                (numOrNull(line.ordered_qty) != null &&
                  numOrNull(line.qty) != null &&
                  Math.abs(Number(line.qty) - Number(line.ordered_qty)) >= 0.005);
              const priceDiffers =
                line.price_differs_from_order ||
                (numOrNull(line.ordered_unit_price) != null &&
                  numOrNull(line.unit_price) != null &&
                  Math.abs(Number(line.unit_price) - Number(line.ordered_unit_price)) >=
                    0.005);
              return (
                <div
                  key={`${line.item_id || "new"}-${idx}`}
                  style={{
                    marginBottom: 0,
                    padding: "10px 8px",
                    borderRadius: 8,
                    background:
                      needsRematchIdx === idx
                        ? "#FFFBEB"
                        : idx % 2 === 1
                          ? "var(--bg-soft, #f3f4f6)"
                          : "transparent",
                    border:
                      needsRematchIdx === idx ? "1px solid #F59E0B" : "1px solid transparent",
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "2fr 0.8fr 0.9fr 0.9fr",
                      gap: 8,
                    }}
                  >
                    <div style={{ fontSize: 12, alignSelf: "center" }}>
                      <div
                        style={{
                          fontWeight: 700,
                          color: "var(--text-primary)",
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        <span>
                          {procureiqDisplayProductName(
                            line.ordered_product_name || line.product_name
                          )}
                        </span>
                        {line.matched === true ? (
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: "var(--green)",
                            }}
                          >
                            ✓ {t("matched", "ταιριαστό")}
                          </span>
                        ) : (
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: "#B45309",
                            }}
                          >
                            {needsRematchIdx === idx
                              ? t("needs match", "χρειάζεται ταίριασμα")
                              : t("new", "νέο")}
                          </span>
                        )}
                        <button
                          type="button"
                          className="btn-base btn-outline btn-small"
                          onClick={() => openRematchPicker(idx)}
                          title={t("Change match", "Αλλαγή ταιριάσματος")}
                          aria-label={t("Change match", "Αλλαγή ταιριάσματος")}
                          style={{
                            padding: "2px 6px",
                            minHeight: 0,
                            lineHeight: 1,
                            color: "var(--text-muted)",
                          }}
                        >
                          <Icon name="pencil" size={12} />
                        </button>
                      </div>
                      {line.invoice_product_name &&
                      line.invoice_product_name !==
                        (line.ordered_product_name || line.product_name) ? (
                        <div
                          style={{
                            fontSize: 11,
                            color: "var(--text-muted)",
                            marginTop: 2,
                            lineHeight: 1.35,
                          }}
                        >
                          {t("Invoice", "Τιμολόγιο")}:{" "}
                          {procureiqDisplayProductName(line.invoice_product_name)}
                        </div>
                      ) : null}
                      {line.supplier_sku || line.match_via ? (
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 6,
                            alignItems: "center",
                            marginTop: 3,
                            fontSize: 10,
                            lineHeight: 1.3,
                          }}
                          title={
                            Array.isArray(line.match_reasons) && line.match_reasons.length
                              ? line.match_reasons.join(" · ")
                              : undefined
                          }
                        >
                          {line.supplier_sku ? (
                            <span
                              style={{
                                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                                fontWeight: 700,
                                padding: "1px 6px",
                                borderRadius: 6,
                                background: "var(--bg-soft, #f3f4f6)",
                                border: "1px solid var(--border, #e5e7eb)",
                                color: "var(--text-primary)",
                              }}
                            >
                              {t("SKU", "ΚΩΔ")} {line.supplier_sku}
                            </span>
                          ) : null}
                          {line.supplier_sku && line.item_id ? (
                            line.sku_conflict ? (
                              <span style={{ fontWeight: 700, color: "#B45309" }}>
                                ⚠ {t(
                                  "code was remembered for a different item",
                                  "ο κωδικός ήταν για άλλο είδος"
                                )}
                              </span>
                            ) : line.sku_known ? (
                              <span style={{ fontWeight: 700, color: "var(--green)" }}>
                                {t("known code ✓", "γνωστός κωδικός ✓")}
                              </span>
                            ) : (
                              <span style={{ color: "var(--text-muted)", fontWeight: 600 }}>
                                {t(
                                  "code will be remembered on Verify",
                                  "ο κωδικός θα απομνημονευθεί στο Verify"
                                )}
                              </span>
                            )
                          ) : null}
                          {line.match_via && matchViaLabel(line.match_via) ? (
                            <span style={{ color: "var(--text-muted)" }}>
                              {matchViaLabel(line.match_via)}
                              {line.match_score != null
                                ? ` ${Math.round(line.match_score * 100)}%`
                                : ""}
                            </span>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                    <input
                      type="number"
                      className="form-input"
                      value={line.qty}
                      onChange={(e) => updateLine(idx, "qty", e.target.value)}
                      style={
                        qtyDiffers
                          ? { borderColor: "#F59E0B", background: "#FFFBEB" }
                          : undefined
                      }
                      title={
                        numOrNull(line.ordered_qty) != null
                          ? `${t("Ordered", "Παραγγελία")}: ${line.ordered_qty}`
                          : undefined
                      }
                    />
                    <input
                      type="number"
                      className="form-input"
                      step="0.01"
                      value={line.unit_price}
                      onChange={(e) => updateLine(idx, "unit_price", e.target.value)}
                      style={
                        priceDiffers
                          ? { borderColor: "#F59E0B", background: "#FFFBEB" }
                          : undefined
                      }
                      title={
                        numOrNull(line.ordered_unit_price) != null
                          ? `${t("Ordered", "Παραγγελία")}: ${formatProcureEuro(line.ordered_unit_price)}`
                          : undefined
                      }
                    />
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        alignSelf: "center",
                        textAlign: "right",
                        color: "var(--text-primary)",
                      }}
                    >
                      {lineTotal != null ? formatProcureEuro(lineTotal) : "—"}
                    </div>
                  </div>
                  {line.uom_converted_to_order &&
                  numOrNull(line.ocr_qty) != null ? (
                    <div
                      style={{
                        marginTop: 4,
                        fontSize: 11,
                        color: "var(--text-muted)",
                        lineHeight: 1.4,
                      }}
                    >
                      {t("Invoice", "Τιμολόγιο")}{" "}
                      {line.ocr_qty}
                      {" × "}
                      {numOrNull(line.ocr_unit_price) != null
                        ? formatProcureEuro(line.ocr_unit_price)
                        : "—"}
                      {" → "}
                      {t("stored as pack", "αποθήκευση ως συσκευασία")}{" "}
                      {numOrNull(line.qty) != null ? String(line.qty) : "—"}
                      {" × "}
                      {numOrNull(line.unit_price) != null
                        ? formatProcureEuro(line.unit_price)
                        : "—"}
                    </div>
                  ) : null}
                  {orderedHint ? (
                    <div
                      style={{
                        marginTop: 4,
                        fontSize: 11,
                        fontWeight: differs ? 700 : 500,
                        color: differs ? "#B45309" : "var(--text-muted)",
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 8,
                        alignItems: "center",
                      }}
                    >
                      <span>{orderedHint}</span>
                      {differs ? (
                        <span>
                          → {t("Received", "Παραλαβή")}{" "}
                          {numOrNull(line.qty) != null ? String(line.qty) : "—"} ·{" "}
                          {numOrNull(line.unit_price) != null
                            ? formatProcureEuro(line.unit_price)
                            : "—"}
                        </span>
                      ) : (
                        <span style={{ fontWeight: 600, color: "var(--green)" }}>
                          {line.uom_converted_to_order
                            ? t(
                                "matches order (pack UOM)",
                                "ίδιο με παραγγελία (συσκευασία)"
                              )
                            : t("matches order", "ίδιο με παραγγελία")}
                        </span>
                      )}
                    </div>
                  ) : null}
                </div>
              );
            })}
            {(() => {
              const vatRate =
                invoiceVatInfo?.vat_rate_pct != null
                  ? invoiceVatInfo.vat_rate_pct
                  : detail.tax_rate_pct != null
                    ? Number(detail.tax_rate_pct)
                    : null;
              const accepted = computeAcceptedTotals(editLines, detail, vatRate);
              if (!accepted) return null;
              const printedNet = invoiceVatInfo?.total_net;
              const printedGross = invoiceVatInfo?.total_gross;
              const netOk = moneyClose(accepted.subtotal, printedNet);
              const grossOk = moneyClose(accepted.total, printedGross);
              let matchHint = null;
              if (printedNet != null || printedGross != null) {
                if (netOk && (grossOk || printedGross == null)) {
                  matchHint = {
                    ok: true,
                    text: t(
                      "Matches invoice footer (net / gross)",
                      "Ταιριάζει με το υποσέλιδο τιμολογίου (καθαρή / πληρωτέο)"
                    ),
                  };
                } else if (netOk === false || grossOk === false) {
                  const parts = [];
                  if (printedNet != null) {
                    parts.push(
                      `${t("Invoice net", "Καθαρή τιμολ.")} ${formatProcureEuro(printedNet)}`
                    );
                  }
                  if (printedGross != null) {
                    parts.push(
                      `${t("Invoice gross", "Πληρωτέο τιμολ.")} ${formatProcureEuro(printedGross)}`
                    );
                  }
                  matchHint = {
                    ok: false,
                    text: t(
                      `Check totals — ${parts.join(" · ")}`,
                      `Ελέγξτε σύνολα — ${parts.join(" · ")}`
                    ),
                  };
                }
              } else if (invoiceVatInfo?.converted_from_gross) {
                matchHint = {
                  ok: true,
                  text: t(
                    "Invoice unit prices looked gross — shown as net for Supy",
                    "Οι τιμές τιμολογίου φάνηκαν με ΦΠΑ — εμφανίζονται καθαρές για τη Supy"
                  ),
                };
              }
              return (
                <div style={{ marginTop: 10 }}>
                  <TotalsStrip
                    title={t("Received (net → gross)", "Παραλαβή (καθαρή → πληρωτέο)")}
                    subtitle={
                      vatRate != null
                        ? t(
                            `Unit prices are net (ex VAT). Gross uses ${Number.isInteger(Number(vatRate)) ? Number(vatRate) : Number(vatRate).toFixed(1)}% VAT.`,
                            `Οι τιμές μονάδας είναι καθαρές. Το πληρωτέο υπολογίζεται με ΦΠΑ ${Number.isInteger(Number(vatRate)) ? Number(vatRate) : Number(vatRate).toFixed(1)}%.`
                          )
                        : t(
                            "Unit prices are net (ex VAT), same basis as Supy.",
                            "Οι τιμές μονάδας είναι καθαρές, ίδια βάση με τη Supy."
                          )
                    }
                    subtotal={accepted.subtotal}
                    tax={accepted.tax}
                    total={accepted.total}
                    taxRatePct={vatRate}
                    t={t}
                    accent
                    matchHint={matchHint}
                  />
                </div>
              );
            })()}
            <div style={{ marginTop: 4, marginBottom: 14 }}>
              <label className="form-label">
                {invoiceAttachments.length > 0
                  ? t("Invoice", "Τιμολόγιο")
                  : t("Invoice (optional)", "Τιμολόγιο (προαιρετικό)")}
              </label>
              {invoiceAttachments.length > 0 ? (
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "stretch",
                    border: "1px solid var(--border, #e5e7eb)",
                    borderRadius: 12,
                    background: "var(--bg-soft, #f9fafb)",
                    padding: 8,
                    opacity: extractBusy ? 0.75 : 1,
                  }}
                >
                  <div
                    style={{
                      width: 92,
                      flexShrink: 0,
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                      maxHeight: 280,
                      overflowY: "auto",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 800,
                        color: "var(--text-muted)",
                        textTransform: "uppercase",
                        letterSpacing: 0.3,
                        padding: "0 2px",
                      }}
                    >
                      {t("Pages", "Σελίδες")}
                    </div>
                    {invoiceAttachments.map((page, pIdx) => {
                      const active = pIdx === activeInvoicePageIdx;
                      return (
                        <div
                          key={page.key || `page-${pIdx}`}
                          style={{
                            position: "relative",
                            borderRadius: 8,
                            border: active
                              ? "2px solid var(--green)"
                              : "1px solid var(--border, #e5e7eb)",
                            background: "var(--surface, #fff)",
                            overflow: "hidden",
                          }}
                        >
                          <button
                            type="button"
                            disabled={extractBusy}
                            onClick={() =>
                              syncActiveInvoicePreview(invoiceAttachments, pIdx)
                            }
                            style={{
                              display: "block",
                              width: "100%",
                              border: "none",
                              background: "transparent",
                              padding: 0,
                              cursor: extractBusy ? "default" : "pointer",
                            }}
                            title={
                              page.filename ||
                              t(`Page ${pIdx + 1}`, `Σελίδα ${pIdx + 1}`)
                            }
                          >
                            {page.previewUrl ? (
                              <img
                                src={page.previewUrl}
                                alt=""
                                style={{
                                  width: "100%",
                                  height: 72,
                                  objectFit: "cover",
                                  display: "block",
                                  background: "var(--surface)",
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  height: 72,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: "var(--text-muted)",
                                  padding: 6,
                                  textAlign: "center",
                                }}
                              >
                                {looksLikeInvoiceImageKey(
                                  page.key,
                                  page.filename,
                                  page.content_type
                                )
                                  ? "…"
                                  : "PDF"}
                              </div>
                            )}
                            <div
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                padding: "3px 5px",
                                color: page.pending
                                  ? "#B45309"
                                  : active
                                    ? "var(--green)"
                                    : "var(--text-primary)",
                              }}
                            >
                              {page.pending
                                ? t("Reading…", "Ανάγνωση…")
                                : t(`Page ${pIdx + 1}`, `Σελ. ${pIdx + 1}`)}
                            </div>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {extractBusy ? (
                      renderInvoiceExtractBusy({ previewMaxH: 140 })
                    ) : (
                      <>
                        {invoicePreviewUrl ? (
                          <button
                            type="button"
                            className="photo-preview-wrap"
                            onClick={() =>
                              setInvoiceLightbox({
                                url: invoicePreviewUrl,
                                label:
                                  invoiceFileName ||
                                  t(
                                    `Page ${activeInvoicePageIdx + 1}`,
                                    `Σελίδα ${activeInvoicePageIdx + 1}`
                                  ),
                              })
                            }
                            style={{
                              marginBottom: 8,
                              display: "block",
                              width: "100%",
                              border: "none",
                              padding: 0,
                              background: "transparent",
                              cursor: "zoom-in",
                            }}
                            title={t("View full size", "Πλήρης προβολή")}
                          >
                            <img
                              src={invoicePreviewUrl}
                              alt={invoiceFileName}
                              style={{
                                width: "100%",
                                maxHeight: 220,
                                objectFit: "contain",
                                display: "block",
                                borderRadius: 10,
                                background: "var(--surface)",
                              }}
                            />
                          </button>
                        ) : (
                          <div
                            style={{
                              fontSize: 12,
                              fontWeight: 700,
                              marginBottom: 6,
                              wordBreak: "break-all",
                            }}
                          >
                            {invoiceFileName || t("PDF attached", "PDF επισυνάφθηκε")}
                          </div>
                        )}
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "baseline",
                            gap: "4px 10px",
                            fontSize: 12,
                            lineHeight: 1.45,
                            marginTop: 2,
                          }}
                        >
                          <span style={{ fontWeight: 700, color: "var(--green)" }}>
                            {invoiceAttachments.length === 1
                              ? t("Invoice attached", "Το τιμολόγιο επισυνάφθηκε")
                              : t(
                                  `${invoiceAttachments.length} pages attached`,
                                  `${invoiceAttachments.length} σελίδες επισυνάφθηκαν`
                                )}
                          </span>
                          <span style={{ color: "var(--text-muted)" }}>·</span>
                          <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
                            {t(
                              "Select page · tap image to enlarge",
                              "Επιλογή σελίδας · πατήστε εικόνα για μεγέθυνση"
                            )}
                          </span>
                          {invoiceFileName ? (
                            <>
                              <span style={{ color: "var(--text-muted)" }}>·</span>
                              <span
                                style={{
                                  fontSize: 11,
                                  color: "var(--text-muted)",
                                  wordBreak: "break-all",
                                }}
                              >
                                {t(
                                  `Page ${activeInvoicePageIdx + 1}`,
                                  `Σελίδα ${activeInvoicePageIdx + 1}`
                                )}
                                {" · "}
                                {invoiceFileName}
                              </span>
                            </>
                          ) : null}
                          {invoiceExtractNote ? (
                            <>
                              <span style={{ color: "var(--text-muted)" }}>·</span>
                              <span
                                style={{
                                  fontSize: 11,
                                  color: "var(--text-muted)",
                                  fontWeight: 500,
                                }}
                              >
                                {invoiceExtractNote}
                              </span>
                            </>
                          ) : null}
                        </div>
                        {invoiceSupplierCheck ? (
                          <div
                            style={{
                              marginTop: 10,
                              padding: "8px 10px",
                              borderRadius: 10,
                              background: invoiceSupplierCheck.ok ? "var(--green-light)" : "#FEF3C7",
                              border: `1px solid ${invoiceSupplierCheck.ok ? "var(--green)" : "#F59E0B"}`,
                              fontSize: 12,
                              fontWeight: 600,
                              textAlign: "left",
                            }}
                          >
                            {invoiceSupplierCheck.ok
                              ? t("Supplier looks OK", "Ο προμηθευτής φαίνεται OK")
                              : t("Supplier may not match", "Ο προμηθευτής μπορεί να μην ταιριάζει")}
                            <div
                              style={{
                                fontSize: 11,
                                fontWeight: 500,
                                marginTop: 4,
                                color: "var(--text-muted)",
                              }}
                            >
                              {t("Invoice", "Τιμολόγιο")}:{" "}
                              {invoiceSupplierCheck.extracted_supplier_name || "—"}
                              <br />
                              {t("Order", "Παραγγελία")}:{" "}
                              {invoiceSupplierCheck.expected_supplier_name || "—"}
                            </div>
                          </div>
                        ) : null}
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div
                  className="photo-upload-zone"
                  role="button"
                  tabIndex={extractBusy ? -1 : 0}
                  style={{
                    borderColor: invoiceDragOver ? "var(--green)" : undefined,
                    background: invoiceDragOver ? "var(--green-light)" : undefined,
                    opacity: extractBusy ? 0.7 : 1,
                    pointerEvents: extractBusy ? "none" : undefined,
                    padding: "18px 14px",
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setInvoiceDragOver(true);
                  }}
                  onDragLeave={() => setInvoiceDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setInvoiceDragOver(false);
                    acceptInvoiceFile(e.dataTransfer.files?.[0]);
                  }}
                  onClick={async () => {
                    if (extractBusy) return;
                    const file = await pickInvoiceUploadFile();
                    if (file) acceptInvoiceFile(file);
                  }}
                  onKeyDown={async (e) => {
                    if (e.key !== "Enter" && e.key !== " ") return;
                    e.preventDefault();
                    if (extractBusy) return;
                    const file = await pickInvoiceUploadFile();
                    if (file) acceptInvoiceFile(file);
                  }}
                >
                  {extractBusy ? (
                    renderInvoiceExtractBusy({ previewMaxH: 160 })
                  ) : (
                    <>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>
                        {t("Drop PDF or photo here", "Ρίξτε PDF ή φωτο εδώ")}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
                        {t(
                          "or click to browse · multi-page invoices: use Scan above",
                          "ή κλικ για επιλογή · πολύσέλιδα: χρησιμοποιήστε Σάρωση πάνω"
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: 10,
                          color: "var(--text-muted)",
                          marginTop: 8,
                          fontWeight: 600,
                        }}
                      >
                        {t(
                          "All files shown — open Downloads if today’s photos aren’t in Recents",
                          "Όλα τα αρχεία — ανοίξτε Λήψεις αν οι σημερινές δεν είναι στα Πρόσφατα"
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            {showVerifyActions ? (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button type="button" className="btn-base btn-green" disabled={busy || extractBusy} onClick={handleVerify}>
                  {t("Verify received", "Επιβεβαίωση παραλαβής")}
                </button>
                <button
                  type="button"
                  className="btn-base btn-outline"
                  disabled={busy || extractBusy}
                  onClick={handleNotReceivedYet}
                >
                  {t("Not received yet", "Δεν παραλήφθηκε ακόμα")}
                </button>
              </div>
            ) : (
              <div
                style={{
                  fontSize: 12,
                  color: "var(--text-muted)",
                  fontWeight: 600,
                  padding: "8px 0 2px",
                }}
              >
                {reviewBucketLabel(detail.review_bucket, t)}
                {detail.status ? ` · ${detail.status}` : ""}
              </div>
            )}
              </>
            )}
          </div>
        </div>,
        document.body
      )}

      {rematchIdx != null && editLines[rematchIdx] && (
        <div className="modal-overlay" style={{ zIndex: 1350 }}>
          <div
            className="card-box"
            style={{
              maxWidth: 480,
              width: "94%",
              margin: "32px auto",
              maxHeight: "88vh",
              overflow: "auto",
            }}
          >
            {(() => {
              const current = editLines[rematchIdx];
              const invoiceLabel = procureiqDisplayProductName(
                current.invoice_product_name || current.product_name || "—"
              );
              const q = rematchFilter.trim().toLowerCase();
              const orderLines = Array.isArray(detail?.lines) ? detail.lines : [];
              const usedByIdx = new Map();
              editLines.forEach((l, i) => {
                if (l.item_id) usedByIdx.set(String(l.item_id), i);
              });
              const candidates = orderLines.filter((ol) => {
                if (!ol?.item_id) return false;
                if (!q) return true;
                const hay = [
                  ol.product_name,
                  ol.item_code,
                  ol.item_id,
                  ol.ordered_qty,
                  ol.unit_price,
                ]
                  .map((x) => String(x || "").toLowerCase())
                  .join(" ");
                return hay.includes(q);
              });
              return (
                <>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 12,
                      marginBottom: 8,
                    }}
                  >
                    <div style={{ fontWeight: 800, fontSize: 16 }}>
                      {t("Match invoice line", "Ταίριασμα γραμμής τιμολογίου")}
                    </div>
                    <button
                      type="button"
                      aria-label={t("Close", "Κλείσιμο")}
                      onClick={closeRematchPicker}
                      style={{
                        border: "none",
                        background: "none",
                        cursor: "pointer",
                        color: "var(--text-muted)",
                        fontSize: 18,
                        lineHeight: 1,
                        padding: "2px 4px",
                      }}
                    >
                      ✕
                    </button>
                  </div>
                  <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 10 }}>
                    {t("Invoice", "Τιμολόγιο")}:{" "}
                    <span style={{ fontWeight: 700, color: "var(--text-primary)" }}>
                      {invoiceLabel}
                    </span>
                    {current.supplier_sku ? (
                      <>
                        {" · "}
                        <span style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                          {current.supplier_sku}
                        </span>
                      </>
                    ) : null}
                  </p>
                  <input
                    className="form-input"
                    type="search"
                    value={rematchFilter}
                    onChange={(e) => setRematchFilter(e.target.value)}
                    placeholder={t("Search order items…", "Αναζήτηση ειδών παραγγελίας…")}
                    style={{ marginBottom: 10 }}
                    autoFocus
                  />
                  <div
                    style={{
                      maxHeight: "46vh",
                      overflowY: "auto",
                      border: "1px solid var(--border, #e5e7eb)",
                      borderRadius: 10,
                      marginBottom: 12,
                    }}
                  >
                    {!candidates.length ? (
                      <div
                        style={{
                          padding: 14,
                          fontSize: 13,
                          color: "var(--text-muted)",
                          textAlign: "center",
                        }}
                      >
                        {t("No order items match", "Κανένα είδος δεν ταιριάζει")}
                      </div>
                    ) : (
                      candidates.map((ol) => {
                        const id = String(ol.item_id);
                        const usedIdx = usedByIdx.get(id);
                        const isCurrent = usedIdx === rematchIdx;
                        const usedElsewhere = usedIdx != null && usedIdx !== rematchIdx;
                        const usedLabel =
                          usedElsewhere && editLines[usedIdx]
                            ? procureiqDisplayProductName(
                                editLines[usedIdx].invoice_product_name ||
                                  editLines[usedIdx].product_name ||
                                  "—"
                              )
                            : null;
                        return (
                          <button
                            key={id}
                            type="button"
                            onClick={() => applyRematch(ol)}
                            style={{
                              width: "100%",
                              textAlign: "left",
                              border: "none",
                              borderBottom: "1px solid var(--border, #e5e7eb)",
                              background: isCurrent
                                ? "var(--green-light, #ecfdf5)"
                                : usedElsewhere
                                  ? "#FFFBEB"
                                  : "transparent",
                              cursor: "pointer",
                              padding: "10px 12px",
                            }}
                          >
                            <div style={{ fontWeight: 700, fontSize: 13, color: "var(--text-primary)" }}>
                              {procureiqDisplayProductName(ol.product_name)}
                              {isCurrent ? (
                                <span
                                  style={{
                                    marginLeft: 6,
                                    fontSize: 10,
                                    color: "var(--green)",
                                    fontWeight: 700,
                                  }}
                                >
                                  {t("current", "τρέχον")}
                                </span>
                              ) : null}
                            </div>
                            <div
                              style={{
                                fontSize: 11,
                                color: "var(--text-muted)",
                                marginTop: 3,
                              }}
                            >
                              {ol.item_code ? `${ol.item_code} · ` : ""}
                              {t("Ordered", "Παραγγελία")}{" "}
                              {numOrNull(ol.ordered_qty ?? ol.qty) ?? "—"}
                              {" · "}
                              {formatProcureEuro(
                                numOrNull(ol.ordered_unit_price ?? ol.unit_price)
                              )}
                            </div>
                            {usedElsewhere ? (
                              <div
                                style={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: "#B45309",
                                  marginTop: 4,
                                }}
                              >
                                {t(
                                  `Already matched to “${usedLabel}” — selecting frees that line`,
                                  `Ήδη σε «${usedLabel}» — η επιλογή ελευθερώνει εκείνη τη γραμμή`
                                )}
                              </div>
                            ) : null}
                          </button>
                        );
                      })
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      className="btn-base btn-outline"
                      onClick={closeRematchPicker}
                    >
                      {t("Cancel", "Ακύρωση")}
                    </button>
                    {current.matched || current.item_id ? (
                      <button
                        type="button"
                        className="btn-base btn-outline"
                        onClick={clearRematchAtPicker}
                        style={{ borderColor: "#DC2626", color: "#B91C1C" }}
                      >
                        {t("Unmatch", "Αποσύνδεση")}
                      </button>
                    ) : null}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {pendingInvoiceDraft && (
        <div className="modal-overlay" style={{ zIndex: 1300 }}>
          <div
            className="card-box"
            style={{
              maxWidth: 440,
              width: "92%",
              margin: "40px auto",
              borderColor: "#F59E0B",
            }}
          >
            <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 8, color: "#B45309" }}>
              {pendingInvoiceDraft.append
                ? t("This page may not match", "Αυτή η σελίδα μπορεί να μην ταιριάζει")
                : t("Invoice may not match this order", "Το τιμολόγιο μπορεί να μην ταιριάζει")}
            </div>
            <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 12 }}>
              {pendingInvoiceDraft.append
                ? t(
                    "Confirm to append lines from this page, or cancel to discard only this page.",
                    "Επιβεβαιώστε για προσθήκη γραμμών από αυτή τη σελίδα, ή ακύρωση για απόρριψη μόνο αυτής της σελίδας."
                  )
                : t(
                    "With an invoice attached we always use its lines (not the full PO). If only some order items are on the invoice, set a partial receive — or cancel and pick another order.",
                    "Με συνημμένο τιμολόγιο χρησιμοποιούμε πάντα τις γραμμές του (όχι όλη την παραγγελία). Αν λείπουν είδη, ορίστε μερική παραλαβή — ή ακυρώστε και επιλέξτε άλλη παραγγελία."
                  )}
            </p>
            <ul
              style={{
                margin: "0 0 16px",
                paddingLeft: 18,
                fontSize: 13,
                fontWeight: 600,
                color: "var(--text-primary)",
              }}
            >
              {draftMatchWarningLines(pendingInvoiceDraft.quality, t).map((line) => (
                <li key={line} style={{ marginBottom: 6 }}>
                  {line}
                </li>
              ))}
            </ul>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                type="button"
                className="btn-base btn-outline"
                disabled={extractBusy}
                onClick={cancelPendingInvoiceDraft}
              >
                {t("Cancel", "Ακύρωση")}
              </button>
              <button
                type="button"
                className="btn-base btn-green"
                disabled={extractBusy || !pendingInvoiceDraft.draft?.draft_lines?.length}
                onClick={confirmPendingInvoiceDraft}
              >
                {pendingInvoiceDraft.append
                  ? t("Add page lines", "Προσθήκη γραμμών σελίδας")
                  : pendingInvoiceDraft.quality?.matchedN <
                      pendingInvoiceDraft.quality?.orderN
                    ? t("Set as partial receive", "Μερική παραλαβή")
                    : t("Use invoice lines", "Χρήση γραμμών τιμολογίου")}
              </button>
            </div>
          </div>
        </div>
      )}

      <BlockingActionOverlay
        open={Boolean(invoiceOverlay)}
        title={invoiceOverlay?.title || ""}
        subtitle={invoiceOverlay?.subtitle || null}
        progressPct={invoiceOverlay?.progressPct ?? null}
        steps={invoiceOverlay?.steps || null}
      />

      {invoiceCropFile && (
        <InvoicePerspectiveCrop
          file={invoiceCropFile}
          t={t}
          onCancel={() => {
            setInvoiceCropFile(null);
            // Abort: drop any scan pages queued via Add Page in this session.
            scanPageQueueRef.current = [];
          }}
          onConfirm={finishCroppedInvoice}
        />
      )}

      {invoiceLightbox?.url ? (
        <div
          className="photo-lightbox"
          style={{ zIndex: 1400 }}
          onClick={() => setInvoiceLightbox(null)}
          role="dialog"
          aria-modal="true"
          aria-label={invoiceLightbox.label || t("Invoice page", "Σελίδα τιμολογίου")}
        >
          <button
            type="button"
            className="photo-lightbox-close"
            onClick={() => setInvoiceLightbox(null)}
            aria-label={t("Close", "Κλείσιμο")}
          >
            ✕
          </button>
          <img
            src={invoiceLightbox.url}
            alt={invoiceLightbox.label || ""}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      ) : null}
    </div>
  );
}
